import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { getWhatsAppConversations, getWhatsAppConversationsPage, getConversationMessages, getMessageByWamid, markConversationRead, sendWhatsAppMessage, retryFailedWhatsAppMessage, getWhatsAppTemplates, getMetaTemplates, getWhatsAppSnippets, generateQRCode, uploadWhatsAppMedia, autoLinkConversationClient, getWhatsAppAssignableUsers, getWhatsAppInboxAssignees, assignConversationToEmployee, updateConversationTags, setConversationBroadcastOptOut, getConversationNotes, createConversationNote, getClient, getDeals, getActivities, getWhatsAppWebSocketUrl, getWhatsAppIntegrations, getMyChannels, getTemplateHeader, transcribeAudio, getClients, recordSnippetUse, getWhatsAppInteractivePresets, recordInteractivePresetUse, geocodeSearch, setConversationType, resolveConversationFollowup, getWhatsAppTriageCounts } from '../utils/api';
import EmojiPicker from 'emoji-picker-react';
import { FaWhatsapp, FaSpinner, FaClock, FaUser, FaLightbulb, FaImage, FaFile, FaFilePdf, FaMapMarkerAlt, FaAddressCard, FaQrcode, FaPaperPlane, FaPaperclip, FaArrowLeft, FaThumbtack, FaComments, FaVideo, FaMusic, FaBold, FaItalic, FaStrikethrough, FaCode, FaSync, FaMicrophone, FaStop, FaTimes, FaPlay, FaPause, FaExternalLinkAlt, FaSmile, FaReply } from 'react-icons/fa';
import { ar, en } from '../utils/translations';
import { COUNTRY_CODES } from '../utils/countryCodes';
import { buildInteractiveFromPreset, parseStoredButtons } from '../utils/interactivePresetFormat';
import { formatSnippetCategoryLabel } from '../utils/whatsappSnippetCategories';
import { getConversationActionSuggestions, actOnActionSuggestion, dismissActionSuggestion } from '../utils/api';
import { useToast } from './Toast';
import DualCalendarPicker from './DualCalendarPicker';
import './WhatsApp.css';

// WhatsApp system font stack — matches actual WhatsApp on phones, not Real brand fonts
const whatsappFontFamily = 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/**
 * Detects text direction using the Unicode Bidirectional Algorithm approach.
 * Based on the first strong directional character in the text.
 * @param {string} text - The text to analyze
 * @returns {'rtl'|'ltr'} - The detected text direction
 */
function getTextDirection(text) {
  // Handle empty/whitespace
  if (!text || !text.trim()) return 'ltr';
  
  // Find first strong directional character
  for (const char of text) {
    // RTL scripts: Arabic (U+0600-U+06FF), Hebrew (U+0590-U+05FF), 
    // Arabic Supplement, Arabic Extended, Arabic Presentation Forms
    if (/[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/.test(char)) {
      return 'rtl';
    }
    // LTR scripts: Basic Latin, Latin-1 Supplement, Latin Extended, Cyrillic
    if (/[A-Za-z\u00C0-\u017F\u0400-\u04FF]/.test(char)) {
      return 'ltr';
    }
  }
  
  // Fallback: if no strong characters found, default to LTR
  // This handles cases with only numbers, punctuation, or neutral characters
  return 'ltr';
}

// Add pulse animation for typing indicators
const pulseAnimation = `
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
`;

// Inject CSS if not already present
if (!document.getElementById('typing-indicator-styles')) {
  const style = document.createElement('style');
  style.id = 'typing-indicator-styles';
  style.textContent = pulseAnimation;
  document.head.appendChild(style);
}

/** Inbox document card: type label + accent for client/agent PDFs and office files */
function getDocumentPresentation(mime, filename) {
  const m = String(mime || '').toLowerCase();
  const f = String(filename || '').toLowerCase();
  if (m.includes('pdf') || f.endsWith('.pdf')) return { label: 'PDF', accent: '#dc2626', isPdf: true };
  if (m.includes('word') || m.includes('msword') || f.endsWith('.doc') || f.endsWith('.docx')) return { label: 'Word', accent: '#2563eb', isPdf: false };
  if (m.includes('spreadsheet') || m.includes('excel') || f.endsWith('.xls') || f.endsWith('.xlsx')) return { label: 'Excel', accent: '#16a34a', isPdf: false };
  if (m.includes('presentation') || f.endsWith('.ppt') || f.endsWith('.pptx')) return { label: 'Slides', accent: '#ea580c', isPdf: false };
  return { label: 'Document', accent: '#64748b', isPdf: false };
}

/**
 * Whether to render the text block *below* the media/template rows in a bubble.
 *
 * - Always show real client/agent text: plain messages, and captions (e.g. document/image caption
 *   is stored as msg.message and does NOT match the [Document:…] placeholder pattern).
 * - Attachment file names stay in the structured card above (document filename, etc.).
 * - Hide only redundant *system* one-liners that duplicate that UI: [Location:…], [Document:…],
 *   [Contact:…], or bare [Image] with no caption — not user-written content.
 */
function truncateReplyPreview(text, max = 96) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return '…';
  return t.length > max ? `${t.slice(0, max)}…` : t;
}

function parseMessageMetadataLoose(msg) {
  let meta = msg?.message_metadata ?? msg?.messageMetadata;
  if (typeof meta === 'string') {
    try {
      return JSON.parse(meta);
    } catch {
      return {};
    }
  }
  return meta && typeof meta === 'object' ? meta : {};
}

function getMessageTypeLoose(msg) {
  return String(msg?.message_type ?? msg?.messageType ?? '').toLowerCase();
}

/**
 * Reaction sidecar rows (staff → client message, or client → business message). Hidden from thread; emoji shown on target bubble.
 */
function isReactionSidecarRecord(msg) {
  if (!msg) return false;
  if (getMessageTypeLoose(msg) === 'reaction') return true;
  const meta = parseMessageMetadataLoose(msg);
  if (meta.reaction_target_wamid || meta.reactionTargetWamid) return true;
  return false;
}

/** Split body text for thread rendering: capture http(s) and www. URLs (split includes separators). */
function splitTextWithUrls(text) {
  return String(text || '').split(/(https?:\/\/[^\s]+|www\.[^\s]+)/gi);
}

function hrefFromUrlToken(token) {
  const t = String(token || '');
  if (/^https?:\/\//i.test(t)) return t;
  if (/^www\./i.test(t)) return `https://${t}`;
  return t;
}

function shouldShowSecondaryMessageText(msg) {
  if (!msg?.message) return false;
  const type = getMessageTypeLoose(msg) || msg.message_type;
  const t = String(msg.message).trim();
  if (type === 'reaction') return false;
  if (type === 'interactive') return false;
  if (['[Image]', '[Video]', '[Audio]', '[Voice Note]', '[Contact]'].includes(t)) return false;
  if (type === 'location') return false;
  if (type === 'document' && /^\[Document:/.test(t)) return false;
  if (type === 'contact' && /^\[Contact:/.test(t)) return false;
  if (type === 'image' && t === '[Image]') return false;
  return true;
}

// Audio Message Player Component
const AudioMessagePlayer = ({ audioUrl, language = 'ar', messageId }) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [duration, setDuration] = React.useState(null);
  const [currentTime, setCurrentTime] = React.useState(0);
  const audioRef = React.useRef(null);
  /** voice-note.mp4 from browsers is often video/mp4; <video> plays it reliably vs <audio> in some browsers. */
  const useVideoBackend = /\.mp4(\?|#|$)/i.test(String(audioUrl || ''));

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [audioUrl]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!audioUrl) {
    return (
      <div style={{
        padding: '12px',
        backgroundColor: 'rgba(0,0,0,0.05)',
        borderRadius: '8px',
        marginBottom: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <FaMicrophone style={{ fontSize: '24px', color: '#ea580c', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
            {language === 'ar' ? 'رسالة صوتية' : 'Voice Note'}
          </div>
          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
            {language === 'ar' ? 'غير متاح' : 'Not available'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '12px',
      backgroundColor: 'var(--wa-agent-bubble-fill, #f5f6f8)',
      border: '1px solid var(--wa-agent-bubble-border, rgba(37, 211, 102, 0.12))',
      borderRadius: '8px',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      boxShadow: 'var(--wa-agent-bubble-shadow, 0 0 0 1px rgba(37, 211, 102, 0.08), 0 2px 8px rgba(37, 211, 102, 0.07))'
    }}>
      {useVideoBackend ? (
        <video
          ref={audioRef}
          src={audioUrl}
          preload="metadata"
          playsInline
          style={{ display: 'none', width: 0, height: 0 }}
          title=""
        />
      ) : (
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        style={{ display: 'none' }}
      />
      )}
      <button
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          togglePlayPause();
        }}
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          backgroundColor: '#f97316',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          flexShrink: 0,
          boxShadow: '0 2px 8px rgba(249, 115, 22, 0.25)'
        }}
        title={isPlaying ? (language === 'ar' ? 'إيقاف' : 'Pause') : (language === 'ar' ? 'تشغيل' : 'Play')}
      >
        {isPlaying ? <FaPause style={{ fontSize: '18px' }} /> : <FaPlay style={{ fontSize: '18px', marginLeft: '2px' }} />}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <FaMicrophone style={{ fontSize: '16px', color: '#ea580c', flexShrink: 0 }} />
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
            {language === 'ar' ? 'رسالة صوتية' : 'Voice Note'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#6b7280' }}>
          <span>{formatTime(currentTime)}</span>
          <div style={{ flex: 1, height: '4px', backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{
              width: duration ? `${(currentTime / duration) * 100}%` : '0%',
              height: '100%',
              backgroundColor: '#f97316',
              transition: 'width 0.1s linear'
            }} />
          </div>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
};

async function probeInboxProxyMediaStatus(src) {
  if (!src || !String(src).includes('/api/whatsapp/inbox-media/')) return null;
  try {
    const abs = src.startsWith('http') ? src : `${window.location.origin}${src.startsWith('/') ? '' : '/'}${src}`;
    const r = await fetch(abs, { method: 'HEAD', credentials: 'include', cache: 'no-store' });
    return r.status;
  } catch {
    return null;
  }
}

function inboxProxyMediaHintText(status, language) {
  const ar = language === 'ar';
  if (status === 403) {
    return ar
      ? 'لا يوجد ربط صالح لهذه المحادثة على الخادم. اطلب من المسؤول التحقق من خط واتساب المنظمة.'
      : 'No WhatsApp credentials on file for this thread. Ask an admin to verify your org’s WhatsApp line.';
  }
  if (status === 401 || status === 400) {
    return ar
      ? 'رفضت واجهة ميتا الطلب (صلاحية/طلب غير صالح). قد يتطلب ذلك تحديث التوكن في الإعدادات.'
      : 'Meta rejected the request (auth or invalid). Your admin may need to refresh the WhatsApp token under Settings.';
  }
  if (status === 502 || status === 404) {
    return ar
      ? 'ميتا لم تعُد الملف (انتهاء الوسائط أو توكن منتهٍ). أعد ربط التكامل أو اطلب من المسؤول تجديد التوكن — قد لا تُرسَل الرسائل حتى يُصلَح ذلك.'
      : 'Meta did not return this file (expired media or token). Ask an admin to renew the WhatsApp integration — outbound messages may fail until fixed.';
  }
  if (status != null && status >= 500) {
    return ar
      ? 'خطأ مؤقت من الخادم أو ميتا. حاول لاحقاً؛ إذا استمر، راجع سجلات المنصة.'
      : 'Temporary server or Meta error. Retry later; if it persists, check platform logs.';
  }
  return ar
    ? 'تعذّر تحميل الوسائط. قد يكون الملف قديم لدى ميتا أو يحتاج التوكن إلى تجديد.'
    : 'Could not load media. The file may have expired on Meta’s side or the integration token may need renewal.';
}

const WhatsAppInbox = ({
  language = 'ar',
  refreshKey = 0,
  userRole = 'employee',
  canInboxTriage = false,
  currentUserId = null,
  conversationIdToOpen,
  onConversationOpened,
  conversationToOpen,
  onConversationToOpenHandled,
  onNavigateToTemplates
}) => {
  const toast = useToast();
  const t = language === 'ar' ? ar : en;
  /** Tags in bulk bar + conversation header (set true to show). */
  const INBOX_SHOW_TAGS_UI = false;
  /** Per-conversation campaign inclusion toggle — hidden for now (manage from Campaigns tab / future inbox iteration). */
  const INBOX_SHOW_CAMPAIGNS_BUTTON = false;
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  // Guard against concurrent loadConversations calls — only one in-flight at a time
  const loadConversationsInFlight = useRef(false);
  /** Bumped on every loadConversations() invocation so in-flight responses can be ignored after filter changes. */
  const loadConversationsListVersionRef = useRef(0);
  /** If loadConversations ran while another fetch was in flight, run again after it finishes (latest filters). */
  const loadConversationsRetryPendingRef = useRef(false);
  // Debounce timer ref for WebSocket-triggered reloads
  const loadConversationsDebounceTimer = useRef(null);
  // Separate debounce timer for search input — prevents a DB query on every keystroke
  const searchDebounceTimer = useRef(null);
  // REMOVED: Pagination state variables - pagination eliminated to fix filter inconsistencies
  // Server-side triage badge counts (accurate across all pages)
  const [triageCounts, setTriageCounts] = useState({ needs_attention: 0, vendor_non_client: 0, high: 0, unassigned: 0 });
  const triageCountsDebounceTimer = useRef(null);
  const conversationListRef = useRef(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [retryingFailedMessageId, setRetryingFailedMessageId] = useState(null);
  /** After inbox-media proxy fails: messageId -> last known HTTP status from HEAD probe */
  const [inboxProxyMediaHints, setInboxProxyMediaHints] = useState({});
  /** Quoted message for contextual reply to the client ({ id, whatsapp_message_id, textPreview, quotedSender }) */
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [showComposerEmojiPicker, setShowComposerEmojiPicker] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [snippets, setSnippets] = useState([]);
  const [showSnippets, setShowSnippets] = useState(false);
  const [snippetSearch, setSnippetSearch] = useState('');
  const [snippetCategoryFilter, setSnippetCategoryFilter] = useState('all');
  const [slashQuery, setSlashQuery] = useState(null); // null = inactive, '' or 'word' = active
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  // Typing indicators: track who is typing in each conversation
  const [typingAgents, setTypingAgents] = useState({}); // { conversationId: [{ userId, userName, timestamp }] }
  const [isCurrentUserTyping, setIsCurrentUserTyping] = useState(false);
  // agent_replying: { conversationId: { agentName, timestamp } } — flashes for 5s after another agent sends
  const [replyingAgents, setReplyingAgents] = useState({});
  const typingTimeoutRef = useRef(null);
  const wsRef = useRef(null);
  // Incrementing counter to trigger re-render when a lazy-fetched quoted message arrives
  const [quotedMsgCacheVersion, setQuotedMsgCacheVersion] = useState(0);
  const [messageType, setMessageType] = useState('text'); // 'text', 'image', 'document', 'video', 'audio', 'location', 'contact', 'interactive'
  const [showMediaOptions, setShowMediaOptions] = useState(false);
  const [composerDragOverlay, setComposerDragOverlay] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [contactData, setContactData] = useState(null);
  const [contactPhoneCode, setContactPhoneCode] = useState('+966');
  const [contactPhoneLocal, setContactPhoneLocal] = useState('');
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [locationSearchResults, setLocationSearchResults] = useState([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const locationSearchTimeoutRef = useRef(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState([]);
  const [isSearchingContact, setIsSearchingContact] = useState(false);
  const contactSearchTimeoutRef = useRef(null);
  const [snippetMediaUrl, setSnippetMediaUrl] = useState(null);
  const [snippetMediaType, setSnippetMediaType] = useState(null);
  const [interactivePresets, setInteractivePresets] = useState([]);
  const [interactivePresetsLoaded, setInteractivePresetsLoaded] = useState(false);
  const [selectedInteractivePreset, setSelectedInteractivePreset] = useState(null);
  const [actionSuggestions, setActionSuggestions] = useState([]);
  const [schedulingModal, setSchedulingModal] = useState(null); // { suggestion }
  const [schedulingLabel, setSchedulingLabel] = useState('');
  const [schedulingDate, setSchedulingDate] = useState('');
  const [schedulingTime, setSchedulingTime] = useState('');
  const [schedulingNotes, setSchedulingNotes] = useState('');
  const [schedulingSaving, setSchedulingSaving] = useState(false);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignableLoading, setAssignableLoading] = useState(false);
  const [reassignTarget, setReassignTarget] = useState('');
  const [reassignReasonDraft, setReassignReasonDraft] = useState('');
  const [showQRCode, setShowQRCode] = useState(false);
  const [imageModal, setImageModal] = useState(null); // { src, alt }
  const [documentPreviewModal, setDocumentPreviewModal] = useState(null); // { url, filename } — PDF iframe preview in-app
  const [templateHeaderCache, setTemplateHeaderCache] = useState({}); // Cache template header info: { templateName: { headerFormat, headerImageUrl, headerText } }
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  const [recordingError, setRecordingError] = useState(null);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioPlayerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  /** Mic recorder preview file — server applies purpose=voice_note (MP4→AAC normalize) only when true. */
  const voiceRecordingForUploadRef = useRef(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const metaTemplatePanelRef = useRef(null);
  /** Prevents double-submit before React re-renders; cleared in finally with sendingMessage. */
  const outboundSendLockRef = useRef(false);
  /** Cache for quoted messages fetched by wamid that were outside the paginated window. */
  const quotedMsgCacheRef = useRef({});

  // ESC closes image lightbox or PDF preview modal; lock scroll while either is open
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key !== 'Escape') return;
      if (documentPreviewModal) setDocumentPreviewModal(null);
      else if (imageModal) setImageModal(null);
    };
    if (imageModal || documentPreviewModal) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [imageModal, documentPreviewModal]);

  // Cleanup location search timeout
  useEffect(() => {
    return () => {
      if (locationSearchTimeoutRef.current) {
        clearTimeout(locationSearchTimeoutRef.current);
      }
      if (contactSearchTimeoutRef.current) {
        clearTimeout(contactSearchTimeoutRef.current);
      }
    };
  }, []);

  // Create audio URL when audioFile changes
  useEffect(() => {
    if (audioFile) {
      const url = URL.createObjectURL(audioFile);
      setAudioUrl(url);
      return () => {
        URL.revokeObjectURL(url);
        setAudioUrl(null);
      };
    } else {
      setAudioUrl(null);
    }
  }, [audioFile]);

  // Handle audio playback
  useEffect(() => {
    const audio = audioPlayerRef.current;
    if (!audio) return;

    const handlePlay = () => setIsPlayingAudio(true);
    const handlePause = () => setIsPlayingAudio(false);
    const handleEnded = () => setIsPlayingAudio(false);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioUrl]);

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);
  const fileInputRef = useRef(null);
  const composerRef = useRef(null);
  const searchInputRef = useRef(null);
  const selectedConvRef = useRef(null);
  const messageInputRef = useRef(null);
  const [mockData, setMockData] = useState(false); // Use real API data
  const [searchQuery, setSearchQuery] = useState('');
  const [waitingFilter, setWaitingFilter] = useState('all');
  const [leadFilter, setLeadFilter] = useState('all');
  const [linkedFilter, setLinkedFilter] = useState('all');
  const [businessPhoneFilter, setBusinessPhoneFilter] = useState('all');
  /** all = full org inbox. Owner/manager: also id:<userId>. Employee: mine | unassigned narrows the list. */
  const [assigneeScope, setAssigneeScope] = useState('all');
  const [inboxAssigneeOptions, setInboxAssigneeOptions] = useState([]);
  const [showCampaignOnlyConversations, setShowCampaignOnlyConversations] = useState(false);
  const [sortMode, setSortMode] = useState('latest_message');
  const [quickTriageFilter, setQuickTriageFilter] = useState('all');
  const [integrationNumbers, setIntegrationNumbers] = useState([]);
  const [pinnedConversationIds, setPinnedConversationIds] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem('wa_pinned_conversation_ids');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  });
  const [selectedConversationIds, setSelectedConversationIds] = useState([]);
  const [showWorkspacePanel, setShowWorkspacePanel] = useState(true); // Always default to open
  const NARROW_LAYOUT_BREAKPOINT = 1100;
  const FILTER_COLLAPSE_BREAKPOINT = 1400;
  const [viewportWidth, setViewportWidth] = useState(() => {
    if (typeof window === 'undefined') return 1600;
    return window.innerWidth;
  });
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [viewPreset, setViewPreset] = useState('list'); // 'list' | 'sla_board' | 'simple'
  const [wsConnected, setWsConnected] = useState(false);
  const [aiInsightsExpanded, setAiInsightsExpanded] = useState(false);
  const [conversationNotes, setConversationNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [clientProfile, setClientProfile] = useState(null);
  const [metaTemplateName, setMetaTemplateName] = useState('hello_world'); // For sending outside 24h window
  const [customMetaTemplateName, setCustomMetaTemplateName] = useState(''); // When "Other" is selected
  const [metaTemplates, setMetaTemplates] = useState([]); // Meta-approved templates for dropdown
  const isManagerOrOwner = userRole === 'manager' || userRole === 'owner';

  const parseTimestampMs = (value) => {
    if (!value) return NaN;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return NaN;
      return value > 1e12 ? value : value * 1000;
    }
    const raw = String(value).trim();
    if (!raw) return NaN;

    // PostgreSQL timestamps may come without timezone; treat as UTC.
    const isoLikeNoTz = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/;
    const normalized = isoLikeNoTz.test(raw) ? `${raw.replace(' ', 'T')}Z` : raw;
    const ms = new Date(normalized).getTime();
    return Number.isFinite(ms) ? ms : NaN;
  };

  // Mock data for testing
  const mockConversations = [
    {
      id: '1',
      phone: '+966501234567',
      last_message: 'أبحث عن شقة 3 غرف في العليا',
      last_message_timestamp: new Date().toISOString(),
      message_count: 5,
      last_message_at: new Date().toISOString()
    },
    {
      id: '2',
      phone: '+966507654321',
      last_message: 'ما هو سعر الفيلا؟',
      last_message_timestamp: new Date(Date.now() - 3600000).toISOString(),
      message_count: 3,
      last_message_at: new Date(Date.now() - 3600000).toISOString()
    },
    {
      id: '3',
      phone: '+966509876543',
      last_message: 'شكراً لك',
      last_message_timestamp: new Date(Date.now() - 7200000).toISOString(),
      message_count: 8,
      last_message_at: new Date(Date.now() - 7200000).toISOString()
    }
  ];

  const mockMessages = {
    '1': [
      { id: 'm1', sender: 'client', message: 'السلام عليكم', timestamp: new Date(Date.now() - 3600000).toISOString() },
      { id: 'm2', sender: 'agent', message: 'وعليكم السلام ورحمة الله', timestamp: new Date(Date.now() - 3500000).toISOString() },
      { id: 'm3', sender: 'client', message: 'أبحث عن شقة 3 غرف في العليا', timestamp: new Date(Date.now() - 3400000).toISOString() },
      { id: 'm4', sender: 'agent', message: 'حسناً، لدينا عدة خيارات. ما هي ميزانيتك؟', timestamp: new Date(Date.now() - 3300000).toISOString() },
      { id: 'm5', sender: 'client', message: 'حوالي 5000 ريال شهرياً', timestamp: new Date(Date.now() - 3200000).toISOString() }
    ],
    '2': [
      { id: 'm6', sender: 'client', message: 'ما هو سعر الفيلا؟', timestamp: new Date(Date.now() - 7200000).toISOString() },
      { id: 'm7', sender: 'agent', message: 'السعر يبدأ من 8000 ريال شهرياً', timestamp: new Date(Date.now() - 7100000).toISOString() },
      { id: 'm8', sender: 'client', message: 'شكراً', timestamp: new Date(Date.now() - 7000000).toISOString() }
    ],
    '3': [
      { id: 'm9', sender: 'client', message: 'مرحباً', timestamp: new Date(Date.now() - 10800000).toISOString() },
      { id: 'm10', sender: 'agent', message: 'مرحباً بك', timestamp: new Date(Date.now() - 10700000).toISOString() },
      { id: 'm11', sender: 'client', message: 'شكراً لك', timestamp: new Date(Date.now() - 10600000).toISOString() }
    ]
  };

  const getWaitingMinutes = (conversation) => {
    const source = conversation?.last_inbound_at;
    if (!source) return 0;
    const sourceMs = parseTimestampMs(source);
    if (!Number.isFinite(sourceMs)) return 0;
    return Math.max(0, Math.floor((Date.now() - sourceMs) / 60000));
  };

  const getConversationLatestMs = (conv) => {
    const ts = conv?.last_message_at || conv?.last_inbound_at || conv?.last_message_timestamp || conv?.updated_at || conv?.created_at;
    const ms = parseTimestampMs(ts);
    return Number.isFinite(ms) ? ms : 0;
  };

  const getReadinessRank = (conv) => {
    const temp = String(conv?.client_readiness || conv?.lead_temperature || '').toLowerCase();
    if (temp === 'high' || temp === 'hot') return 3;
    if (temp === 'medium' || temp === 'warm') return 2;
    if (temp === 'low' || temp === 'cold') return 1;
    return 0;
  };

  /**
   * A campaign shell is a conversation created when an outbound campaign was sent to a new number
   * that had never messaged the org before. It has no inbound message yet (last_inbound_at is null).
   * These are hidden from the inbox until the contact actually replies — at that point last_inbound_at
   * is set by the server and the conversation surfaces naturally.
   */
  const isCampaignShell = (conv) => !conv?.last_inbound_at;

  const shouldHideCampaignOnlyConversation = (conv) => {
    // Hide pure campaign shells (no inbound ever) — they are not actionable inbox items.
    if (isCampaignShell(conv)) return true;
    // Legacy signal-based check kept as fallback for older rows that may lack last_inbound_at.
    const clientCount = Number(conv?.client_message_count || 0);
    const campaignCount = Number(conv?.campaign_message_count || 0);
    const lastSender = String(conv?.last_message_sender || '').toLowerCase();
    const lastAttr = String(conv?.last_message_attribution_source || '').toLowerCase();
    return campaignCount > 0 && clientCount === 0 && (lastSender === 'agent' || lastAttr === 'campaign');
  };

  const visibleConversations = showCampaignOnlyConversations
    ? [...conversations]
    : conversations.filter((conv) => !shouldHideCampaignOnlyConversation(conv));

  const displayedConversations = [...visibleConversations].sort((a, b) => {
    const aPinned = pinnedConversationIds.includes(a.id);
    const bPinned = pinnedConversationIds.includes(b.id);
    if (aPinned !== bPinned) return aPinned ? -1 : 1;
    if (sortMode === 'latest_message') {
      return getConversationLatestMs(b) - getConversationLatestMs(a);
    }
    const readinessDiff = getReadinessRank(b) - getReadinessRank(a);
    if (readinessDiff !== 0) return readinessDiff;
    return getConversationLatestMs(b) - getConversationLatestMs(a);
  });
  const businessPhoneOptions = Array.from(
    new Map(
      [
        ...(conversations || [])
          .map((conv) => {
            const id = String(conv?.business_phone_number_id || '').trim();
            if (!id) return null;
            return [
              id,
              {
                id,
                label: String(conv?.business_display_phone_number || '').trim() || id
              }
            ];
          })
          .filter(Boolean),
        ...(integrationNumbers || [])
          .map((item) => {
            const id = String(item?.id || '').trim();
            if (!id) return null;
            return [
              id,
              {
                id,
                label: String(item?.label || '').trim() || id
              }
            ];
          })
          .filter(Boolean)
      ]
    ).values()
  );

  /** No formal assignee and no CRM outbound actor yet — used for "غير معين" triage (matches GET /api/whatsapp/triage-counts unassigned). */
  const isUnassigned = (conv) =>
    !conv?.assigned_employee_id &&
    !String(conv?.assigned_employee_name || '').trim() &&
    !conv?.last_outbound_user_id &&
    !String(conv?.last_outbound_user_name || '').trim();

  /** Per-contact exclusion from bulk campaign sends (toggle in Inbox; campaigns are created in the Campaigns tab). Coerces DB strings so "false" is not treated as opted-out. */
  const isConversationBroadcastOptedOut = (conv) => {
    const v = conv?.broadcast_opt_out;
    if (v === true || v === 1) return true;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === 'true' || s === 't' || s === '1';
    }
    return false;
  };
  /** Prefer explicit assignee; else show who last sent an outbound CRM message (pickup without formal assign). */
  const conversationStaffLine = (conv) => {
    const assignee = String(conv?.assigned_employee_name || '').trim();
    if (assignee) return { kind: 'assignee', label: assignee };
    const lastOut = String(conv?.last_outbound_user_name || '').trim();
    if (lastOut) return { kind: 'lastReply', label: lastOut };
    return null;
  };
  /** Name shown next to thread stats: last CRM sender, else assignee, else unassigned. */
  const lastResponderNameForStats = (conv) => {
    const lastOut = String(conv?.last_outbound_user_name || '').trim();
    if (lastOut) return lastOut;
    const assignee = String(conv?.assigned_employee_name || '').trim();
    if (assignee) return assignee;
    return language === 'ar' ? 'غير معين' : 'Unassigned';
  };
  /** Arabic-friendly labels for stored conversation tags (AI slugs + manual). English UI keeps raw slug. */
  const localizeConversationTag = (raw) => {
    const tag = String(raw || '').trim().replace(/^#/, '');
    if (!tag) return '';
    if (language !== 'ar') return `#${tag}`;
    // Manual tags with no Latin letters (e.g. Arabic text) — show unchanged
    if (!/[A-Za-z]/.test(tag)) return `#${tag}`;

    const lower = tag.toLowerCase();
    const byFullKey = {
      'type-apt': 'شقة',
      'type-apartment': 'شقة',
      'type-villa': 'فيلا',
      'type-land': 'أرض',
      'type-office': 'مكتب',
      'type-warehouse': 'مستودع',
      'type-shop': 'محل',
      'intent-buy': 'نية شراء',
      'intent-rent': 'نية إيجار',
      'intent-sell': 'بيع',
      'budget-under-500k': 'ميزانية أقل من ٥٠٠ ألف',
      'budget-500k-1m': 'ميزانية ٥٠٠ ألف – ١ مليون',
      'budget-1m-2m': 'ميزانية ١–٢ مليون',
      'budget-2m-plus': 'ميزانية أكثر من ٢ مليون',
      urgent: 'عاجل',
      'follow-up': 'متابعة',
      followup: 'متابعة',
      viewing: 'معاينة',
      'lead-hot': 'عميل ساخن',
      'lead-warm': 'عميل دافئ',
      'lead-cold': 'عميل بارد'
    };
    if (byFullKey[lower]) return `#${byFullKey[lower]}`;
    if (lower.startsWith('type-')) {
      const sub = lower.slice(5);
      const subMap = {
        apt: 'شقة',
        apartment: 'شقة',
        villa: 'فيلا',
        land: 'أرض',
        office: 'مكتب',
        warehouse: 'مستودع',
        shop: 'محل'
      };
      if (subMap[sub]) return `#${subMap[sub]}`;
    }
    if (lower.startsWith('area-')) {
      return `#منطقة ${tag.slice(5).replace(/-/g, ' ')}`;
    }
    return `#${tag}`;
  };
  /**
   * Contact line under client profile: skip phones that duplicate the displayed name line (same digits).
   */
  const formatClientProfileContactLine = (client, nameLineForDedupe) => {
    if (!client) return null;
    const digits = (s) => String(s || '').replace(/\D/g, '');
    const phone = String(client.phone || '').trim();
    const whatsapp = String(client.whatsapp || '').trim();
    const email = String(client.email || '').trim();
    const nameD = digits(String(nameLineForDedupe || '').trim());
    const sameDigitsAsName = (v) => nameD && digits(v) && nameD === digits(v);
    const parts = [];
    if (phone && whatsapp && digits(phone) === digits(whatsapp)) {
      if (!sameDigitsAsName(phone)) parts.push(phone);
    } else {
      if (phone && !sameDigitsAsName(phone)) parts.push(phone);
      if (whatsapp && digits(whatsapp) !== digits(phone) && !sameDigitsAsName(whatsapp)) {
        parts.push(`${language === 'ar' ? 'واتساب' : 'WhatsApp'} ${whatsapp}`);
      }
    }
    if (email) parts.push(email);
    if (parts.length === 0) return null;
    return parts.join(' · ');
  };
  /** Strip legacy auto-prospect prefix from CRM name (DB may still store "WhatsApp 966…"). */
  const stripLegacyWhatsAppClientName = (raw) => {
    const n = String(raw || '').trim();
    if (!n) return '';
    if (/^whatsapp\s+/i.test(n)) {
      return n.replace(/^whatsapp\s+/i, '').trim() || n;
    }
    return n;
  };
  const displayLinkedClientName = (raw) => stripLegacyWhatsAppClientName(raw);
  /** Primary row title: CRM name when linked, else WhatsApp number. */
  const conversationPrimaryTitle = (conv) => {
    const name = displayLinkedClientName(conv?.linked_client_name);
    const phone = String(conv?.phone || '').trim();
    return name || phone || '—';
  };
  /** When a linked name is shown as primary, show phone on a muted second line. */
  const conversationPhoneSubtitle = (conv) => {
    const name = displayLinkedClientName(conv?.linked_client_name);
    const phone = String(conv?.phone || '').trim();
    if (!name || !phone) return null;
    const nd = name.replace(/\D/g, '');
    const pd = phone.replace(/\D/g, '');
    if (nd && pd && nd === pd) return null;
    return phone;
  };

  /** Meta Cloud API phone_number_id values are long numeric strings — never show them as the activity label. */
  const isLikelyMetaPhoneNumberId = (value) => {
    const s = String(value || '').trim();
    if (!s || !/^\d+$/.test(s)) return false;
    return s.length >= 14;
  };

  /**
   * Friendly business / activity line for UI only: integration name or display number.
   * Does not fall back to raw phone_number_id (avoids confusing strings like "920010213 · 1056890720834947").
   */
  const conversationBusinessLineFriendly = (conv) => {
    const id = String(conv?.business_phone_number_id || '').trim();
    const fromConv = String(conv?.business_display_phone_number || '').trim();
    if (id) {
      const fromMap = businessPhoneOptions.find((x) => String(x.id) === id);
      if (fromMap?.label) return String(fromMap.label).trim();
      const fromInt = (integrationNumbers || []).find((x) => String(x.id) === id);
      if (fromInt?.label) return String(fromInt.label).trim();
    }
    if (fromConv && !isLikelyMetaPhoneNumberId(fromConv)) return fromConv;
    return null;
  };

  const isVendorNonClientConversation = (conv) => {
    const cType = String(conv?.conversation_type || 'potential_client');
    const classified = cType === 'vendor' || cType === 'other';
    const pendingSuggest = conv?.vendor_suggestion === true || conv?.vendor_suggestion === 1;
    return classified || pendingSuggest;
  };

  const triagedConversations = displayedConversations.filter((conv) => {
    // CRITICAL FIX: When quickTriageFilter === 'all', show ALL conversations without additional filtering
    // The server already handles filtering based on the API parameters sent in loadConversations()
    if (quickTriageFilter === 'all') return true;
    
    // For specific filters, apply additional client-side filtering only as a fallback
    // This ensures the server and client filtering logic stay consistent
    if (quickTriageFilter === 'needs_attention') {
      // Note: Server already filters for needs_attention via triage parameter
      // Only apply additional client-side filtering if using mock data or as safety net
      if (mockData) {
        // Exclude conversations that have been explicitly resolved/dismissed
        if (conv.followup_resolved_at) return false;
        // Exclude vendor/other type conversations (they go through their own classification)
        const cType = String(conv.conversation_type || 'potential_client');
        if (cType === 'vendor' || cType === 'other') return false;
      const unread = Number(conv.unread_count || 0) > 0;
      const waitingForAgent = conv.waiting_for === 'agent';
      const slaRisk = waitingForAgent && getWaitingMinutes(conv) >= 30;
      return unread || waitingForAgent || slaRisk;
      }
      // For real API data, trust server filtering
      return true;
    }
    if (quickTriageFilter === 'vendor_non_client') {
      // For real API data, trust server filtering when vendor_non_client parameter was sent
      return mockData ? isVendorNonClientConversation(conv) : true;
    }
    if (quickTriageFilter === 'high') {
      // Apply client-side filtering for 'high' since it's not server-side filtered
      const readiness = String(conv.client_readiness || conv.lead_temperature || '').toLowerCase();
      return readiness === 'high' || readiness === 'hot';
    }
    if (quickTriageFilter === 'unassigned') {
      // Real API: GET ... triage=unassigned matches the badge; do not re-filter client-side
      // (isUnassigned also checks last_outbound_user_name from activities COALESCE, which can
      // disagree with DB-only triage-counts and hide rows).
      return mockData ? isUnassigned(conv) : true;
      return isUnassigned(conv);
    }
    return true;
  });

  const assigneeFilterActive = useMemo(() => assigneeScope !== 'all', [assigneeScope]);

  useEffect(() => {
    loadConversations();
    loadTriageCounts();
  }, []);

  // Deep-link: a full conversation object was passed in from the Clients page or the
  // quick-convo button. Select it immediately (instant feedback), then reload the list
  // so filters/sort are correct. Clears the prop via onConversationToOpenHandled once done.
  useEffect(() => {
    if (!conversationToOpen) return;
    setSelectedConversation(conversationToOpen);
    loadConversations().finally(() => {
      onConversationToOpenHandled?.();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationToOpen]);

  useEffect(() => {
    if (mockData) return;
    if (userRole !== 'owner' && userRole !== 'manager') return;
    getWhatsAppInboxAssignees()
      .then((res) => setInboxAssigneeOptions(res.data || []))
      .catch(() => setInboxAssigneeOptions([]));
  }, [userRole, mockData]);

  // Reload immediately when any filter (except search) changes
  useEffect(() => {
    if (!mockData) {
      loadConversations();
    }
  }, [waitingFilter, leadFilter, linkedFilter, businessPhoneFilter, assigneeScope, userRole, currentUserId, quickTriageFilter]);

  // Debounce search input — wait 350ms after the agent stops typing before firing the query
  useEffect(() => {
    if (mockData) return;
    clearTimeout(searchDebounceTimer.current);
    searchDebounceTimer.current = setTimeout(() => {
      loadConversations();
    }, 350);
    return () => clearTimeout(searchDebounceTimer.current);
  }, [searchQuery]);

  useEffect(() => {
    try {
      window.localStorage.setItem('wa_pinned_conversation_ids', JSON.stringify(pinnedConversationIds));
    } catch (e) {
      // ignore storage errors in restricted environments
    }
  }, [pinnedConversationIds]);

  useEffect(() => {
    // Keyboard shortcuts: j/k navigate; m/a mark-read & auto-assign only if canInboxTriage (owner/manager or wa_inbox_triage)
    const onKeyDown = (e) => {
      const target = e.target;
      const tagName = target?.tagName?.toLowerCase();
      const isTyping = tagName === 'input' || tagName === 'textarea' || tagName === 'select' || target?.isContentEditable;
      if (isTyping) return;
      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
        return;
      }
      if (!triagedConversations.length) return;

      const currentIdx = selectedConversation
        ? triagedConversations.findIndex((c) => c.id === selectedConversation.id)
        : -1;

      if (e.key === 'j') {
        e.preventDefault();
        const nextIdx = Math.min(triagedConversations.length - 1, currentIdx + 1);
        setSelectedConversation(triagedConversations[Math.max(0, nextIdx)]);
      } else if (e.key === 'k') {
        e.preventDefault();
        const prevIdx = Math.max(0, currentIdx - 1);
        setSelectedConversation(triagedConversations[prevIdx]);
      } else if (e.key === 'm' && selectedConversation && canInboxTriage) {
        e.preventDefault();
        markConversationRead(selectedConversation.id)
          .then(() => {
            setConversations((prev) =>
              prev.map((c) => c.id === selectedConversation.id ? { ...c, unread_count: 0 } : c)
            );
          })
          .catch((err) => console.warn('Shortcut mark read failed:', err?.message || err));
      } else if (e.key === 'a' && selectedConversation && canInboxTriage) {
        e.preventDefault();
        handleAutoAssign();
      } else if (e.key === 't' && selectedConversation && INBOX_SHOW_TAGS_UI) {
        e.preventDefault();
        handleUpdateTags();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [triagedConversations, selectedConversation, canInboxTriage]);

  // Refetch conversations when refreshKey changes (e.g., after simulation)
  useEffect(() => {
    console.log(`🔄 WhatsAppInbox: refreshKey prop = ${refreshKey}`);
    if (refreshKey > 0) {
      console.log(`🔄 WhatsAppInbox: refreshKey changed to ${refreshKey}, refetching conversations...`);
      loadConversations();
    }
  }, [refreshKey]);

  // Real-time WebSocket: subscribe to new message updates and typing indicators
  const selectedConversationIdRef = useRef(selectedConversation?.id);
  selectedConversationIdRef.current = selectedConversation?.id;
  useEffect(() => {
    if (mockData) return;
    if (!localStorage.getItem('token')) return;
    const url = getWhatsAppWebSocketUrl();
    let ws = null;
    let reconnectTimer = null;
    const connect = () => {
      try {
        ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => setWsConnected(true);
        ws.onmessage = (ev) => {
          try {
            const payload = JSON.parse(ev.data);
            if (payload?.type === 'new_message') {
              const convId = payload.conversationId;
              if (convId === selectedConversationIdRef.current) {
                loadMessages(convId);
              }
              // Debounce: collapse rapid bursts of WS events into a single reload
              loadConversationsDebounced();
              loadTriageCountsDebounced();
            } else if (payload?.type === 'send_failed') {
              // Optimistic send failed — Meta rejected the message after the agent already saw "sent"
              const convId = payload.conversationId;
              const errMsg = payload.error || (language === 'ar' ? 'فشل إرسال الرسالة' : 'Failed to send message');
              toast.error(language === 'ar' ? `فشل الإرسال: ${errMsg}` : `Send failed: ${errMsg}`, { duration: 6000 });
              // Reload messages so the failed status is reflected in the thread
              if (convId === selectedConversationIdRef.current) {
                loadMessages(convId);
              }
            } else if (payload?.type === 'agent_typing') {
              // Handle typing indicators from other agents
              const { conversationId, userId, userName, isTyping, timestamp } = payload;
              if (!conversationId || !userId) return;
              const convKey = String(conversationId);

              setTypingAgents(prev => {
                const convTyping = prev[convKey] || [];
                let next;
                if (isTyping) {
                  const updated = convTyping.filter(agent => String(agent.userId) !== String(userId));
                  updated.push({ userId, userName, timestamp: Date.now() });
                  next = { ...prev, [convKey]: updated };
                } else {
                  const updated = convTyping.filter(agent => String(agent.userId) !== String(userId));
                  if (updated.length === 0) {
                    const { [convKey]: _, ...rest } = prev;
                    next = rest;
                  } else {
                    next = { ...prev, [convKey]: updated };
                  }
                }
                return next;
              });
            } else if (payload?.type === 'agent_replying') {
              // Another agent just sent a message — flash a "replied" notice for 5s
              const { conversationId, agentName } = payload;
              if (!conversationId) return;
              const convKey = String(conversationId);
              setReplyingAgents(prev => ({ ...prev, [convKey]: { agentName: agentName || 'Agent', timestamp: Date.now() } }));
              // Also clear any typing indicator for this conversation — they sent, not typing anymore
              setTypingAgents(prev => {
                const { [convKey]: _, ...rest } = prev;
                return rest;
              });
            }
          } catch (_) {}
        };
        ws.onclose = () => {
          setWsConnected(false);
          wsRef.current = null;
          reconnectTimer = setTimeout(connect, 3000);
        };
        ws.onerror = () => {};
      } catch (_) {}
    };
    connect();
    return () => {
      clearTimeout(reconnectTimer);
      wsRef.current = null;
      if (ws && ws.readyState === 1) ws.close();
    };
  }, [mockData]);

  // Infinite scroll: load next page when user scrolls within 150px of the list bottom
  // Removed scroll handler for pagination since we load all conversations now

  useEffect(() => {
    setReplyToMessage(null);
    setShowComposerEmojiPicker(false);
    // Clear quoted-message cache on conversation switch to free memory
    quotedMsgCacheRef.current = {};
  }, [selectedConversation?.id]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
      if (!mockData && Number(selectedConversation.unread_count || 0) > 0) {
        markConversationRead(selectedConversation.id)
          .then(() => {
            // Update unread badge in-place; triage counts refresh via loadTriageCountsDebounced on next WS event
            setConversations((prev) =>
              prev.map((c) => c.id === selectedConversation.id ? { ...c, unread_count: 0 } : c)
            );
          })
          .catch((err) => console.warn('Failed to mark conversation read:', err?.message || err));
      }
      getConversationNotes(selectedConversation.id)
        .then((res) => setConversationNotes(res.data?.notes || []))
        .catch(() => setConversationNotes([]));
      getConversationActionSuggestions(selectedConversation.id)
        .then((res) => setActionSuggestions(res.data?.suggestions || []))
        .catch(() => setActionSuggestions([]));
      if (selectedConversation.linked_client_id) {
        const clientId = selectedConversation.linked_client_id;
        Promise.all([
          getClient(clientId).catch(() => null),
          getDeals('client', clientId).catch(() => ({ data: [] })),
          getActivities('client', clientId).catch(() => ({ data: [] }))
        ]).then(([clientRes, dealsRes, activitiesRes]) => {
          setClientProfile({
            client: clientRes?.data || null,
            deals: dealsRes?.data || [],
            activities: activitiesRes?.data || []
          });
        }).catch(() => setClientProfile(null));
      } else {
        setClientProfile(null);
      }
    } else {
      setConversationNotes([]);
      setClientProfile(null);
    }
    selectedConvRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    loadTemplates();
    loadSnippets();
    loadMetaTemplates();
    loadBusinessNumberOptions();
  }, []);

  // Fetch template header info for old template messages
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    
    const templateNamesToFetch = new Set();
    messages.forEach(msg => {
      if (msg.message_type === 'template' && msg.message_metadata) {
        const meta = typeof msg.message_metadata === 'string' 
          ? (() => { try { return JSON.parse(msg.message_metadata); } catch(_) { return {}; } })() 
          : (msg.message_metadata || {});
        
        // Only fetch if header info is missing
        if (!meta.headerFormat && !meta.headerImageUrl && !meta.headerText) {
          const templateName = meta.templateName || meta.name || msg.template_name;
          if (templateName && !templateHeaderCache[templateName]) {
            templateNamesToFetch.add(templateName);
          }
        }
      }
    });
    
    // Fetch headers for all templates that need them
    templateNamesToFetch.forEach(templateName => {
      getTemplateHeader(templateName).then(res => {
        if (res.data && (res.data.headerFormat || res.data.headerImageUrl || res.data.headerText)) {
          setTemplateHeaderCache(prev => {
            // Check again to avoid race conditions
            if (prev[templateName]) return prev;
            return {
              ...prev,
              [templateName]: res.data
            };
          });
        }
      }).catch(err => {
        console.warn(`Failed to fetch header for template ${templateName}:`, err);
      });
    });
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  const [metaTemplatesLoadFailed, setMetaTemplatesLoadFailed] = useState(false);

  const loadMetaTemplates = async () => {
    try {
      const res = await getMetaTemplates();
      const list = (res.data?.templates || []).filter((t) => t.name !== 'hello_world' || t.status === 'APPROVED');
      setMetaTemplates(list);
      setMetaTemplatesLoadFailed(list.length === 0);
      if (list.length > 0) setMetaTemplateName(list[0].name);
    } catch (e) {
      setMetaTemplates([]);
      setMetaTemplatesLoadFailed(true);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await getWhatsAppTemplates();
      setTemplates(response.data?.templates || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadSnippets = async () => {
    try {
      const response = await getWhatsAppSnippets();
      setSnippets(response.data?.snippets || []);
    } catch (error) {
      console.error('Error loading snippets:', error);
    }
  };

  const loadBusinessNumberOptions = async () => {
    try {
      const [intResp, accessResp] = await Promise.all([
        getWhatsAppIntegrations(),
        getMyChannels().catch(() => null),
      ]);
      const rows = Array.isArray(intResp?.data?.integrations) ? intResp.data.integrations : [];
      const myChannels = accessResp?.data;

      // Build allowed set: null means "all channels" (owner / manager or no grants configured yet)
      let allowedIds = null;
      if (myChannels && !myChannels.all_channels && Array.isArray(myChannels.grants) && myChannels.grants.length > 0) {
        allowedIds = new Set(myChannels.grants.filter(g => g.channel_type === 'whatsapp').map(g => g.channel_identifier));
      }

      const options = rows
        .map((row) => {
          const id = String(row?.phone_number_id || '').trim();
          if (!id) return null;
          if (allowedIds && !allowedIds.has(id)) return null;
          return {
            id,
            label: String(row?.business_display_name || '').trim() || id
          };
        })
        .filter(Boolean);
      setIntegrationNumbers(options);
    } catch (_error) {
      setIntegrationNumbers([]);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Also scroll to bottom when typing indicator appears for the selected conversation
  useEffect(() => {
    if (selectedConversation?.id && typingAgents[String(selectedConversation.id)]?.length > 0) {
      scrollToBottom();
    }
  }, [typingAgents, selectedConversation?.id]);

  // When the client sends while this chat is open, WS reloads messages but the conversation row
  // (last_inbound_at / window_closes_at) can stay stale until a full list fetch — keep template vs free-text UI aligned with the thread.
  useEffect(() => {
    const convId = selectedConversation?.id;
    if (!convId || !messages.length) return;
    let latestMs = -1;
    for (const m of messages) {
      if (m.sender !== 'client') continue;
      const ms = parseTimestampMs(m.timestamp);
      if (Number.isFinite(ms) && ms > latestMs) latestMs = ms;
    }
    if (latestMs < 0) return;
    const lastInboundAt = new Date(latestMs).toISOString();
    const windowClosesAt = new Date(latestMs + 24 * 60 * 60 * 1000).toISOString();
    setSelectedConversation((prev) => {
      if (!prev || prev.id !== convId) return prev;
      const cur = parseTimestampMs(prev.last_inbound_at);
      if (Number.isFinite(cur) && latestMs <= cur) return prev;
      return { ...prev, last_inbound_at: lastInboundAt, window_closes_at: windowClosesAt };
    });
    setConversations((prev) =>
      prev.map((c) => {
        if (c.id !== convId) return c;
        const cur = parseTimestampMs(c.last_inbound_at);
        if (Number.isFinite(cur) && latestMs <= cur) return c;
        return { ...c, last_inbound_at: lastInboundAt, window_closes_at: windowClosesAt };
      })
    );
  }, [messages, selectedConversation?.id]);

  useEffect(() => {
    const onResize = () => {
      setViewportWidth(window.innerWidth);
      setIsFilterDropdownOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    loadConversationsListVersionRef.current += 1;
    const listVersion = loadConversationsListVersionRef.current;

    if (loadConversationsInFlight.current) {
      loadConversationsRetryPendingRef.current = true;
      return;
    }
    loadConversationsInFlight.current = true;
    try {
      setLoading(true);
      
      if (mockData) {
        setTimeout(() => {
          if (listVersion !== loadConversationsListVersionRef.current) return;
          setConversations([...mockConversations]);
          setLoading(false);
          loadConversationsInFlight.current = false;
        }, 500);
        return;
      }

        const filters = {};
        if (searchQuery.trim()) filters.q = searchQuery.trim();
        if (waitingFilter !== 'all') filters.waiting_for = waitingFilter;
        if (leadFilter !== 'all') filters.client_readiness = leadFilter;
        if (linkedFilter !== 'all') filters.linked = linkedFilter;
      if (businessPhoneFilter !== 'all') {
        filters.business_phone_number_id = businessPhoneFilter;
      }
      if (quickTriageFilter === 'needs_attention') filters.triage = 'needs_attention';
      if (quickTriageFilter === 'unassigned') filters.triage = 'unassigned';
      if (quickTriageFilter === 'vendor_non_client') filters.vendor_non_client = 'true';
      
      console.log('API filters being sent:', filters, 'quickTriageFilter:', quickTriageFilter);
      const isMgr = userRole === 'owner' || userRole === 'manager';
      if (isMgr) {
        if (assigneeScope === 'unassigned') filters.assigned_employee_id = 'unassigned';
        else if (assigneeScope.startsWith('id:')) filters.assigned_employee_id = assigneeScope.slice(3);
      } else if (userRole === 'employee') {
        if (assigneeScope === 'mine' && currentUserId != null) filters.assigned_employee_id = String(currentUserId);
        else if (assigneeScope === 'unassigned') filters.assigned_employee_id = 'unassigned';
      }

      const response = await getWhatsAppConversationsPage(filters, 0, 0); // Load all matching conversations
      if (listVersion !== loadConversationsListVersionRef.current) return;

      const envelope = response.data || {};
      const responseData = envelope.conversations || (Array.isArray(envelope) ? envelope : []);
      let nextConversations = [...responseData];
      
      console.log('API response received:', {
        filters,
        count: nextConversations.length,
        conversations: nextConversations.map(c => ({ 
          id: c.id, 
          phone: c.phone, 
          unread_count: c.unread_count, 
          waiting_for: c.waiting_for 
        }))
      });
      // REMOVED: pagination handling - all conversations loaded in single request
        setConversations(nextConversations);
      // Prefer explicit id (supervisor) or deep-link from Clients/quick-convo — same as list row with window_closes_at.
      const mergeOpenId = conversationIdToOpen || conversationToOpen?.id;
      if (mergeOpenId) {
        const toOpen = nextConversations.find((c) => c.id === mergeOpenId);
        if (toOpen) {
          setSelectedConversation(toOpen);
        } else if (conversationToOpen && conversationToOpen.id === mergeOpenId) {
          // Row can be missing from GET (filters) — keep POST payload; server now includes window_closes_at.
          setSelectedConversation({ ...conversationToOpen });
        }
        if (conversationIdToOpen) onConversationOpened?.();
        } else {
          // Use the ref (always current) instead of the closure value which may be stale
          const currentId = selectedConvRef.current?.id;
          if (currentId) {
            const updatedSelection = nextConversations.find((conv) => conv.id === currentId);
          if (updatedSelection) setSelectedConversation(updatedSelection);
          }
        }
        setLoading(false);
    } catch (error) {
      if (listVersion !== loadConversationsListVersionRef.current) return;
      console.error('WhatsAppInbox: Error loading conversations:', error.response?.data || error.message);
      setConversations([]);
      setLoading(false);
    } finally {
      loadConversationsInFlight.current = false;
      if (loadConversationsRetryPendingRef.current) {
        loadConversationsRetryPendingRef.current = false;
        queueMicrotask(() => {
          loadConversations();
        });
      }
    }
  };

  // Debounced version for WebSocket-triggered reloads — collapses rapid bursts into one call
  const loadConversationsDebounced = () => {
    clearTimeout(loadConversationsDebounceTimer.current);
    loadConversationsDebounceTimer.current = setTimeout(() => {
      loadConversations();
    }, 600);
  };

  const loadTriageCounts = async () => {
    try {
      const res = await getWhatsAppTriageCounts();
      if (res?.data) setTriageCounts(res.data);
    } catch (_) {}
  };

  // Item D: triage-counts fires at most once per 5 seconds, not on every WS burst.
  // Badge lag is ≤5s — acceptable trade-off vs running an aggregation query every 800ms.
  const TRIAGE_COUNT_THROTTLE_MS = 5000;
  const loadTriageCountsDebounced = () => {
    clearTimeout(triageCountsDebounceTimer.current);
    triageCountsDebounceTimer.current = setTimeout(() => {
      loadTriageCounts();
    }, TRIAGE_COUNT_THROTTLE_MS);
  };

  // REMOVED: All pagination-related functions and logic eliminated

  const loadMessages = async (conversationId) => {
    try {
      if (mockData) {
        setMessages(mockMessages[conversationId] || []);
      } else {
        const response = await getConversationMessages(conversationId);
        const data = response.data;
        // Support both old (plain array) and new (envelope) formats
        if (Array.isArray(data)) {
          setMessages(data);
          setHasOlderMessages(false);
        } else {
          setMessages(data?.messages || []);
          setHasOlderMessages(data?.has_more ?? false);
        }
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessages(mockMessages[conversationId] || []);
    }
  };

  const loadOlderMessages = async () => {
    if (!selectedConversation || loadingOlderMessages || messages.length === 0) return;
    setLoadingOlderMessages(true);
    // Capture scroll height before prepend so we can restore position after React re-renders
    const container = messagesContainerRef.current;
    const scrollHeightBefore = container ? container.scrollHeight : 0;
    try {
      const oldestId = messages[0]?.id;
      const response = await getConversationMessages(selectedConversation.id, { before_id: oldestId });
      const data = response.data;
      const older = Array.isArray(data) ? data : (data?.messages || []);
      setHasOlderMessages(Array.isArray(data) ? false : (data?.has_more ?? false));
      if (older.length > 0) {
        setMessages((prev) => [...older, ...prev]);
        // After React updates the DOM, restore scroll position so the user stays at the same message
        requestAnimationFrame(() => {
          if (container) {
            container.scrollTop = container.scrollHeight - scrollHeightBefore;
          }
        });
      }
    } catch (err) {
      console.error('Error loading older messages:', err);
    } finally {
      setLoadingOlderMessages(false);
    }
  };

  // Typing indicator functions
  const sendTypingIndicator = (isTyping) => {
    if (!selectedConversation?.id || !wsRef.current || wsRef.current.readyState !== 1) return;
    try {
      wsRef.current.send(JSON.stringify({
        type: 'typing_indicator',
        conversationId: selectedConversation.id,
        isTyping
      }));
    } catch (err) {
      console.warn('Failed to send typing indicator:', err);
    }
  };

  const handleTypingStart = () => {
    if (!isCurrentUserTyping) {
      setIsCurrentUserTyping(true);
      sendTypingIndicator(true);
    }
    
    // Reset the timeout - if user keeps typing, keep showing as typing
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setIsCurrentUserTyping(false);
      sendTypingIndicator(false);
    }, 3000); // 3s inactivity timeout — disappears promptly when agent stops typing
  };

  const handleTypingStop = () => {
    clearTimeout(typingTimeoutRef.current);
    if (isCurrentUserTyping) {
      setIsCurrentUserTyping(false);
      sendTypingIndicator(false);
    }
  };

  // Clean up typing state when conversation changes
  useEffect(() => {
    handleTypingStop();
    setIsCurrentUserTyping(false);
  }, [selectedConversation?.id]);

  // Auto-expire typing indicators (5s) and agent_replying notices (5s)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setTypingAgents(prev => {
        const updated = {};
        let hasChanges = false;
        Object.entries(prev).forEach(([convId, agents]) => {
          const activeAgents = agents.filter(agent => now - agent.timestamp < 4000);
          if (activeAgents.length !== agents.length) hasChanges = true;
          if (activeAgents.length > 0) updated[convId] = activeAgents;
        });
        return hasChanges ? updated : prev;
      });
      setReplyingAgents(prev => {
        const updated = {};
        let hasChanges = false;
        Object.entries(prev).forEach(([convId, entry]) => {
          if (now - entry.timestamp < 5000) {
            updated[convId] = entry;
          } else {
            hasChanges = true;
          }
        });
        return hasChanges ? updated : prev;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleSendMetaTemplate = async () => {
    if (!selectedConversation) return;
    const name = (metaTemplateName === '__custom__' ? customMetaTemplateName : metaTemplateName)?.trim();
    if (!name) return;
    if (outboundSendLockRef.current) return;
    const selectedTpl = (metaTemplates.length > 0 ? metaTemplates : [{ name: 'hello_world', body_text: 'Hello World', language: 'en_US' }]).find((t) => t.name === name);
    const bodyText = selectedTpl?.body_text || null;
    // Use the full language code exactly as Meta registered it (e.g. en_US, ar).
    // Stripping the suffix (en_US → en) causes #132001 "does not exist in translation".
    const tplLang = (typeof selectedTpl?.language === 'string' && selectedTpl.language) || (language === 'ar' ? 'ar' : 'en_US');
    setReplyToMessage(null);
    outboundSendLockRef.current = true;
    try {
      setSendingMessage(true);
      await sendWhatsAppMessage(selectedConversation.id, {
        messageType: 'template',
        template: { name, language: { code: tplLang }, body_text: bodyText, meta_components: selectedTpl?.components || [] }
      });
      await loadMessages(selectedConversation.id);
      // WS new_message event triggers loadConversationsDebounced — no extra full reload needed
    } catch (error) {
      console.error('Error sending template:', error);
      const data = error.response?.data;
      const msg = data?.details?.error?.message || data?.error || error.message;
      
      // Check for token expiration
      const isTokenExpired = 
        (error.response?.status === 401 || error.response?.status === 403) ||
        (msg && (
          msg.includes('token has expired') ||
          msg.includes('OAuthException') ||
          msg.includes('Invalid OAuth access token')
        ));
      
      if (isTokenExpired) {
        toast.error(language === 'ar'
          ? '🚨 انتهت صلاحية رمز الواتساب. يرجى إبلاغ المشرف لتحديث الإعدادات.'
          : '🚨 WhatsApp token expired. Please notify your supervisor to update settings.');
      } else {
      toast.error(language === 'ar' ? `فشل إرسال القالب: ${msg}` : `Failed to send template: ${msg}`);
      }
    } finally {
      outboundSendLockRef.current = false;
      setSendingMessage(false);
    }
  };

  /** Same default strip as WhatsApp: thumbs, heart, laugh, wow, sad, thanks */
  const WHATSAPP_STYLE_REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

  const handleStartQuotedReply = (msg) => {
    if (!msg?.whatsapp_message_id) return;
    let preview = '';
    if (msg.message_type === 'reaction') {
      const meta = typeof msg.message_metadata === 'string' ? (() => { try { return JSON.parse(msg.message_metadata); } catch(_) { return {}; } })() : (msg.message_metadata || {});
      const emoji = meta.emoji || msg.message || '👍';
      preview = `${emoji} ${language === 'ar' ? 'تفاعل' : 'Reaction'}`;
    }
    else if (msg.message_type === 'image') preview = language === 'ar' ? '[صورة]' : '[Image]';
    else if (msg.message_type === 'video') preview = language === 'ar' ? '[فيديو]' : '[Video]';
    else if (msg.message_type === 'audio') preview = language === 'ar' ? '[صوت]' : '[Audio]';
    else if (msg.message_type === 'location') preview = language === 'ar' ? '[موقع]' : '[Location]';
    else if (msg.message_type === 'contact') preview = language === 'ar' ? '[جهة اتصال]' : '[Contact]';
    else if (msg.message_type === 'document') preview = language === 'ar' ? '[مستند]' : '[Document]';
    else if (msg.message_type === 'interactive') preview = language === 'ar' ? '[تفاعلي]' : '[Interactive]';
    else preview = truncateReplyPreview(msg.message);
    setReplyToMessage({
      id: msg.id,
      whatsapp_message_id: msg.whatsapp_message_id,
      textPreview: preview,
      quotedSender: msg.sender === 'client' ? 'client' : 'agent',
    });
    messageInputRef.current?.focus();
  };

  const sendEmojiReaction = async (msg, emoji) => {
    if (!selectedConversation || !msg?.whatsapp_message_id) return;
    if (outboundSendLockRef.current) return;
    const win = get24hWindowStatus(selectedConversation);
    if (!win.open) {
      toast.error(language === 'ar'
        ? 'انتهت نافذة الرد — لا يمكن التفاعل بالإيموجي الآن.'
        : 'Reply window closed — reactions require an open 24h session.');
      return;
    }
    outboundSendLockRef.current = true;
    try {
      setSendingMessage(true);
      await sendWhatsAppMessage(selectedConversation.id, {
        messageType: 'reaction',
        reactionToMessageId: msg.whatsapp_message_id,
        emoji,
      });
      await loadMessages(selectedConversation.id);
      // WS new_message event triggers loadConversationsDebounced — no extra full reload needed
      toast.success(language === 'ar' ? 'تم إرسال التفاعل' : 'Reaction sent');
    } catch (error) {
      console.error('Reaction send error:', error);
      const data = error.response?.data;
      const msgErr = data?.details?.error?.message || data?.error || error.message;
      
      // Check for token expiration
      const isTokenExpired = 
        (error.response?.status === 401 || error.response?.status === 403) ||
        (msgErr && (
          msgErr.includes('token has expired') ||
          msgErr.includes('OAuthException') ||
          msgErr.includes('Invalid OAuth access token')
        ));
      
      if (isTokenExpired) {
        toast.error(language === 'ar'
          ? '🚨 انتهت صلاحية رمز الواتساب. يرجى إبلاغ المشرف لتحديث الإعدادات.'
          : '🚨 WhatsApp token expired. Please notify your supervisor to update settings.');
      } else {
        toast.error(language === 'ar' ? `فشل التفاعل: ${msgErr}` : `Reaction failed: ${msgErr}`);
      }
    } finally {
      outboundSendLockRef.current = false;
      setSendingMessage(false);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedConversation) return;

    const win = get24hWindowStatus(selectedConversation);
    if (!win.open) {
      toast.error(language === 'ar'
        ? 'انتهت نافذة الرد (24 ساعة). استخدم حقل "قالب ميتا" أعلاه لإرسال قالب معتمد من واتساب.'
        : 'Reply window expired (24h). Use the "Meta template" field above to send a WhatsApp-approved template.');
      return;
    }

    // Validate based on message type
    if (messageType === 'text' && !newMessage.trim()) return;
    if (messageType === 'image' && !imageFile && !snippetMediaUrl && !newMessage.trim()) return;
    if (messageType === 'document' && !documentFile && !newMessage.trim()) return;
    if (messageType === 'video' && !videoFile && !snippetMediaUrl && !newMessage.trim()) return;
    if (messageType === 'audio' && !audioFile && !snippetMediaUrl && !newMessage.trim()) return;
    if (messageType === 'location' && !locationData) return;
    if (messageType === 'contact' && !contactData) return;
    if (messageType === 'interactive' && !selectedInteractivePreset) return;

    if (outboundSendLockRef.current) return;
    outboundSendLockRef.current = true;
    try {
      setSendingMessage(true);
      
      const replyToWam = replyToMessage?.whatsapp_message_id ? String(replyToMessage.whatsapp_message_id).trim() : '';
      let messageData = {
        messageType: messageType,
        conversationId: selectedConversation.id,
        ...(replyToWam ? { replyToWhatsAppMessageId: replyToWam } : {}),
      };

      // Build message data based on type
      switch (messageType) {
        case 'text':
          messageData.message = newMessage.trim();
          break;
        case 'image':
          if (imageFile) {
            try {
              const uploadRes = await uploadWhatsAppMedia(imageFile);
              messageData.imageUrl = uploadRes.data?.url;
              messageData.caption = newMessage.trim() || '';
              if (messageData.imageUrl) await sendMessage(messageData);
              else throw new Error('Upload failed');
            } catch (e) {
              throw e;
            }
            return;
          } else if (snippetMediaUrl && snippetMediaType === 'image') {
            messageData.imageUrl = snippetMediaUrl;
            messageData.caption = newMessage.trim() || '';
            await sendMessage(messageData);
            return;
          } else {
            messageData.imageUrl = newMessage.trim();
            messageData.caption = '';
          }
          break;
        case 'document':
          if (documentFile) {
            try {
              const uploadRes = await uploadWhatsAppMedia(documentFile);
              messageData.documentUrl = uploadRes.data?.url;
              messageData.filename = documentFile.name || 'document';
              messageData.caption = newMessage.trim() || '';
              if (messageData.documentUrl) await sendMessage(messageData);
              else throw new Error('Upload failed');
            } catch (e) {
              throw e;
            }
            return;
          } else {
            messageData.documentUrl = newMessage.trim();
            messageData.filename = 'document';
            messageData.caption = '';
          }
          break;
        case 'video':
          if (videoFile) {
            try {
              const uploadRes = await uploadWhatsAppMedia(videoFile);
              messageData.videoUrl = uploadRes.data?.url;
              messageData.caption = newMessage.trim() || '';
              if (messageData.videoUrl) await sendMessage(messageData);
              else throw new Error('Upload failed');
            } catch (e) {
              throw e;
            }
            return;
          } else if (snippetMediaUrl && snippetMediaType === 'video') {
            messageData.videoUrl = snippetMediaUrl;
            messageData.caption = newMessage.trim() || '';
            await sendMessage(messageData);
            return;
          } else {
            messageData.videoUrl = newMessage.trim();
            messageData.caption = '';
          }
          break;
        case 'audio':
          if (audioFile) {
            const useVoicePurpose = voiceRecordingForUploadRef.current;
            try {
              const uploadRes = await uploadWhatsAppMedia(audioFile, useVoicePurpose ? { purpose: 'voice_note' } : {});
              voiceRecordingForUploadRef.current = false;
              const hinted = uploadRes.data?.suggestedWhatsAppType;
              if (hinted === 'video' && uploadRes.data?.url) {
                messageData.messageType = 'video';
                messageData.videoUrl = uploadRes.data.url;
                messageData.caption = newMessage.trim() || '';
              } else {
              messageData.audioUrl = uploadRes.data?.url;
              messageData.caption = newMessage.trim() || '';
              }
              if ((hinted === 'video' ? messageData.videoUrl : messageData.audioUrl)) await sendMessage(messageData);
              else throw new Error('Upload failed');
            } catch (e) {
              if (useVoicePurpose) voiceRecordingForUploadRef.current = true;
              throw e;
            }
            return;
          } else if (snippetMediaUrl && snippetMediaType === 'audio') {
            messageData.audioUrl = snippetMediaUrl;
            messageData.caption = newMessage.trim() || '';
            await sendMessage(messageData);
            return;
          } else {
            messageData.audioUrl = newMessage.trim();
            messageData.caption = '';
          }
          break;
        case 'location':
          messageData.location = locationData;
          break;
        case 'contact':
          messageData.contact = contactData;
          break;
        case 'interactive':
          if (selectedInteractivePreset) {
            messageData.interactive = buildInteractiveFromPreset(selectedInteractivePreset);
            const inter = messageData.interactive;
            if (inter.type === 'list' && (!inter.action?.sections || inter.action.sections.length === 0)) {
              toast.error(language === 'ar' ? 'قالب القائمة غير صالح' : 'Invalid list preset');
              return;
            }
            if (inter.type === 'button' && (!inter.action?.buttons || inter.action.buttons.length === 0)) {
              toast.error(language === 'ar' ? 'قالب الأزرار غير صالح' : 'Invalid button preset');
              return;
            }
            if (selectedInteractivePreset.id) {
              messageData.presetId = selectedInteractivePreset.id;
              recordInteractivePresetUse(selectedInteractivePreset.id).catch(() => {});
            }
          }
          break;
      }

      await sendMessage(messageData);
    } catch (error) {
      console.error('Error sending message:', error);
      const data = error.response?.data;
      // Meta API returns details.error.message; server returns requires_template for 24h window
      const metaMsg = data?.details?.error?.message;
      const requiresTemplate = data?.requires_template || data?.details?.requires_template;
      const errorMsg = metaMsg || data?.error || error.message;
      
      // Check for specific error conditions
      const isTokenExpired = 
        (error.response?.status === 401 || error.response?.status === 403) ||
        (errorMsg && (
          errorMsg.includes('token has expired') ||
          errorMsg.includes('OAuthException') ||
          errorMsg.includes('Invalid OAuth access token') ||
          errorMsg.includes('token is invalid')
        ));
      
      const isConnectionError = 
        errorMsg && (
          errorMsg.includes('ECONNREFUSED') ||
          errorMsg.includes('Network Error') ||
          errorMsg.includes('connection refused')
        );

      if (requiresTemplate) {
        // Window is closed — scroll the template panel into view so the employee
        // can immediately pick and send a template without hunting for it.
        toast.error(language === 'ar' 
          ? 'انتهت نافذة الرد (24 ساعة). اختر قالباً معتمداً أدناه.'
          : 'Reply window expired (24h). Pick an approved template below.');
        setTimeout(() => {
          metaTemplatePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      } else if (isTokenExpired) {
        toast.error(language === 'ar'
          ? '🚨 انتهت صلاحية رمز الواتساب. يرجى إبلاغ المشرف لتحديث الإعدادات.'
          : '🚨 WhatsApp token expired. Please notify your supervisor to update settings.');
      } else if (isConnectionError) {
        toast.error(language === 'ar'
          ? '⚠️ مشكلة في الاتصال بالخادم. يرجى المحاولة مرة أخرى.'
          : '⚠️ Server connection issue. Please try again.');
      } else {
        toast.error(language === 'ar'
          ? `فشل إرسال الرسالة: ${errorMsg}`
          : `Failed to send message: ${errorMsg}`);
      }
    } finally {
      outboundSendLockRef.current = false;
      setSendingMessage(false);
    }
  };

  const handleRetryFailedMessage = async (msg) => {
    if (!msg?.id || String(msg.status || '').toLowerCase() !== 'failed' || mockData) return;
    if (retryingFailedMessageId) return;
    try {
      setRetryingFailedMessageId(msg.id);
      const { data } = await retryFailedWhatsAppMessage(msg.id);
      if (data?.simulated) {
        toast.success(language === 'ar' ? 'تمت المحاكاة (لا يوجد اتصال بميتا)' : 'Simulated send (no Meta credentials)');
      } else {
        toast.success(language === 'ar' ? 'تم طلب إعادة الإرسال' : 'Retry started — message will update when Meta responds');
      }
      if (selectedConversation?.id) {
        await loadMessages(selectedConversation.id);
      }
    } catch (err) {
      const apiErr = err.response?.data?.error || err.message;
      toast.error(language === 'ar' ? `فشلت إعادة المحاولة: ${apiErr}` : `Retry failed: ${apiErr}`);
    } finally {
      setRetryingFailedMessageId(null);
    }
  };

  const sendMessage = async (messageData) => {
    if (mockData) {
      const newMsg = {
        id: `m${Date.now()}`,
        sender: 'agent',
        message: messageData.message || messageData.caption || '[Media Message]',
        timestamp: new Date().toISOString(),
        messageType: messageData.messageType
      };
      setMessages([...messages, newMsg]);
      resetMessageForm();
    } else {
      await sendWhatsAppMessage(messageData.conversationId, messageData);
      await loadMessages(selectedConversation.id);
      // No full list reload here — the outbound message triggers a WS new_message event
      // which calls loadConversationsDebounced() (~600ms), avoiding a redundant double-fetch.
      resetMessageForm();
    }
  };

  const resetMessageForm = () => {
    setReplyToMessage(null);
    // Stop typing indicator when clearing form
    handleTypingStop();
    setNewMessage('');
    setMessageType('text');
    setImageFile(null);
    setDocumentFile(null);
    setVideoFile(null);
    setAudioFile(null);
    voiceRecordingForUploadRef.current = false;
    setSnippetMediaUrl(null);
    setSnippetMediaType(null);
    setSelectedInteractivePreset(null);
    setLocationData(null);
    setContactData(null);
    setShowTemplates(false);
    setShowMediaOptions(false);
    setShowLocationPicker(false);
    setLocationSearchQuery('');
    setLocationSearchResults([]);
    setShowContactForm(false);
    setContactSearchQuery('');
    setContactSearchResults([]);
    setIsRecording(false);
    setIsProcessingVoice(false);
    setRecordingError(null);
    setIsPlayingAudio(false);
    // Stop any active recording
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    // Stop audio playback
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.currentTime = 0;
    }
    audioChunksRef.current = [];
    if (locationSearchTimeoutRef.current) {
      clearTimeout(locationSearchTimeoutRef.current);
    }
    if (contactSearchTimeoutRef.current) {
      clearTimeout(contactSearchTimeoutRef.current);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShowComposerEmojiPicker(false);
  };

  const handleTemplateClick = (template) => {
    if (selectedConversation && !get24hWindowStatus(selectedConversation).open) {
      setShowTemplates(false);
      toast.error(language === 'ar'
        ? 'قوالب الرد السريع تعمل داخل نافذة 24 ساعة فقط. استخدم حقل "قالب ميتا" أعلاه.'
        : 'Quick-reply templates work only within the 24h window. Use the "Meta template" field above.');
      return;
    }
    const templateMessage = language === 'ar' ? template.message.ar : template.message.en;
    setNewMessage(templateMessage);
    setShowTemplates(false);
  };

  const handleSnippetClick = (snippet) => {
    // Fire-and-forget: increment usage counter without blocking UI
    if (snippet.id) recordSnippetUse(snippet.id).catch(() => {});
    setNewMessage(snippet.content || '');
    const meta = snippet.media_metadata || {};
    const mt = snippet.message_type || 'text';
    setSnippetMediaUrl(null);
    setSnippetMediaType(null);

    if (mt === 'image' && meta.image?.url) {
      setSnippetMediaUrl(meta.image.url);
      setSnippetMediaType('image');
      setMessageType('image');
    } else if (mt === 'video' && meta.video?.url) {
      setSnippetMediaUrl(meta.video.url);
      setSnippetMediaType('video');
      setMessageType('video');
    } else if (mt === 'audio' && meta.audio?.url) {
      setSnippetMediaUrl(meta.audio.url);
      setSnippetMediaType('audio');
      setMessageType('audio');
    } else if (mt === 'location' && meta.location?.lat != null && meta.location?.lng != null) {
      setLocationData({
        latitude: meta.location.lat,
        longitude: meta.location.lng,
        name: meta.location.name || '',
        address: meta.location.address || ''
      });
      setMessageType('location');
    } else if (meta.image?.url) {
      // fallback for older snippets without message_type
      setSnippetMediaUrl(meta.image.url);
      setSnippetMediaType('image');
      setMessageType('image');
    } else if (meta.video?.url) {
      setSnippetMediaUrl(meta.video.url);
      setSnippetMediaType('video');
      setMessageType('video');
    } else if (meta.audio?.url) {
      setSnippetMediaUrl(meta.audio.url);
      setSnippetMediaType('audio');
      setMessageType('audio');
    } else if (meta.location?.lat != null) {
      setLocationData({ latitude: meta.location.lat, longitude: meta.location.lng, name: meta.location.name || '', address: meta.location.address || '' });
      setMessageType('location');
    } else {
      setMessageType('text');
    }
    setShowSnippets(false);
    setSlashQuery(null);
    setSnippetSearch('');
  };

  // Formatting rendering function (similar to WhatsAppTemplatePreview)
  const renderFormattedText = (rawText) => {
    if (!rawText) return null;
    const lines = String(rawText).split('\n');
    const output = [];
    for (let li = 0; li < lines.length; li++) {
      if (li > 0) output.push(<br key={`br${li}`} />);
      const line = lines[li];
      // regex matches *bold*, _italic_, ~strikethrough~, `monospace`
      const re = /\*([^*]+)\*|_([^_]+)_|~([^~]+)~|`([^`]+)`/g;
      let cursor = 0;
      let m;
      while ((m = re.exec(line)) !== null) {
        // plain text before match
        if (m.index > cursor) {
          output.push(<span key={`${li}p${cursor}`}>{line.slice(cursor, m.index)}</span>);
        }
        if (m[1] !== undefined) {
          output.push(<strong key={`${li}b${m.index}`} style={{ fontWeight: 700 }}>{m[1]}</strong>);
        } else if (m[2] !== undefined) {
          output.push(<em key={`${li}i${m.index}`}>{m[2]}</em>);
        } else if (m[3] !== undefined) {
          output.push(<s key={`${li}s${m.index}`}>{m[3]}</s>);
        } else if (m[4] !== undefined) {
          output.push(<code key={`${li}c${m.index}`} style={{ fontFamily: 'monospace', backgroundColor: 'rgba(0,0,0,0.05)', padding: '2px 4px', borderRadius: '3px', fontSize: '0.9em' }}>{m[4]}</code>);
        }
        cursor = m.index + m[0].length;
      }
      // remainder of line after last match
      if (cursor < line.length) {
        output.push(<span key={`${li}p${cursor}`}>{line.slice(cursor)}</span>);
      }
    }
    return output;
  };

  // Insert formatting at cursor position
  const insertFormatting = (formatType) => {
    const textarea = messageInputRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = newMessage;
    const selectedText = text.substring(start, end);

    let before, after, replacement;
    switch (formatType) {
      case 'bold':
        before = '*';
        after = '*';
        break;
      case 'italic':
        before = '_';
        after = '_';
        break;
      case 'strikethrough':
        before = '~';
        after = '~';
        break;
      case 'monospace':
        before = '`';
        after = '`';
        break;
      default:
        return;
    }

    if (selectedText) {
      // Wrap selected text
      replacement = `${before}${selectedText}${after}`;
    } else {
      // Insert formatting markers at cursor
      replacement = `${before}${after}`;
    }

    const newText = text.substring(0, start) + replacement + text.substring(end);
    setNewMessage(newText);

    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = selectedText 
        ? start + replacement.length 
        : start + before.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const insertComposerEmoji = (emojiData) => {
    const emoji = emojiData?.emoji || emojiData;
    if (!emoji) return;
    const textarea = messageInputRef.current;
    if (!textarea) {
      setNewMessage((prev) => prev + emoji);
      setShowComposerEmojiPicker(false);
      return;
    }
    const start = textarea.selectionStart ?? newMessage.length;
    const end = textarea.selectionEnd ?? start;
    const text = newMessage;
    const newText = text.substring(0, start) + emoji + text.substring(end);
    setNewMessage(newText);
    setShowComposerEmojiPicker(false);
    setTimeout(() => {
      textarea.focus();
      const pos = start + emoji.length;
      textarea.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleKeyPress = (e) => {
    // Escape closes the slash-triggered snippet picker
    if (e.key === 'Escape' && slashQuery !== null) {
      e.preventDefault();
      setSlashQuery(null);
      setShowSnippets(false);
      return;
    }
    // Enter sends message, Shift+Enter creates new line
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (sendingMessage || outboundSendLockRef.current) return;
      if (selectedConversation && !get24hWindowStatus(selectedConversation).open) return;
      handleSendMessage();
    }
    // Shift+Enter allows new line (default textarea behavior - no preventDefault)
  };

  const inferComposerMediaType = (file) => {
    const t = (file && file.type) || '';
    const n = String(file && file.name || '').toLowerCase();
    if (t.startsWith('image/')) return 'image';
    if (t.startsWith('video/')) return 'video';
    if (t.startsWith('audio/')) return 'audio';
    if (t === 'application/pdf') return 'document';
    if (t.startsWith('application/vnd.ms-') || (t.startsWith('application/') && (
      t.includes('officedocument') || t.includes('msword') || t.includes('spreadsheetml') || t.includes('presentationml')
    ))) return 'document';
    if (t.startsWith('text/') && /\.(txt|csv|tsv)$/i.test(n)) return 'document';
    if (/\.(jpe?g|png|gif|webp|heic|bmp|svg)$/i.test(n)) return 'image';
    if (/\.(mp4|m4v|webm|mov|mkv|avi|3gp)$/i.test(n)) return 'video';
    if (/\.(mp3|ogg|m4a|aac|flac|wav|opus)$/i.test(n)) return 'audio';
    if (/\.(pdf|doc|docx?|xls|xlsx|pptx?|txt|csv)$/i.test(n)) return 'document';
    if (t.startsWith('text/')) return 'document';
    return null;
  };

  const assignMediaByType = (file, type, { fromDrop = false } = {}) => {
    if (type === 'image') {
      const ok = file.type.startsWith('image/')
        || (fromDrop && /\.(jpe?g|png|gif|webp|heic|bmp|svg)$/i.test(file.name || ''));
      if (!ok) {
        toast.error(language === 'ar' ? 'الرجاء اختيار ملف صورة' : 'Please select an image file');
        return false;
      }
      setImageFile(file);
      setMessageType('image');
    } else if (type === 'document') {
      setDocumentFile(file);
      setMessageType('document');
    } else if (type === 'video') {
      const ok = file.type.startsWith('video/')
        || (fromDrop && /\.(mp4|m4v|webm|mov|mkv|avi|3gp)$/i.test(file.name || ''));
      if (!ok) {
        toast.error(language === 'ar' ? 'الرجاء اختيار ملف فيديو' : 'Please select a video file');
        return false;
      }
      setVideoFile(file);
      setMessageType('video');
    } else if (type === 'audio') {
      const ok = file.type.startsWith('audio/')
        || (fromDrop && /\.(mp3|ogg|m4a|aac|flac|wav|opus)$/i.test(file.name || ''));
      if (!ok) {
        toast.error(language === 'ar' ? 'الرجاء اختيار ملف صوتي' : 'Please select an audio file');
        return false;
      }
      setAudioFile(file);
      setMessageType('audio');
    } else {
      return false;
    }
    return true;
  };

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    if (assignMediaByType(file, type, { fromDrop: false })) {
    setShowMediaOptions(false);
    }
  };

  const dragEnterCountRef = useRef(0);
  // Always holds the latest processDroppedFile — avoids stale closure in native listeners
  const processDropRef = useRef(null);

  const dtHasFiles = (e) => {
    const t = e?.dataTransfer?.types;
    if (!t) return false;
    for (let i = 0; i < t.length; i++) {
      if (t[i] === 'Files' || t[i] === 'application/x-moz-file') return true;
    }
    return false;
  };

  const processDroppedFile = (file) => {
    if (!file) return;
    const guessed = inferComposerMediaType(file);
    if (!guessed) {
      toast.error(language === 'ar' ? 'نوع الملف غير مدعوم' : 'Unsupported file type');
      return;
    }
    assignMediaByType(file, guessed, { fromDrop: true });
    setShowMediaOptions(false);
  };

  // Keep ref in sync every render so native listeners always call the latest version
  processDropRef.current = processDroppedFile;

  useEffect(() => {
    const onDocumentDragEnter = (e) => {
      if (!dtHasFiles(e)) return;
      e.preventDefault();
      if (!composerRef.current) return;
      dragEnterCountRef.current += 1;
      setComposerDragOverlay(true);
    };

    const onDocumentDragOver = (e) => {
      if (!dtHasFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };

    const onDocumentDragLeave = (e) => {
      if (!dtHasFiles(e)) return;
      dragEnterCountRef.current = Math.max(0, dragEnterCountRef.current - 1);
      if (dragEnterCountRef.current === 0) setComposerDragOverlay(false);
    };

    const onDocumentDrop = (e) => {
      e.preventDefault();
      dragEnterCountRef.current = 0;
      setComposerDragOverlay(false);
      // Only process the file if the composer is currently mounted (conversation open)
      if (!composerRef.current) return;
      const file = e.dataTransfer?.files?.[0];
      if (file) processDropRef.current?.(file);
    };

    const onDragEnd = () => {
      dragEnterCountRef.current = 0;
      setComposerDragOverlay(false);
    };

    document.addEventListener('dragenter', onDocumentDragEnter, true);
    document.addEventListener('dragover', onDocumentDragOver, true);
    document.addEventListener('dragleave', onDocumentDragLeave, true);
    document.addEventListener('drop', onDocumentDrop, true);
    window.addEventListener('dragend', onDragEnd);

    return () => {
      document.removeEventListener('dragenter', onDocumentDragEnter, true);
      document.removeEventListener('dragover', onDocumentDragOver, true);
      document.removeEventListener('dragleave', onDocumentDragLeave, true);
      document.removeEventListener('drop', onDocumentDrop, true);
      window.removeEventListener('dragend', onDragEnd);
    };
  }, []);

  // Search via API (Nominatim + Photon, Saudi bias; short codes e.g. RRRA6565)
  const searchLocation = async (query) => {
    const q = String(query || '').trim();
    if (!q || q.length < 2) {
      setLocationSearchResults([]);
      return;
    }

    setIsSearchingLocation(true);
    try {
      const data = await geocodeSearch(q, language === 'ar' ? 'ar' : 'en');
      setLocationSearchResults(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Location search error:', error);
      setLocationSearchResults([]);
      toast.error(language === 'ar' ? 'فشل البحث عن الموقع' : 'Failed to search location');
    } finally {
      setIsSearchingLocation(false);
    }
  };

  // Debounced location search
  const handleLocationSearchChange = (value) => {
    setLocationSearchQuery(value);
    if (locationSearchTimeoutRef.current) {
      clearTimeout(locationSearchTimeoutRef.current);
    }
    locationSearchTimeoutRef.current = setTimeout(() => {
      searchLocation(value);
    }, 400);
  };

  // Select a location from search results
  const handleSelectLocation = (result) => {
    setLocationData({
      latitude: parseFloat(result.lat),
      longitude: parseFloat(result.lon),
      name: result.display_name || locationSearchQuery,
      address: result.display_name || ''
    });
    setMessageType('location');
    setShowLocationPicker(false);
    setLocationSearchQuery('');
    setLocationSearchResults([]);
  };

  // Use current location
  const handleLocationSelect = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocationData({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            name: locationSearchQuery.trim() || (language === 'ar' ? 'موقعي الحالي' : 'My Current Location'),
            address: ''
          });
          setMessageType('location');
          setShowLocationPicker(false);
          setLocationSearchQuery('');
          setLocationSearchResults([]);
        },
        () => {
          toast.error(language === 'ar' ? 'فشل الحصول على الموقع' : 'Failed to get location');
        }
      );
    } else {
      toast.error(language === 'ar' ? 'المتصفح لا يدعم تحديد الموقع' : 'Browser does not support geolocation');
    }
  };

  // Search for contacts
  const searchContact = async (query) => {
    if (!query || query.length < 2) {
      setContactSearchResults([]);
      return;
    }

    setIsSearchingContact(true);
    try {
      const response = await getClients(query, null, null, null, null);
      const clients = response.data || [];
      // Limit to 5 results
      setContactSearchResults(clients.slice(0, 5));
    } catch (error) {
      console.error('Contact search error:', error);
      setContactSearchResults([]);
      // Don't show error toast for search failures, just return empty results
    } finally {
      setIsSearchingContact(false);
    }
  };

  // Debounced contact search
  const handleContactSearchChange = (value) => {
    setContactSearchQuery(value);
    if (contactSearchTimeoutRef.current) {
      clearTimeout(contactSearchTimeoutRef.current);
    }
    contactSearchTimeoutRef.current = setTimeout(() => {
      searchContact(value);
    }, 500);
  };

  // Select a contact from search results
  const handleSelectContact = (client) => {
    setContactData({
      name: client.name || '',
      phone: client.phone || '',
      email: client.email || ''
    });
    setContactSearchQuery('');
    setContactSearchResults([]);
  };

  const handleContactSubmit = () => {
    const fullPhone = contactPhoneLocal.trim()
      ? contactPhoneCode.replace(/\D/g, '') + contactPhoneLocal.replace(/\D/g, '')
      : '';
    if (!contactData?.name && !fullPhone) {
      toast.error(language === 'ar' ? 'الرجاء إدخال الاسم أو رقم الهاتف' : 'Please enter name or phone');
      return;
    }
    const formattedContact = {
      name: contactData?.name || '',
      firstName: contactData?.name?.split(' ')[0] || '',
      phone: fullPhone,
      phones: fullPhone ? [{ phone: fullPhone, type: 'MOBILE' }] : [],
      email: contactData?.email || '',
      emails: contactData?.email ? [{ email: contactData.email }] : []
    };
    setContactData(formattedContact);
    setMessageType('contact');
    setShowContactForm(false);
    setContactSearchQuery('');
    setContactSearchResults([]);
  };

  const handleInteractiveButtons = async (buttons) => {
    const interactiveData = {
      type: 'button',
      body: {
        text: newMessage.trim() || (language === 'ar' ? 'اختر خياراً' : 'Choose an option')
      },
      action: {
        buttons: buttons.map((btn, idx) => ({
          type: 'reply',
          reply: {
            id: `btn_${idx}`,
            title: btn
          }
        }))
      }
    };

    try {
      setSendingMessage(true);
      await sendMessage({
        conversationId: selectedConversation.id,
        messageType: 'interactive',
        interactive: interactiveData
      });
    } catch (error) {
      console.error('Error sending interactive message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleQRCodeGenerate = async () => {
    if (!selectedConversation) return;
    try {
      const response = await generateQRCode(selectedConversation.phone, newMessage.trim() || '');
      setShowQRCode(true);
      // QR code URL will be in response.data.qrCodeUrl
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  // Voice recording handlers
  const handleStartRecording = async () => {
    if (isRecording) return;
    
    try {
      setRecordingError(null);
      audioChunksRef.current = [];
      
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        } 
      });
      
      streamRef.current = stream;
      
      // Prefer WebM/Opus first so Chrome/Edge rarely emit misleading video/mp4 for mic capture.
      // Safari may still use audio/mp4 (sometimes reported as video/mp4) — server normalizes voice_note uploads.
      let mimeType = 'audio/webm';
      const supportedTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg',
        'audio/mpeg',
        'audio/mp4'
      ];
      
      for (const type of supportedTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }
      
      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType
      });
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Convert audio chunks to blob
        let audioBlob = new Blob(audioChunksRef.current, { 
          type: mediaRecorder.mimeType 
        });
        
        // If we got webm, convert to a WhatsApp-supported format
        // Create a File with a supported mimeType
        if (audioBlob.type === 'audio/webm' || audioBlob.type.startsWith('audio/webm')) {
          // Try to convert to audio/ogg (WhatsApp supports this)
          // Note: This doesn't actually convert the audio data, but changes the mimeType
          // WhatsApp may still reject if the actual format is incompatible
          // For a proper conversion, we'd need a library like ffmpeg.wasm
          audioBlob = new Blob(audioChunksRef.current, { 
            type: 'audio/ogg;codecs=opus' 
          });
          
          // Create a File object with the correct mimeType for upload
          const audioFile = new File([audioBlob], 'voice-note.ogg', { 
            type: 'audio/ogg;codecs=opus',
            lastModified: Date.now()
          });
          setAudioFile(audioFile);
        } else {
          // Other formats — keep real blob MIME; filename extension helps server/CDN
          const extension = audioBlob.type.includes('mp4') ? 'mp4' : 
                           audioBlob.type.includes('mpeg') ? 'mp3' : 
                           audioBlob.type.includes('ogg') ? 'ogg' : 'webm';
          const audioFile = new File([audioBlob], `voice-note.${extension}`, { 
            type: audioBlob.type,
            lastModified: Date.now()
          });
          setAudioFile(audioFile);
        }
        
        voiceRecordingForUploadRef.current = true;
        setMessageType('audio');
        setIsRecording(false);
        setIsProcessingVoice(false);
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      setIsRecording(false);
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error(language === 'ar'
          ? 'يجب السماح بالوصول إلى الميكروفون في إعدادات المتصفح.'
          : 'Please allow microphone access in your browser settings.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error(language === 'ar'
          ? 'لم يتم العثور على ميكروفون. يرجى التأكد من توصيل الميكروفون.'
          : 'Microphone not found. Please ensure a microphone is connected.');
      } else {
        toast.error(language === 'ar'
          ? 'حدث خطأ في بدء التسجيل. يرجى المحاولة مرة أخرى.'
          : 'Error starting recording. Please try again.');
      }
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop();
        setIsProcessingVoice(true);
      } catch (error) {
        console.error('Error stopping recording:', error);
        setIsRecording(false);
        setIsProcessingVoice(false);
      }
    }
  };

  const handleAutoLinkClient = async () => {
    if (!selectedConversation) return;
    try {
      const res = await autoLinkConversationClient(selectedConversation.id);
      const { linked_client_id, linked_client_name } = res?.data || {};
      if (linked_client_id) {
        const patch = { linked_client_id, linked_client_name: linked_client_name || null };
        setConversations((prev) => prev.map((c) =>
          c.id === selectedConversation.id ? { ...c, ...patch } : c
        ));
        setSelectedConversation((prev) => prev ? { ...prev, ...patch } : prev);
      }
    } catch (error) {
      console.error('Error auto-linking client:', error);
    }
  };

  const handleAutoAssign = async () => {
    if (!selectedConversation) return;
    try {
      let assignmentReason = '';
      if (selectedConversation.assigned_employee_id) {
        const reasonInput = window.prompt(
          'سبب إعادة التعيين (اختياري لكن مستحسن):',
          ''
        );
        if (reasonInput === null) return;
        assignmentReason = String(reasonInput || '').trim();
      }
      const res = await assignConversationToEmployee(selectedConversation.id, 'auto', assignmentReason);
      const { assigned_employee_id, assigned_employee_name } = res?.data || {};
      if (assigned_employee_id) {
        const patch = { assigned_employee_id, assigned_employee_name: assigned_employee_name || null };
        setConversations((prev) => prev.map((c) =>
          c.id === selectedConversation.id ? { ...c, ...patch } : c
        ));
        setSelectedConversation((prev) => prev ? { ...prev, ...patch } : prev);
      }
    } catch (error) {
      console.error('Error auto-assigning conversation:', error);
    }
  };

  const openReassignModal = async () => {
    if (!selectedConversation) return;
    setReassignModalOpen(true);
    setReassignTarget('');
    setReassignReasonDraft('');
    setAssignableLoading(true);
    setAssignableUsers([]);
    try {
      const res = await getWhatsAppAssignableUsers();
      const rows = Array.isArray(res.data) ? res.data : [];
      setAssignableUsers(rows);
      const currentAssigneeId =
        selectedConversation.assigned_employee_id != null
          ? Number(selectedConversation.assigned_employee_id)
          : null;
      const candidates = rows.filter(
        (u) => currentAssigneeId == null || Number(u.id) !== currentAssigneeId
      );
      setReassignTarget(candidates.length > 0 ? String(candidates[0].id) : '');
    } catch (error) {
      console.error('Error loading assignable users:', error);
      toast.error('تعذّر تحميل قائمة الفريق. حاول مرة أخرى.');
    } finally {
      setAssignableLoading(false);
    }
  };

  const confirmReassignFromModal = async () => {
    if (!selectedConversation) return;
    const reason = String(reassignReasonDraft || '').trim();
    if (!reason) {
      toast.error('سبب إعادة التعيين مطلوب.');
      return;
    }
    const n = parseInt(String(reassignTarget), 10);
    if (Number.isNaN(n)) {
      toast.error('اختر موظفاً من القائمة.');
      return;
    }
    try {
      const res = await assignConversationToEmployee(selectedConversation.id, n, reason);
      const { assigned_employee_id, assigned_employee_name } = res?.data || {};
      setReassignModalOpen(false);
      if (assigned_employee_id) {
        const patch = { assigned_employee_id, assigned_employee_name: assigned_employee_name || null };
        setConversations((prev) => prev.map((c) =>
          c.id === selectedConversation.id ? { ...c, ...patch } : c
        ));
        setSelectedConversation((prev) => prev ? { ...prev, ...patch } : prev);
      }
      toast.success('تم التعيين.');
    } catch (error) {
      console.error('Error reassigning conversation:', error);
      const msg = error?.response?.data?.error || error.message;
      toast.error(`فشل التعيين: ${msg}`);
    }
  };

  const roleLabelAr = (role) => {
    const r = String(role || '').toLowerCase();
    if (r === 'owner') return 'مالك';
    if (r === 'manager') return 'مدير';
    if (r === 'employee') return 'موظف';
    return role || '';
  };

  /** Manual reassign: org team only, excluding whoever is currently assigned (if any). */
  const reassignModalCandidates = useMemo(() => {
    if (!selectedConversation) return [];
    const rid = selectedConversation.assigned_employee_id != null
      ? Number(selectedConversation.assigned_employee_id)
      : null;
    return assignableUsers.filter(
      (u) => rid == null || Number(u.id) !== rid
    );
  }, [selectedConversation, assignableUsers]);

  useEffect(() => {
    if (!reassignModalOpen || assignableLoading) return;
    if (!reassignModalCandidates.length) return;
    if (!reassignModalCandidates.some((u) => String(u.id) === String(reassignTarget))) {
      setReassignTarget(String(reassignModalCandidates[0].id));
    }
  }, [reassignModalOpen, assignableLoading, reassignModalCandidates, reassignTarget]);

  /** Manual conversation type (lead / vendor / other) in workspace panel. */
  const handleConversationTypeChange = async (newType) => {
    if (!selectedConversation) return;
    try {
      const { data } = await setConversationType(selectedConversation.id, newType);
      const d = data || {};
      const isNonClient = newType === 'vendor' || newType === 'other';
      setConversations((prev) => prev.map((c) =>
        c.id === selectedConversation.id
          ? {
              ...c,
              conversation_type: newType,
              vendor_suggestion: false,
              followup_resolved_at: d.followup_resolved_at != null ? d.followup_resolved_at : (isNonClient ? new Date().toISOString() : null),
              followup_resolved_by: d.followup_resolved_by ?? null,
              followup_resolved_by_name: d.followup_resolved_by_name ?? null,
              waiting_for: isNonClient ? 'none' : c.waiting_for
            }
          : c
      ));
      setSelectedConversation((prev) => (prev ? {
        ...prev,
        conversation_type: newType,
        vendor_suggestion: false,
        followup_resolved_at: d.followup_resolved_at != null ? d.followup_resolved_at : (isNonClient ? new Date().toISOString() : null),
        followup_resolved_by: d.followup_resolved_by != null ? d.followup_resolved_by : null,
        followup_resolved_by_name: d.followup_resolved_by_name != null ? d.followup_resolved_by_name : null,
        waiting_for: isNonClient ? 'none' : prev.waiting_for
      } : prev));
      toast.success(language === 'ar' ? 'تم حفظ التصنيف.' : 'Classification saved.');
    } catch (e) {
      console.warn('Failed to set conversation type:', e?.message || e);
      const msg = e?.response?.data?.error || e?.message || '';
      toast.error(language === 'ar' ? `تعذّر حفظ التصنيف. ${msg}` : `Could not save classification. ${msg}`);
    }
  };

  const handleUpdateTags = async () => {
    if (!selectedConversation) return;
    const currentTags = Array.isArray(selectedConversation.tags) ? selectedConversation.tags.join(', ') : '';
    const input = window.prompt(
      language === 'ar'
        ? 'أدخل التصنيفات مفصولة بفاصلة (مثال: مستعجل، زيارة، ميزانية عالية)'
        : 'Enter comma-separated tags (e.g., urgent, viewing, high budget)',
      currentTags || (language === 'ar' ? 'مستعجل، متابعة، زيارة' : 'urgent, follow-up, viewing')
    );
    if (input === null) return;
    const tags = String(input)
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);

    try {
      await updateConversationTags(selectedConversation.id, tags);
      setConversations((prev) => prev.map((c) =>
        c.id === selectedConversation.id ? { ...c, tags } : c
      ));
      setSelectedConversation((prev) => prev ? { ...prev, tags } : prev);
    } catch (error) {
      console.error('Error updating tags:', error);
    }
  };

  const toggleSelectConversation = (conversationId) => {
    setSelectedConversationIds((prev) => {
      if (prev.includes(conversationId)) return prev.filter((id) => id !== conversationId);
      return [...prev, conversationId];
    });
  };

  const clearSelectedConversations = () => setSelectedConversationIds([]);

  const handleBulkMarkRead = async () => {
    if (!selectedConversationIds.length) return;
    try {
      await Promise.all(selectedConversationIds.map((id) => markConversationRead(id)));
      const ids = new Set(selectedConversationIds);
      setConversations((prev) => prev.map((c) =>
        ids.has(c.id) ? { ...c, unread_count: 0 } : c
      ));
      clearSelectedConversations();
    } catch (error) {
      console.error('Bulk mark read failed:', error);
    }
  };

  const handleBulkResolveFollowup = async () => {
    if (!selectedConversationIds.length) return;
    try {
      const results = await Promise.all(selectedConversationIds.map((id) =>
        resolveConversationFollowup(id).then((r) => ({ id, data: r?.data })).catch(() => ({ id, data: null }))
      ));
      setConversations((prev) => prev.map((c) => {
        const hit = results.find((r) => r.id === c.id && r.data?.success);
        if (!hit?.data) return c;
        return {
          ...c,
          followup_resolved_at: hit.data.followup_resolved_at,
          followup_resolved_by: hit.data.followup_resolved_by,
          followup_resolved_by_name: hit.data.followup_resolved_by_name,
          waiting_for: 'none',
          unread_count: 0
        };
      }));
      clearSelectedConversations();
      loadTriageCounts();
    } catch (error) {
      console.error('Bulk resolve follow-up failed:', error);
    }
  };

  const handleBulkAutoAssign = async () => {
    if (!selectedConversationIds.length) return;
    try {
      const results = await Promise.all(selectedConversationIds.map((id) =>
        assignConversationToEmployee(id, 'auto').then((r) => ({ id, data: r?.data })).catch(() => ({ id, data: null }))
      ));
      setConversations((prev) => prev.map((c) => {
        const hit = results.find((r) => r.id === c.id && r.data?.assigned_employee_id);
        if (!hit) return c;
        return { ...c, assigned_employee_id: hit.data.assigned_employee_id, assigned_employee_name: hit.data.assigned_employee_name || null };
      }));
      clearSelectedConversations();
    } catch (error) {
      console.error('Bulk auto-assign failed:', error);
    }
  };

  const handleBulkUpdateTags = async () => {
    if (!selectedConversationIds.length) return;
    const input = window.prompt(
      language === 'ar'
        ? 'أدخل التصنيفات (مفصولة بفاصلة) لتطبيقها على المحدد (مثال: مستعجل، متابعة)'
        : 'Enter tags (comma-separated) to apply on selected (e.g., urgent, follow-up)',
      language === 'ar' ? 'مستعجل، متابعة' : 'urgent, follow-up'
    );
    if (input === null) return;
    const tags = String(input).split(',').map((tag) => tag.trim()).filter(Boolean);
    try {
      await Promise.all(selectedConversationIds.map((id) => updateConversationTags(id, tags)));
      const ids = new Set(selectedConversationIds);
      setConversations((prev) => prev.map((c) =>
        ids.has(c.id) ? { ...c, tags } : c
      ));
      clearSelectedConversations();
    } catch (error) {
      console.error('Bulk tag update failed:', error);
    }
  };

  const getWaitingAgeLabel = (conversation) => {
    if (conversation?.waiting_for !== 'agent') return null;
    const source = conversation?.last_inbound_at || conversation?.last_message_at;
    if (!source) return null;
    const sourceMs = parseTimestampMs(source);
    if (!Number.isFinite(sourceMs)) return null;
    const mins = Math.max(0, Math.round((Date.now() - sourceMs) / 60000));
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h`;
  };

  /** Single line for the “waiting for staff” pill (RTL-friendly; respects hideMetadata with other badges). */
  const getWaitingEmployeeBadgeText = (conversation) => {
    const age = getWaitingAgeLabel(conversation);
    if (!age) return null;
    if (language === 'ar') {
      const dur = age.replace(/^(\d+)m$/, '$1 د').replace(/^(\d+)h$/, '$1 س');
      return `بانتظار الموظف · ${dur}`;
    }
    return `Waiting · ${age}`;
  };

  // 24h window status — prefers window_closes_at from GET /conversations (computed server-side).
  // Deep-link paths (e.g. Clients → createOrGetConversation) return raw rows without that column;
  // fall back to last_inbound_at + 24h so the UI matches list behavior and send enforcement.
  const get24hWindowStatus = (conversation) => {
    let closesAt = conversation?.window_closes_at;
    if (!closesAt && conversation?.last_inbound_at) {
      const inboundMs = parseTimestampMs(conversation.last_inbound_at);
      if (Number.isFinite(inboundMs)) {
        closesAt = new Date(inboundMs + 24 * 60 * 60 * 1000).toISOString();
      }
    }
    if (!closesAt) return { open: false };
    const closesMs = parseTimestampMs(closesAt);
    if (!Number.isFinite(closesMs)) return { open: false };
    const remainingMs = closesMs - Date.now();
    if (remainingMs <= 0) return { open: false };
    const hoursLeft = Math.floor(remainingMs / (60 * 60 * 1000));
    const minutesLeft = Math.floor((remainingMs % (60 * 60 * 1000)) / 60000);
    return { open: true, hoursLeft, minutesLeft };
  };

  const getSuggestedReplies = () => {
    if (!selectedConversation) return [];
    const lastClientMsg = [...messages].reverse().find((m) => m.sender === 'client')?.message || '';
    let profile = selectedConversation.ai_profile;
    if (typeof profile === 'string') {
      try { profile = JSON.parse(profile || '{}'); } catch (_) { profile = {}; }
    }
    profile = profile || {};
    const intent = (profile.intent || '').toLowerCase();
    const propType = (profile.property_type || '').toLowerCase();

    const snips = (snippets || []);
    const msg = lastClientMsg.toLowerCase();

    // Score each snippet: keyword relevance (0–5) + normalised usage bonus (0–2)
    const maxUsage = Math.max(...snips.map((s) => s.usage_count || 0), 1);
    const scoredSnips = snips.map((s) => {
      let score = 0;
      const cat = (s.category || '').toLowerCase();
      const cnt = (s.content || '').toLowerCase();
      const lbl = (s.label || '').toLowerCase();
      // Greeting signals
      const isGreeting = /^(مرحبا|السلام|اهلا|هلا|hi|hello|صباح|مساء)/i.test(msg.trim()) || (msg.length < 20 && /(مرحبا|السلام|اهلا|هلا)/i.test(msg));
      if (isGreeting && (cat.includes('greeting') || cat.includes('general'))) score += 5;
      // Intent signals
      if ((intent === 'rent' || /إيجار|ايجار/.test(msg)) && (cat.includes('rent') || cnt.includes('إيجار'))) score += 4;
      if ((intent === 'buy' || /شراء|بيع/.test(msg)) && (cat.includes('buy') || cnt.includes('شراء'))) score += 4;
      // Property type signals
      if ((propType === 'apartment' || msg.includes('شقة')) && (cnt.includes('شقة') || cnt.includes('apartment'))) score += 3;
      if ((propType === 'villa' || msg.includes('فيلا')) && (cnt.includes('فيلا') || cnt.includes('villa'))) score += 3;
      if ((propType === 'land' || /أرض|ارض/.test(msg)) && (cnt.includes('أرض') || cnt.includes('land'))) score += 3;
      // Budget/price signals
      if (/ميزانية|سعر|كم|ريال|مليون/.test(msg) && (cnt.includes('ميزانية') || cnt.includes('سعر') || cat.includes('budget') || cat.includes('pric'))) score += 4;
      // Keyword overlap between last message words and label/content
      const msgWords = msg.split(/\s+/).filter((w) => w.length > 3);
      msgWords.forEach((w) => { if (cnt.includes(w) || lbl.includes(w)) score += 1; });
      // Usage-count bonus (frequently used snippets are probably reliable)
      score += 2 * ((s.usage_count || 0) / maxUsage);
      return { ...s, _score: score };
    });

    // Sort descending by score, then pick top 3 unique contents
    scoredSnips.sort((a, b) => b._score - a._score);
    const suggestions = [];
    const used = new Set();
    for (const s of scoredSnips) {
      if (!s.content) continue;
      if (used.has(s.content)) continue;
      used.add(s.content);
      suggestions.push({ label: s.label, content: s.content });
      if (suggestions.length >= 3) break;
    }

    // Pad with templates if needed
    if (suggestions.length < 3 && templates?.length > 0) {
      const tLabel = (t) => (language === 'ar' ? t.name?.ar : t.name?.en) || t.name?.en || t.name?.ar || 'Template';
      const tContent = (t) => (language === 'ar' ? t.message?.ar : t.message?.en) || t.message?.en || t.message?.ar || '';
      for (const t of templates) {
        const tc = tContent(t);
        if (!tc || used.has(tc)) continue;
        used.add(tc);
        suggestions.push({ label: tLabel(t), content: tc });
        if (suggestions.length >= 3) break;
      }
    }
    return suggestions;
  };

  const suggestedReplies = getSuggestedReplies();

  /** Reaction sidecar rows (staff or client) are stored as separate DB messages; WhatsApp shows them on the target bubble only. */
  const reactionEmojiByTargetWamid = useMemo(() => {
    const map = new Map();
    const list = [...messages].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    for (const m of list) {
      if (!isReactionSidecarRecord(m)) continue;
      const meta = parseMessageMetadataLoose(m);
      const target = meta.reaction_target_wamid || meta.reactionTargetWamid;
      if (!target) continue;
      const emoji =
        meta.emoji !== undefined && meta.emoji !== null ? String(meta.emoji) : String(m.message || '');
      if (emoji === '') {
        map.delete(String(target));
      } else {
        map.set(String(target), emoji);
      }
    }
    return map;
  }, [messages]);

  // Reaction sidecar rows are normally hidden (emoji shown on target bubble).
  // Exception: if the target wamid doesn't exist in this thread (e.g. message
  // was sent from a previous system before migration), show the reaction as a
  // standalone visible bubble so agents know why the conversation was opened.
  const inboxMessagesForList = useMemo(() => {
    const knownWamids = new Set(
      messages.map((m) => m.whatsapp_message_id).filter(Boolean)
    );
    return messages.filter((m) => {
      if (!isReactionSidecarRecord(m)) return true; // normal message — always show
      const meta = parseMessageMetadataLoose(m);
      const targetWamid = meta.reaction_target_wamid || meta.reactionTargetWamid;
      // Keep (show as bubble) only when the target is not in this thread
      return targetWamid && !knownWamids.has(targetWamid);
    });
  }, [messages]);

  // Compute UI settings based on viewPreset (replaces density + readability + simpleMode)
  const getUISettings = () => {
    if (viewPreset === 'simple') {
      return {
        rowPadding: '18px',
        fontSize: '17px',
        fontSm: '14px',
        fontMd: '16px',
        controlHeight: '42px',
        hideMetadata: true
      };
    }
    if (viewPreset === 'sla_board') {
      return {
        rowPadding: '16px',
        fontSize: '16px',
        fontSm: '12px',
        fontMd: '14px',
        controlHeight: '36px',
        hideMetadata: false
      };
    }
    // list (default)
    return {
      rowPadding: '16px',
      fontSize: '16px',
      fontSm: '12px',
      fontMd: '14px',
      controlHeight: '36px',
      hideMetadata: false
    };
  };

  const uiSettings = getUISettings();
  const rowPadding = uiSettings.rowPadding;
  const rowPaddingAdjusted = uiSettings.rowPadding;
  const rowTitleSize = uiSettings.fontSize;
  const ui = {
    fontSm: uiSettings.fontSm,
    fontMd: uiSettings.fontMd,
    radiusSm: '8px',
    radiusMd: '10px',
    controlHeight: uiSettings.controlHeight,
    controlPaddingX: '10px'
  };

  const getLeadBadgeStyle = (leadTemperature) => {
    const normalized = String(leadTemperature || '').toLowerCase();
    if (normalized === 'high' || normalized === 'hot') return { bg: '#fee2e2', fg: '#991b1b' };
    if (normalized === 'medium' || normalized === 'warm') return { bg: '#ffedd5', fg: '#9a3412' };
    return { bg: '#f3f4f6', fg: '#374151' };
  };

  const formatTime = (timestamp) => {
    const tsMs = parseTimestampMs(timestamp);
    if (!Number.isFinite(tsMs)) return language === 'ar' ? 'الآن' : 'Now';
    const date = new Date(tsMs);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return language === 'ar' ? 'الآن' : 'Now';
    if (diffMins < 60) return `${diffMins} ${language === 'ar' ? 'د' : 'm'}`;
    if (diffHours < 24) return `${diffHours} ${language === 'ar' ? 'س' : 'h'}`;
    return `${diffDays} ${language === 'ar' ? 'يوم' : 'd'}`;
  };

  const formatDate = (timestamp) => {
    const tsMs = parseTimestampMs(timestamp);
    if (!Number.isFinite(tsMs)) return '';
    const date = new Date(tsMs);
    return date.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isNarrowLayout = viewportWidth < NARROW_LAYOUT_BREAKPOINT;
  const isMediumLayout = viewportWidth >= NARROW_LAYOUT_BREAKPOINT && viewportWidth < FILTER_COLLAPSE_BREAKPOINT;
  const forceListFocusForFilters = isMediumLayout && !!selectedConversation && isFilterDropdownOpen;
  const showConversationList = isNarrowLayout ? !selectedConversation : true;
  const showConversationThread = isNarrowLayout ? !!selectedConversation : !forceListFocusForFilters;
  const getConversationPreview = (conv) => {
    const rawMessage = String(conv?.last_message || '').trim();
    const messageType = String(conv?.last_message_type || '').toLowerCase();
    const senderRaw = String(conv?.last_message_sender || conv?.last_sender || '').toLowerCase();
    const senderLabel =
      senderRaw === 'agent'
        ? (language === 'ar' ? 'الموظف' : 'Employee')
        : senderRaw === 'client'
          ? (language === 'ar' ? 'العميل' : 'Client')
          : '';

    let typeLabel = '';
    let secondary = ''; // filename / name after localized type (avoid showing English bracket prefix)
    if (messageType === 'image' || rawMessage === '[Image]') {
      typeLabel = language === 'ar' ? 'صورة' : 'Image';
    } else if (messageType === 'video' || rawMessage === '[Video]') {
      typeLabel = language === 'ar' ? 'فيديو' : 'Video';
    } else if (messageType === 'audio' || rawMessage === '[Audio]' || rawMessage === '[Voice Note]') {
      typeLabel =
        rawMessage === '[Voice Note]'
          ? (language === 'ar' ? 'رسالة صوتية' : 'Voice Note')
          : (language === 'ar' ? 'صوت' : 'Audio');
    } else if (messageType === 'sticker' || rawMessage === '[Sticker]') {
      typeLabel = language === 'ar' ? 'ملصق' : 'Sticker';
    } else if (messageType === 'document' || rawMessage === '[Document]' || /^\[Document:/i.test(rawMessage)) {
      typeLabel = language === 'ar' ? 'مستند' : 'Document';
      const docM = rawMessage.match(/^\[Document:\s*(.+)\]$/i);
      if (docM) secondary = docM[1].trim();
    } else if (messageType === 'location' || rawMessage === '[Location]' || /^\[Location:/i.test(rawMessage)) {
      typeLabel = language === 'ar' ? 'موقع' : 'Location';
      const locM = rawMessage.match(/^\[Location:\s*(.+)\]$/i);
      if (locM) secondary = locM[1].trim();
    } else if (messageType === 'contact' || rawMessage === '[Contact]' || /^\[Contact:/i.test(rawMessage)) {
      typeLabel = language === 'ar' ? 'جهة اتصال' : 'Contact';
      const contactM = rawMessage.match(/^\[Contact:\s*(.+)\]$/i);
      if (contactM) secondary = contactM[1].trim();
    } else if (/^\[Template:/i.test(rawMessage)) {
      typeLabel = language === 'ar' ? 'قالب' : 'Template';
      const templateM = rawMessage.match(/^\[Template:\s*(.+)\]$/i);
      if (templateM) secondary = templateM[1].trim();
    } else if (messageType === 'interactive' || ['[Interactive Message]', '[Button Response]', '[List Response]'].includes(rawMessage)) {
      typeLabel = language === 'ar' ? 'تفاعلية' : 'Interactive';
    }

    const readableMessage = rawMessage || (language === 'ar' ? 'لا توجد رسائل' : 'No messages');
    const englishBracketOnly = new Set([
      '[Image]', '[Video]', '[Audio]', '[Voice Note]', '[Contact]', '[Location]', '[Document]',
      '[Sticker]', '[Interactive Message]', '[Button Response]', '[List Response]'
    ]);
    const suppressRaw =
      !!typeLabel &&
      (englishBracketOnly.has(rawMessage) ||
        /^\[(Document|Location|Contact|Template):/i.test(rawMessage));

    const parts = [];
    if (senderLabel) parts.push(`${senderLabel}:`);
    if (typeLabel) parts.push(`[${typeLabel}]`);
    if (secondary) parts.push(secondary);
    else if (rawMessage && !suppressRaw) parts.push(readableMessage);
    else if (!rawMessage && typeLabel) {
      parts.push('');
    } else if (!typeLabel) {
      parts.push(readableMessage);
    }
    return parts.join(' ').trim() || readableMessage;
  };
  const formatLeadTemperature = (value) => {
    const normalized = String(value || 'low').toLowerCase();
    if (language === 'ar') {
      if (normalized === 'high' || normalized === 'hot') return 'عالية';
      if (normalized === 'medium' || normalized === 'warm') return 'متوسطة';
      return 'منخفضة';
    }
    if (normalized === 'high' || normalized === 'hot') return 'High';
    if (normalized === 'medium' || normalized === 'warm') return 'Medium';
    return 'Low';
  };
  const formatWaitingFor = (value) => {
    const normalized = String(value || 'none').toLowerCase();
    if (language === 'ar') {
      if (normalized === 'agent') return 'الموظف';
      if (normalized === 'client') return 'العميل';
      return 'لا يوجد';
    }
    if (normalized === 'agent') return 'Employee';
    if (normalized === 'client') return 'Client';
    return 'None';
  };
  const getSlaUrgency = (conv) => {
    const waiting = getWaitingMinutes(conv);
    if (conv.waiting_for !== 'agent') return 'normal';
    if (waiting >= 60) return 'critical';
    if (waiting >= 30) return 'warning';
    return 'normal';
  };
  const getEscalationGroup = (conv) => {
    const urgency = getSlaUrgency(conv);
    if (urgency === 'critical') return 'critical';
    if (urgency === 'warning') return 'warning';
    return 'normal';
  };
  const getSlaUrgencyStyle = (conv) => {
    const urgency = getSlaUrgency(conv);
    if (urgency === 'critical') {
      return { border: '1px solid #fecaca', stripe: '#ef4444', bg: '#fff7f7' };
    }
    if (urgency === 'warning') {
      return { border: '1px solid #fed7aa', stripe: '#f59e0b', bg: '#fffbeb' };
    }
    return { border: '1px solid #e5e7eb', stripe: '#e5e7eb', bg: selectedConversation ? '#fff' : '#fff' };
  };
  const getConfidenceScore = (conv) => {
    const priority = Number(conv.priority_score || 0);
    let score = 45;
    const readiness = String(conv.client_readiness || conv.lead_temperature || '').toLowerCase();
    if (readiness === 'high' || readiness === 'hot') score += 28;
    else if (readiness === 'medium' || readiness === 'warm') score += 16;
    score += Math.min(20, Math.round(priority / 6));
    if (Number(conv.unread_count || 0) > 0) score += 5;
    return Math.max(35, Math.min(95, score));
  };
  const togglePinConversation = (conversationId) => {
    setPinnedConversationIds((prev) =>
      prev.includes(conversationId) ? prev.filter((id) => id !== conversationId) : [...prev, conversationId]
    );
  };
  const toolPillStyle = (active) => ({
    display: 'flex', alignItems: 'center', gap: '5px',
    padding: '5px 11px', fontSize: '12px', fontWeight: 600,
    border: active ? '1.5px solid #22c55e' : '1.5px solid #e2e8f0',
    borderRadius: '9999px',
    background: active ? '#dcfce7' : '#f9fafb',
    color: active ? '#15803d' : '#374151',
    cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s ease',
  });
  const mediaItemStyle = {
    padding: '7px 12px', border: 'none', background: 'transparent',
    display: 'flex', alignItems: 'center', gap: '8px',
    fontSize: '13px', color: '#111827', cursor: 'pointer',
    borderRadius: '7px', width: '100%', textAlign: 'left',
    transition: 'background 0.1s',
  };
  const getInteractiveButtonStyle = ({ active = false, subtle = false } = {}) => ({
    height: ui.controlHeight,
    padding: `0 ${ui.controlPaddingX}`,
    border: active ? '1px solid #111827' : '1px solid #d5dde8',
    borderRadius: '9999px',
    background: active
      ? 'linear-gradient(135deg, #111827, #1f2937)'
      : subtle
        ? 'linear-gradient(135deg, #f8fafc, #ffffff)'
        : '#ffffff',
    color: active ? '#ffffff' : '#111827',
    fontSize: ui.fontSm,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: active
      ? '0 6px 14px rgba(17, 24, 39, 0.24)'
      : '0 1px 2px rgba(15, 23, 42, 0.07)',
    transition: 'all 0.2s ease'
  });
  const handleControlMouseEnter = (e) => {
    e.currentTarget.style.transform = 'translateY(-1px)';
    e.currentTarget.style.boxShadow = '0 8px 16px rgba(15, 23, 42, 0.14)';
  };
  const handleControlMouseLeave = (e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = e.currentTarget.dataset.active === 'true'
      ? '0 6px 14px rgba(17, 24, 39, 0.24)'
      : '0 1px 2px rgba(15, 23, 42, 0.07)';
  };
  const filterGridColumns = viewportWidth < 420 ? '1fr' : 'repeat(2, minmax(0, 1fr))';
  const filterSelectStyle = {
    width: '100%',
    height: ui.controlHeight,
    padding: `0 ${ui.controlPaddingX}`,
    border: '1px solid #d5dde8',
    borderRadius: '9999px',
    fontSize: ui.fontSm,
    fontWeight: 600,
    color: '#111827',
    background: 'linear-gradient(135deg, #ffffff, #f9fbff)',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.07)',
    transition: 'all 0.2s ease',
    outline: 'none'
  };

  return (
    <div className="wa-animate-in wa-inbox-root">
      {/* Left Panel - Conversation List */}
      {showConversationList && (
      <div
        className={`wa-inbox-left${isNarrowLayout ? ' wa-inbox-left--narrow' : ''}`}
      >
        <div className="wa-inbox-header">
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #25D366, #20BA5A)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(37,211,102,0.3)' }}>
            <FaWhatsapp style={{ color: '#fff', fontSize: '20px' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--wa-text)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {language === 'ar' ? 'محادثات الواتساب' : 'WhatsApp Conversations'}
            {!mockData && (
              <span style={{ fontSize: '10px', fontWeight: '600', color: wsConnected ? '#16a34a' : '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: wsConnected ? '#22c55e' : '#cbd5e1' }} />
                {wsConnected ? (language === 'ar' ? 'مباشر' : 'Live') : (language === 'ar' ? 'غير متصل' : 'Offline')}
              </span>
            )}
          </h2>
            <button
              onClick={() => {
                loadConversations();
                if (selectedConversation) {
                  loadMessages(selectedConversation.id);
                }
              }}
              title={language === 'ar' ? 'تحديث المحادثات' : 'Refresh conversations'}
              style={{
                padding: '8px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                background: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f9fafb';
                e.currentTarget.style.color = '#111827';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              <FaSync style={{ fontSize: '16px' }} />
            </button>
          </div>
          {/* Tier 1: Quick Actions - Search */}
          <input
            ref={searchInputRef}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={language === 'ar' ? 'بحث... (/)' : 'Search... (/)'}
            title={language === 'ar' ? 'اضغط / للتركيز على البحث' : 'Press / to focus search'}
            style={{
              width: '100%',
              height: ui.controlHeight,
              padding: `0 ${ui.controlPaddingX}`,
              border: '1px solid #e5e7eb',
              borderRadius: ui.radiusMd,
              fontSize: ui.fontSm,
              outline: 'none',
              transition: 'all 0.2s ease',
              backgroundColor: '#fff'
            }}
          />

          {/* Tier 1: Quick Triage Pills */}
          <div style={{ display: 'flex', gap: '6px', width: '100%', flexWrap: 'wrap' }}>
            <button
              onClick={() => setQuickTriageFilter('all')}
              data-active={quickTriageFilter === 'all' ? 'true' : 'false'}
              style={{ ...getInteractiveButtonStyle({ active: quickTriageFilter === 'all' }), flex: '1 1 auto', minWidth: '60px' }}
            >
              {language === 'ar' ? 'الكل' : 'All'}
            </button>
            <button
              onClick={() => setQuickTriageFilter('needs_attention')}
              data-active={quickTriageFilter === 'needs_attention' ? 'true' : 'false'}
              style={{ ...getInteractiveButtonStyle({ active: quickTriageFilter === 'needs_attention' }), flex: '1 1 auto', minWidth: '90px' }}
            >
              {language === 'ar' ? 'يحتاج متابعة' : 'Needs Attention'}
              {triageCounts.needs_attention > 0 && (
                <span style={{ marginLeft: '4px', padding: '2px 6px', background: '#ef4444', color: '#fff', borderRadius: '9999px', fontSize: '10px', fontWeight: '700' }}>
                  {triageCounts.needs_attention}
                </span>
              )}
            </button>
            <button
              onClick={() => setQuickTriageFilter('vendor_non_client')}
              data-active={quickTriageFilter === 'vendor_non_client' ? 'true' : 'false'}
              style={{ ...getInteractiveButtonStyle({ active: quickTriageFilter === 'vendor_non_client' }), flex: '1 1 auto', minWidth: '72px' }}
            >
              {language === 'ar' ? 'مزوّد / غير عميل' : 'Vendor / Other'}
              {triageCounts.vendor_non_client > 0 && (
                <span style={{ marginLeft: '4px', padding: '2px 6px', background: '#d97706', color: '#fff', borderRadius: '9999px', fontSize: '10px', fontWeight: '700' }}>
                  {triageCounts.vendor_non_client}
                </span>
              )}
            </button>
            <button
              onClick={() => setQuickTriageFilter('high')}
              data-active={quickTriageFilter === 'high' ? 'true' : 'false'}
              style={{ ...getInteractiveButtonStyle({ active: quickTriageFilter === 'high' }), flex: '1 1 auto', minWidth: '60px' }}
            >
              {language === 'ar' ? 'عالية' : 'High'}
              {triageCounts.high > 0 && (
                <span style={{ marginLeft: '4px', padding: '2px 6px', background: '#ef4444', color: '#fff', borderRadius: '9999px', fontSize: '10px', fontWeight: '700' }}>
                  {triageCounts.high}
                </span>
              )}
            </button>
            <button
              onClick={() => setQuickTriageFilter('unassigned')}
              data-active={quickTriageFilter === 'unassigned' ? 'true' : 'false'}
              style={{ ...getInteractiveButtonStyle({ active: quickTriageFilter === 'unassigned' }), flex: '1 1 auto', minWidth: '70px' }}
            >
              {language === 'ar' ? 'غير معين' : 'Unassigned'}
              {triageCounts.unassigned > 0 && (
                <span style={{ marginLeft: '4px', padding: '2px 6px', background: '#8b5cf6', color: '#fff', borderRadius: '9999px', fontSize: '10px', fontWeight: '700' }}>
                  {triageCounts.unassigned}
                </span>
              )}
            </button>
          </div>

          {/* Tier 2: View Preset + Sort + Tier 3: More Filters Toggle */}
          <div style={{ display: 'flex', gap: '6px', width: '100%', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: ui.fontSm, color: '#6b7280', whiteSpace: 'nowrap' }}>
              {language === 'ar' ? 'العرض:' : 'View:'}
            </span>
            <select
              value={viewPreset}
              onChange={(e) => setViewPreset(e.target.value)}
              style={{ ...filterSelectStyle, flex: '1 1 180px', minWidth: '150px' }}
              aria-label={language === 'ar' ? 'وضع العرض' : 'View mode'}
            >
              <option value="list">{language === 'ar' ? '📋 قائمة' : '📋 List'}</option>
              <option value="sla_board">{language === 'ar' ? '🎯 مستوى الخدمة' : '🎯 SLA Board'}</option>
              <option value="simple">{language === 'ar' ? '🎨 مبسط' : '🎨 Simple'}</option>
            </select>
            <select
              value={sortMode}
              onFocus={() => setIsFilterDropdownOpen(true)}
              onBlur={() => setIsFilterDropdownOpen(false)}
              onChange={(e) => {
                setSortMode(e.target.value);
                setIsFilterDropdownOpen(false);
              }}
              style={{ ...filterSelectStyle, flex: '0 0 120px', minWidth: '110px' }}
              onMouseEnter={handleControlMouseEnter}
              onMouseLeave={handleControlMouseLeave}
              title={language === 'ar' ? 'ترتيب المحادثات' : 'Sort conversations'}
              aria-label={language === 'ar' ? 'طريقة الترتيب' : 'Sort mode'}
            >
              <option value="readiness">{language === 'ar' ? 'الجاهزية' : 'Readiness'}</option>
              <option value="latest_message">{language === 'ar' ? 'الأحدث' : 'Latest'}</option>
            </select>
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              data-active={showAdvancedFilters ? 'true' : 'false'}
              style={{ ...getInteractiveButtonStyle({ active: showAdvancedFilters }), flex: '0 0 auto', whiteSpace: 'nowrap' }}
              title={language === 'ar' ? 'فلاتر متقدمة' : 'Advanced filters'}
            >
              {language === 'ar' ? 'المزيد من الفلاتر' : 'More Filters'}
              {(waitingFilter !== 'all' || leadFilter !== 'all' || linkedFilter !== 'all' || businessPhoneFilter !== 'all' || showCampaignOnlyConversations || assigneeFilterActive) && (
                <span style={{ marginLeft: '4px', padding: '2px 6px', background: '#3b82f6', color: '#fff', borderRadius: '9999px', fontSize: '10px', fontWeight: '700' }}>
                  {[waitingFilter !== 'all', leadFilter !== 'all', linkedFilter !== 'all', businessPhoneFilter !== 'all', showCampaignOnlyConversations, assigneeFilterActive].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>

          {/* Tier 3: Advanced Filters (Collapsible) */}
          {showAdvancedFilters && (
            <>
            <div style={{ width: '100%', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', paddingTop: '6px', borderTop: '1px solid #e5e7eb' }}>
            {(userRole === 'owner' || userRole === 'manager') && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                  {language === 'ar' ? 'المكلّف' : 'Assignee'}
                </div>
                <select
                  value={assigneeScope}
                  onFocus={() => setIsFilterDropdownOpen(true)}
                  onBlur={() => setIsFilterDropdownOpen(false)}
                  onChange={(e) => {
                    setAssigneeScope(e.target.value);
                    setIsFilterDropdownOpen(false);
                  }}
                  style={filterSelectStyle}
                  onMouseEnter={handleControlMouseEnter}
                  onMouseLeave={handleControlMouseLeave}
                  aria-label={language === 'ar' ? 'تصفية المكلّف' : 'Assignee filter'}
                >
                  <option value="all">{language === 'ar' ? 'الفريق كامل' : 'All team'}</option>
                  <option value="unassigned">{language === 'ar' ? 'غير معيّن' : 'Unassigned'}</option>
                  {inboxAssigneeOptions.map((u) => (
                    <option key={u.id} value={`id:${u.id}`}>{u.name}</option>
                  ))}
                </select>
              </div>
            )}
            {userRole === 'employee' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                  {language === 'ar' ? 'المكلّف' : 'Assignee'}
                </div>
                <select
                  value={assigneeScope}
                  onFocus={() => setIsFilterDropdownOpen(true)}
                  onBlur={() => setIsFilterDropdownOpen(false)}
                  onChange={(e) => {
                    setAssigneeScope(e.target.value);
                    setIsFilterDropdownOpen(false);
                  }}
                  style={filterSelectStyle}
                  onMouseEnter={handleControlMouseEnter}
                  onMouseLeave={handleControlMouseLeave}
                  aria-label={language === 'ar' ? 'تصفية حسب التعيين' : 'Assignee filter'}
                >
                  <option value="all">{language === 'ar' ? 'كل المحادثات' : 'All conversations'}</option>
                  <option value="mine">{language === 'ar' ? 'المعيّنة لي' : 'Assigned to me'}</option>
                  <option value="unassigned">{language === 'ar' ? 'غير معيّن' : 'Unassigned'}</option>
                </select>
              </div>
            )}
            <div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                {language === 'ar' ? 'حالة الانتظار' : 'Waiting'}
              </div>
              <select
                value={waitingFilter}
                onFocus={() => setIsFilterDropdownOpen(true)}
                onBlur={() => setIsFilterDropdownOpen(false)}
                onChange={(e) => {
                  setWaitingFilter(e.target.value);
                  setIsFilterDropdownOpen(false);
                }}
                style={filterSelectStyle}
                onMouseEnter={handleControlMouseEnter}
                onMouseLeave={handleControlMouseLeave}
              >
                <option value="all">{language === 'ar' ? 'الكل' : 'All'}</option>
                <option value="agent">{language === 'ar' ? 'بانتظار الموظف' : 'Waiting Employee'}</option>
                <option value="client">{language === 'ar' ? 'بانتظار العميل' : 'Waiting Client'}</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                {language === 'ar' ? 'جاهزية العميل' : 'Readiness'}
              </div>
              <select
                value={leadFilter}
                onFocus={() => setIsFilterDropdownOpen(true)}
                onBlur={() => setIsFilterDropdownOpen(false)}
                onChange={(e) => {
                  setLeadFilter(e.target.value);
                  setIsFilterDropdownOpen(false);
                }}
                style={filterSelectStyle}
                onMouseEnter={handleControlMouseEnter}
                onMouseLeave={handleControlMouseLeave}
              >
                <option value="all">{language === 'ar' ? 'الكل' : 'All'}</option>
                <option value="high">{language === 'ar' ? 'عالية' : 'High'}</option>
                <option value="medium">{language === 'ar' ? 'متوسطة' : 'Medium'}</option>
                <option value="low">{language === 'ar' ? 'منخفضة' : 'Low'}</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                {language === 'ar' ? 'ربط العميل' : 'Client Link'}
              </div>
              <select
                value={linkedFilter}
                onFocus={() => setIsFilterDropdownOpen(true)}
                onBlur={() => setIsFilterDropdownOpen(false)}
                onChange={(e) => {
                  setLinkedFilter(e.target.value);
                  setIsFilterDropdownOpen(false);
                }}
                style={filterSelectStyle}
                onMouseEnter={handleControlMouseEnter}
                onMouseLeave={handleControlMouseLeave}
              >
                <option value="all">{language === 'ar' ? 'الكل' : 'All'}</option>
                <option value="true">{language === 'ar' ? 'مرتبط' : 'Linked'}</option>
                <option value="false">{language === 'ar' ? 'غير مرتبط' : 'Unlinked'}</option>
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                {language === 'ar' ? 'رقم النشاط' : 'Business Number'}
              </div>
              <select
                value={businessPhoneFilter}
                onFocus={() => setIsFilterDropdownOpen(true)}
                onBlur={() => setIsFilterDropdownOpen(false)}
                onChange={(e) => {
                  setBusinessPhoneFilter(e.target.value);
                  setIsFilterDropdownOpen(false);
                }}
                style={filterSelectStyle}
                onMouseEnter={handleControlMouseEnter}
                onMouseLeave={handleControlMouseLeave}
              >
                <option value="all">{language === 'ar' ? 'كل الأرقام' : 'All numbers'}</option>
                {businessPhoneOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#334155', cursor: 'pointer', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}>
                <input
                  type="checkbox"
                  checked={showCampaignOnlyConversations}
                  onChange={(e) => setShowCampaignOnlyConversations(e.target.checked)}
                />
                <span>
                  {language === 'ar'
                    ? 'إظهار محادثات الحملات بدون رد'
                    : 'Show campaign-only conversations (no client reply yet)'}
                </span>
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setWaitingFilter('all');
                setLeadFilter('all');
                setLinkedFilter('all');
                setBusinessPhoneFilter('all');
                setSortMode('latest_message');
                setQuickTriageFilter('all');
                setShowCampaignOnlyConversations(false);
                setAssigneeScope('all');
              }}
              className="wa-btn wa-btn-secondary"
              style={{ height: ui.controlHeight, padding: `0 ${ui.controlPaddingX}`, fontSize: ui.fontSm }}
            >
              {language === 'ar' ? 'إعادة تعيين الفلاتر' : 'Reset filters'}
            </button>
          </div>
            </>
          )}
          {selectedConversationIds.length > 0 && (
            <div className="wa-bulk-bar" style={{ width: '100%', position: 'sticky', top: '6px', zIndex: 3 }}>
              <span>
                {selectedConversationIds.length} {language === 'ar' ? 'محدد' : 'selected'}
              </span>
              {canInboxTriage && (
              <button onClick={handleBulkMarkRead} className="wa-btn wa-bulk-btn-primary">
                {language === 'ar' ? 'تمييز كمقروء' : 'Mark Read'}
              </button>
              )}
              {canInboxTriage && (
              <button onClick={handleBulkResolveFollowup} className="wa-btn wa-bulk-btn-secondary">
                {language === 'ar' ? 'لا تحتاج متابعة' : 'No Follow-up'}
              </button>
              )}
              {canInboxTriage && (
              <button onClick={handleBulkAutoAssign} className="wa-btn wa-bulk-btn-secondary">
                تعيين تلقائي
              </button>
              )}
              {INBOX_SHOW_TAGS_UI && (
              <button onClick={handleBulkUpdateTags} className="wa-btn wa-bulk-btn-secondary">
                {language === 'ar' ? 'تصنيفات' : 'Tags'}
              </button>
              )}
              <button onClick={clearSelectedConversations} className="wa-btn wa-bulk-btn-secondary" style={{ marginLeft: 'auto' }}>
                {language === 'ar' ? 'إلغاء' : 'Clear'}
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="wa-loading" style={{ flex: 1, minHeight: 120 }}>
            <div className="wa-loading-spinner" />
            <p className="wa-loading-text">
              {language === 'ar' ? 'جاري تحميل المحادثات...' : 'Loading conversations...'}
            </p>
          </div>
        ) : (
          <div ref={conversationListRef} style={{
            overflowY: 'auto',
            flex: 1,
            minHeight: 0,
            paddingTop: '8px',
            paddingLeft: '8px',
            paddingRight: '8px',
            paddingBottom: 'max(20px, calc(8px + env(safe-area-inset-bottom, 0px)))',
          }}>
            {viewPreset === 'sla_board' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { key: 'critical', title: language === 'ar' ? 'حرج' : 'Critical', bg: '#fff1f2', border: '#fecdd3' },
                  { key: 'warning', title: language === 'ar' ? 'تنبيه' : 'Warning', bg: '#fffbeb', border: '#fed7aa' },
                  { key: 'normal', title: language === 'ar' ? 'طبيعي' : 'Normal', bg: '#f8fafc', border: '#e2e8f0' }
                ].map((section) => {
                  const items = triagedConversations.filter((conv) => getEscalationGroup(conv) === section.key);
                  return (
                    <div key={section.key} style={{ backgroundColor: section.bg, border: `1px solid ${section.border}`, borderRadius: '10px', padding: '8px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '8px', color: '#111827' }}>
                        {section.title} ({items.length})
                      </div>
                      {items.length === 0 ? (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          {language === 'ar' ? 'لا توجد محادثات' : 'No conversations'}
                        </div>
                      ) : (
                        items.map((conv) => (
                          <div
                            key={conv.id}
                            onClick={() => setSelectedConversation(conv)}
                            style={{
                              backgroundColor: selectedConversation?.id === conv.id ? '#fff5f0' : '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              padding: '8px',
                              marginBottom: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#111827' }}>{conversationPrimaryTitle(conv)}</div>
                            {conversationPhoneSubtitle(conv) && (
                              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{conversationPhoneSubtitle(conv)}</div>
                            )}
                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                              {language === 'ar' ? 'انتظار' : 'Wait'}: {getWaitingMinutes(conv)}m • {language === 'ar' ? 'غير مقروء' : 'Unread'}: {Number(conv.unread_count || 0)}
                            </div>
                            <div style={{ fontSize: '11px', color: '#4b5563', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {getConversationPreview(conv)}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  );
                })}
              </div>
            ) : triagedConversations.length === 0 ? (
              <div className="wa-empty-state">
                <div className="wa-empty-state-icon">
                  <FaWhatsapp />
                </div>
                <div>{language === 'ar' ? 'لا توجد محادثات' : 'No conversations'}</div>
              </div>
            ) : (
              triagedConversations.map((conv) => {
                const staffLine = conversationStaffLine(conv);
                const bizFriendly = conversationBusinessLineFriendly(conv);
                return (
                <div
                  key={conv.id}
                  onClick={() => {
                    setSelectedConversation(conv);
                    setIsFilterDropdownOpen(false);
                  }}
                  data-urgency={getSlaUrgency(conv)}
                  style={{
                    padding: rowPaddingAdjusted,
                    cursor: 'pointer',
                    backgroundColor: selectedConversation?.id === conv.id ? '#fff5f0' : getSlaUrgencyStyle(conv).bg,
                    transition: 'all 0.22s ease',
                    borderRadius: '12px',
                    marginBottom: '8px',
                    border: selectedConversation?.id === conv.id ? '1px solid #fed7aa' : getSlaUrgencyStyle(conv).border,
                    borderLeft: `4px solid ${selectedConversation?.id === conv.id ? '#fb923c' : getSlaUrgencyStyle(conv).stripe}`
                  }}
                  onMouseEnter={(e) => {
                    if (selectedConversation?.id !== conv.id) {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 6px 14px rgba(17, 24, 39, 0.06)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedConversation?.id !== conv.id) {
                      e.currentTarget.style.backgroundColor = '#fff';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={selectedConversationIds.includes(conv.id)}
                        onChange={() => toggleSelectConversation(conv.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#25D366',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '18px',
                        fontWeight: '600'
                      }}>
                        <FaUser />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '600', fontSize: rowTitleSize, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {conversationPrimaryTitle(conv)}
                            </div>
                            {conversationPhoneSubtitle(conv) && (
                              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {conversationPhoneSubtitle(conv)}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: '11px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                togglePinConversation(conv.id);
                              }}
                              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: pinnedConversationIds.includes(conv.id) ? '#f59e0b' : '#9ca3af', padding: 0, display: 'inline-flex', alignItems: 'center' }}
                              title={pinnedConversationIds.includes(conv.id) ? (language === 'ar' ? 'إلغاء التثبيت' : 'Unpin') : (language === 'ar' ? 'تثبيت للأعلى' : 'Pin to top')}
                            >
                              <FaThumbtack style={{ fontSize: '11px' }} />
                            </button>
                            <FaClock style={{ fontSize: '10px' }} />
                            {formatTime(conv.last_message_at)}
                          </div>
                        </div>

                        {/* Simplified status flags (wrapped, non-overlapping) */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '4px' }}>
                          {/* Typing indicator in conversation list — hidden for the open conversation since the chat indicator covers it */}
                          {typingAgents[String(conv.id)] && typingAgents[String(conv.id)].length > 0 && String(conv.id) !== String(selectedConversation?.id) && (
                            <div style={{
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '999px',
                              backgroundColor: '#dcfce7',
                              color: '#166534',
                              fontWeight: '600',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <div style={{
                                width: '4px',
                                height: '4px',
                                borderRadius: '50%',
                                background: '#16a34a',
                                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                              }} />
                              {typingAgents[String(conv.id)].length === 1 
                                ? `${typingAgents[String(conv.id)][0].userName} ${language === 'ar' ? 'يكتب' : 'typing'}`
                                : `${typingAgents[String(conv.id)].length} ${language === 'ar' ? 'يكتبون' : 'typing'}`
                              }
                            </div>
                          )}
                          {/* agent_replying badge — flashes for 5s after another agent sends */}
                          {replyingAgents[String(conv.id)] && (
                            <div style={{
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '999px',
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                              fontWeight: '600',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              ✓ {replyingAgents[String(conv.id)].agentName} {language === 'ar' ? 'ردّ الآن' : 'just replied'}
                            </div>
                          )}
                          {Number(conv.unread_count || 0) > 0 && (
                            <div style={{
                              fontSize: '10px',
                              minWidth: '20px',
                              height: '18px',
                              padding: '0 6px',
                              borderRadius: '999px',
                              backgroundColor: '#dc2626',
                              color: '#fff',
                              fontWeight: '700',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {Number(conv.unread_count) > 99 ? '99+' : Number(conv.unread_count)}
                            </div>
                          )}
                          {!uiSettings.hideMetadata && (
                            <div style={{
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '999px',
                              backgroundColor: getLeadBadgeStyle(conv.client_readiness || conv.lead_temperature).bg,
                              color: getLeadBadgeStyle(conv.client_readiness || conv.lead_temperature).fg,
                              fontWeight: '700'
                            }}>
                              {formatLeadTemperature(conv.client_readiness || conv.lead_temperature)} • P{Math.round(Number(conv.priority_score || 0))}
                            </div>
                          )}
                          {bizFriendly && (
                            <div style={{
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '999px',
                              backgroundColor: '#f3f4f6',
                              color: '#374151',
                              fontWeight: '600'
                            }}>
                              {language === 'ar' ? 'رقم النشاط' : 'Business line'}: {bizFriendly}
                            </div>
                          )}
                          {/* Conf % — hidden in list view (shown in workspace panel) */}
                          {!uiSettings.hideMetadata && viewPreset !== 'list' && (
                            <div style={{
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '999px',
                              backgroundColor: '#eff6ff',
                              color: '#1d4ed8',
                              fontWeight: '700'
                            }}>
                              {language === 'ar' ? 'ثقة' : 'Conf'} {getConfidenceScore(conv)}%
                            </div>
                          )}
                          {!uiSettings.hideMetadata && getWaitingEmployeeBadgeText(conv) && (
                            <div style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '999px', backgroundColor: '#fef2f2', color: '#991b1b', fontWeight: '700', unicodeBidi: 'plaintext' }}>
                              {getWaitingEmployeeBadgeText(conv)}
                            </div>
                          )}
                        </div>

                        {!uiSettings.hideMetadata && staffLine && (
                          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {staffLine.kind === 'assignee'
                              ? (language === 'ar' ? 'مسند إلى' : 'Assignee')
                              : (language === 'ar' ? 'آخر رد' : 'Last reply')}
                            : {staffLine.label}
                          </div>
                        )}

                        {/* Tags — hidden in list view (shown in workspace panel) */}
                        {!uiSettings.hideMetadata && viewPreset !== 'list' && Array.isArray(conv.tags) && conv.tags.length > 0 && (
                          <div style={{ fontSize: '11px', color: '#4b5563', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {conv.tags.slice(0, 2).map((tg) => localizeConversationTag(tg)).join(' ')}
                            {conv.tags.length > 2 ? ` +${conv.tags.length - 2}` : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{
                    fontSize: ui.fontMd,
                    color: '#4b5563',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginTop: '4px'
                  }}>
                    {getConversationPreview(conv)}
                  </div>
                </div>
              );
              })
            )}

            {/* REMOVED: Load-more pagination UI - all conversations loaded initially */}
          </div>
        )}
      </div>
      )}

      {/* Right Panel - Message Thread */}
      {showConversationThread && (
      <div className={`wa-inbox-right${isNarrowLayout ? ' wa-inbox-right--narrow' : ''}`}>
        {selectedConversation ? (
          <>
            {/* Header — compact single row: identity + action ribbon + panel toggle */}
            <div style={{
              flexShrink: 0,
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#ffffffdd',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              minHeight: '52px',
              flexWrap: 'wrap',
              rowGap: '6px',
            }}>
              {/* Back button (narrow/mobile) */}
              {isNarrowLayout && (
                <button
                  onClick={() => setSelectedConversation(null)}
                  style={{ width: '30px', height: '30px', borderRadius: '7px', border: '1px solid #e5e7eb', backgroundColor: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151', flexShrink: 0 }}
                  title={language === 'ar' ? 'رجوع' : 'Back'}
                >
                  <FaArrowLeft />
                </button>
              )}
              {/* Avatar */}
              <div style={{ width: '34px', height: '34px', borderRadius: '50%', backgroundColor: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '15px', flexShrink: 0 }}>
                <FaUser />
                </div>
              {/* Identity */}
              <div style={{ minWidth: 0, flex: '1 1 140px' }}>
                <div style={{ fontWeight: '600', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {conversationPrimaryTitle(selectedConversation)}
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {[
                    conversationPhoneSubtitle(selectedConversation),
                    (() => { const biz = conversationBusinessLineFriendly(selectedConversation); return biz ? `${language === 'ar' ? 'الخط' : 'Line'}: ${biz}` : null; })(),
                    (() => { const s = conversationStaffLine(selectedConversation); return s ? `${s.kind === 'assignee' ? (language === 'ar' ? 'المكلف' : 'Assignee') : (language === 'ar' ? 'آخر رد' : 'Last reply')}: ${s.label}` : (language === 'ar' ? 'غير معين' : 'Unassigned'); })()
                  ].filter(Boolean).join('  ·  ')}
                </div>
              </div>
              {/* Compact action buttons + panel toggle */}
              <div style={{ display: 'flex', gap: '4px', flexShrink: 0, flexWrap: 'wrap' }}>
                {/* Link Client */}
                <button
                  onClick={handleAutoLinkClient}
                  style={{ ...getInteractiveButtonStyle({ subtle: true }), height: '28px', padding: '0 8px', fontSize: '11px', whiteSpace: 'nowrap' }}
                  onMouseEnter={handleControlMouseEnter}
                  onMouseLeave={handleControlMouseLeave}
                  title={language === 'ar' ? 'ربط عميل' : 'Link Client'}
                >
                  {language === 'ar' ? 'ربط عميل' : 'Link'}
                </button>
                {canInboxTriage && (
                <button
                  onClick={handleAutoAssign}
                    style={{ ...getInteractiveButtonStyle({ subtle: true }), height: '28px', padding: '0 8px', fontSize: '11px', whiteSpace: 'nowrap' }}
                  onMouseEnter={handleControlMouseEnter}
                  onMouseLeave={handleControlMouseLeave}
                    title={language === 'ar' ? 'تعيين تلقائي — النظام يختار موظفاً' : 'Auto Assign — system picks agent'}
                >
                    {language === 'ar' ? '⚡ تلقائي' : 'Auto'}
                </button>
                )}
                {canInboxTriage && (
                <button
                    onClick={openReassignModal}
                    style={{ ...getInteractiveButtonStyle({ subtle: true }), height: '28px', padding: '0 8px', fontSize: '11px', whiteSpace: 'nowrap' }}
                  onMouseEnter={handleControlMouseEnter}
                  onMouseLeave={handleControlMouseLeave}
                    title={language === 'ar' ? 'إعادة تعيين — اختر موظفاً' : 'Reassign — pick an agent'}
                >
                    {language === 'ar' ? '↩ تعيين' : 'Reassign'}
                </button>
                )}
                {INBOX_SHOW_TAGS_UI && (
                <button
                  onClick={handleUpdateTags}
                    style={{ ...getInteractiveButtonStyle({ subtle: true }), height: '28px', padding: '0 8px', fontSize: '11px' }}
                  onMouseEnter={handleControlMouseEnter}
                  onMouseLeave={handleControlMouseLeave}
                >
                    {language === 'ar' ? 'تصنيف' : 'Tags'}
                </button>
                )}
                {INBOX_SHOW_CAMPAIGNS_BUTTON && (
                <button
                  onClick={async () => {
                      const currentlyExcluded = isConversationBroadcastOptedOut(selectedConversation);
                      const optOut = !currentlyExcluded;
                    try {
                      await setConversationBroadcastOptOut(selectedConversation.id, optOut);
                      setConversations((prev) => prev.map((c) => c.id === selectedConversation.id ? { ...c, broadcast_opt_out: optOut } : c));
                      setSelectedConversation((prev) => prev ? { ...prev, broadcast_opt_out: optOut } : null);
                    } catch (e) { toast.error(e?.response?.data?.error || (language === 'ar' ? 'حدث خطأ' : 'An error occurred')); }
                  }}
                  style={{
                      ...getInteractiveButtonStyle({ subtle: true, active: isConversationBroadcastOptedOut(selectedConversation) }),
                      height: '28px', padding: '0 8px', fontSize: '11px',
                      borderColor: isConversationBroadcastOptedOut(selectedConversation) ? '#dc2626' : undefined,
                      color: isConversationBroadcastOptedOut(selectedConversation) ? '#dc2626' : undefined
                  }}
                  onMouseEnter={handleControlMouseEnter}
                  onMouseLeave={handleControlMouseLeave}
                    title={isConversationBroadcastOptedOut(selectedConversation)
                      ? (language === 'ar' ? 'مستثنى من الحملات' : 'Excluded from campaigns')
                      : (language === 'ar' ? 'مشمول بالحملات' : 'Included in campaigns')}
                  >
                    {isConversationBroadcastOptedOut(selectedConversation)
                      ? (language === 'ar' ? '🚫' : '🚫')
                      : (language === 'ar' ? '📢' : '📢')}
                  </button>
                )}
                {/* Panel toggle icon button */}
                <button
                  onClick={() => setShowWorkspacePanel((prev) => !prev)}
                  title={showWorkspacePanel ? (language === 'ar' ? 'إخفاء اللوحة' : 'Hide panel') : (language === 'ar' ? 'إظهار اللوحة' : 'Show panel')}
                  style={{
                    width: '28px', height: '28px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `1px solid ${showWorkspacePanel ? 'var(--wa-primary)' : '#e5e7eb'}`,
                    borderRadius: '7px',
                    background: showWorkspacePanel ? 'rgba(37,211,102,0.08)' : '#fff',
                    color: showWorkspacePanel ? 'var(--wa-primary)' : '#9ca3af',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    flexShrink: 0,
                  }}
                >
                  <FaUser style={{ fontSize: '12px' }} />
                </button>
              </div>
            </div>

            {/* Campaign reply attribution banner */}
            {selectedConversation.attributed_campaign_id && (
              <div style={{
                padding: '6px 16px',
                background: '#f0f9ff',
                borderBottom: '1px solid #bae6fd',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                color: '#0369a1',
              }}>
                <span style={{ fontSize: '14px' }}>📣</span>
                <span>
                  {language === 'ar'
                    ? `ردّ على حملة${selectedConversation.attributed_campaign_name ? `: ${selectedConversation.attributed_campaign_name}` : ''}`
                    : `Reply to campaign${selectedConversation.attributed_campaign_name ? `: ${selectedConversation.attributed_campaign_name}` : ''}`}
                </span>
              </div>
            )}

            {/* Split View Workspace */}
            <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
              {/* Messages */}
              <div ref={messagesContainerRef} style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                paddingTop: '16px',
                paddingLeft: '20px',
                paddingRight: '20px',
                /* Clear composer + template strip; WhatsApp-style breathing room above input */
                paddingBottom: 'max(72px, calc(20px + env(safe-area-inset-bottom, 0px)))',
                background: '#f0f2f5',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {hasOlderMessages && (
                  <button
                    type="button"
                    onClick={loadOlderMessages}
                    disabled={loadingOlderMessages}
                    style={{
                      alignSelf: 'center',
                      padding: '8px 14px',
                      marginBottom: '4px',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#475569',
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      cursor: loadingOlderMessages ? 'wait' : 'pointer',
                    }}
                  >
                    {loadingOlderMessages
                      ? (language === 'ar' ? 'جاري التحميل…' : 'Loading…')
                      : (language === 'ar' ? 'تحميل رسائل أقدم' : 'Load earlier messages')}
                  </button>
                )}
                {inboxMessagesForList.map((msg, index) => {
                const isClient = msg.sender === 'client';
                const rxEmoji = msg.whatsapp_message_id
                  ? reactionEmojiByTargetWamid.get(String(msg.whatsapp_message_id))
                  : null;
                const showDate = index === 0 || 
                  new Date(msg.timestamp).toDateString() !== new Date(inboxMessagesForList[index - 1].timestamp).toDateString();

                return (
                  <React.Fragment key={msg.id}>
                    {showDate && (
                      <div style={{
                        textAlign: 'center',
                        margin: '16px 0',
                        fontSize: '12px',
                        color: '#6b7280'
                      }}>
                        {formatDate(msg.timestamp)}
                      </div>
                    )}
                    <div style={{
                      display: 'flex',
                      justifyContent: isClient ? 'flex-start' : 'flex-end',
                      marginBottom: rxEmoji ? '18px' : '4px',
                      overflow: 'visible',
                    }}>
                      {(() => {
                        const winOpen = Boolean(selectedConversation && get24hWindowStatus(selectedConversation).open);
                        const canQuoteReply = Boolean(winOpen && msg.whatsapp_message_id);
                        const wrapClass = isClient && canQuoteReply ? 'wa-client-bubble-wrap' : (!isClient && canQuoteReply ? 'wa-agent-bubble-wrap' : undefined);
                        return (
                      <div
                        className={wrapClass}
                        style={{ position: 'relative', maxWidth: msg.message_type === 'template' ? 'min(72%, 28rem)' : 'min(78%, 44rem)', minWidth: 0, overflow: 'visible' }}
                        onDoubleClick={(e) => {
                          if (!canQuoteReply) return;
                          const tag = (e.target?.tagName || '').toLowerCase();
                          if (tag === 'a' || tag === 'button' || tag === 'img' || tag === 'video' || tag === 'audio') return;
                          e.preventDefault();
                          handleStartQuotedReply(msg);
                        }}
                        title={
                          canQuoteReply
                            ? (language === 'ar'
                              ? 'مرّر للتفاعل/الرد — أو انقر نقراً مزدوجاً للرد'
                              : 'Hover for reactions & reply — or double-click to reply')
                            : undefined
                        }
                      >
                        {isClient && canQuoteReply && (
                          <div
                            className="wa-client-bubble-actions"
                            role="toolbar"
                            aria-label={language === 'ar' ? 'تفاعل ورد' : 'React and reply'}
                            style={{
                              position: 'absolute',
                              /* Above the bubble only — top:-8 overlapped first lines (links, maps, media) on all ages of messages */
                              bottom: '100%',
                              marginBottom: 6,
                              zIndex: 12,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0,
                              ...(language === 'ar' ? { left: 0 } : { right: 0 }),
                            }}
                          >
                            {WHATSAPP_STYLE_REACTION_EMOJIS.map((em) => (
                              <button
                                key={em}
                                type="button"
                                disabled={sendingMessage}
                                title={language === 'ar' ? 'إرسال تفاعل' : 'Send reaction'}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sendEmojiReaction(msg, em);
                                }}
                                className="wa-reaction-pill-btn"
                              >
                                <span aria-hidden>{em}</span>
                              </button>
                            ))}
                            <span
                              aria-hidden
                              style={{
                                width: 1,
                                height: 20,
                                background: 'rgba(0,0,0,0.08)',
                                margin: '0 2px',
                                flexShrink: 0,
                              }}
                            />
                            <button
                              type="button"
                              disabled={sendingMessage}
                              title={language === 'ar' ? 'رد على الرسالة' : 'Reply to message'}
                              aria-label={language === 'ar' ? 'رد' : 'Reply'}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartQuotedReply(msg);
                              }}
                              className="wa-reply-pill-btn"
                            >
                              <FaReply style={{ fontSize: 15, display: 'block' }} />
                            </button>
                          </div>
                        )}
                        {!isClient && canQuoteReply && (
                          <div
                            className="wa-agent-bubble-actions"
                            role="toolbar"
                            aria-label={language === 'ar' ? 'تفاعل ورد على رسالة زميل' : 'React and reply to teammate'}
                            style={{
                              position: 'absolute',
                              bottom: '100%',
                              marginBottom: 6,
                              zIndex: 12,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0,
                              /* Anchor to outer edge of bubble (screen side) so strip grows toward chat center — avoids clipping in RTL where agent sits on the left */
                              insetInlineEnd: 0,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {WHATSAPP_STYLE_REACTION_EMOJIS.map((em) => (
                              <button
                                key={em}
                                type="button"
                                disabled={sendingMessage}
                                title={language === 'ar' ? 'إرسال تفاعل' : 'Send reaction'}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sendEmojiReaction(msg, em);
                                }}
                                className="wa-reaction-pill-btn"
                              >
                                <span aria-hidden>{em}</span>
                              </button>
                            ))}
                            <span
                              aria-hidden
                              style={{
                                width: 1,
                                height: 20,
                                background: 'rgba(0,0,0,0.08)',
                                margin: '0 2px',
                                flexShrink: 0,
                              }}
                            />
                            <button
                              type="button"
                              disabled={sendingMessage}
                              title={language === 'ar' ? 'رد على الرسالة' : 'Reply to message'}
                              aria-label={language === 'ar' ? 'رد' : 'Reply'}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartQuotedReply(msg);
                              }}
                              className="wa-reply-pill-btn"
                            >
                              <FaReply style={{ fontSize: 15, display: 'block' }} />
                            </button>
                          </div>
                        )}
                      <div style={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'visible',
                        padding: '12px 16px',
                        paddingBottom: rxEmoji ? '14px' : '12px',
                        borderRadius: isClient ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
                        background: isClient ? '#fff' : (msg.status === 'failed'
                          ? '#fef2f2'
                          : 'var(--wa-agent-bubble-fill, #f5f6f8)'),
                        border: isClient ? '1px solid #e5e7eb' : (msg.status === 'failed'
                          ? '1px solid rgba(244, 63, 94, 0.22)'
                          : '1px solid var(--wa-agent-bubble-border, rgba(37, 211, 102, 0.12))'),
                        boxShadow: isClient ? '0 2px 8px rgba(15, 23, 42, 0.06)' : (msg.status === 'failed'
                          ? '0 1px 2px rgba(15, 23, 42, 0.04)'
                          : 'var(--wa-agent-bubble-shadow, 0 0 0 1px rgba(37, 211, 102, 0.08), 0 2px 8px rgba(37, 211, 102, 0.07))'),
                        wordWrap: 'break-word'
                      }}>
                        {/* Sender name — top-left inside the bubble, WhatsApp group-chat style */}
                        {!isClient && (msg.sent_by_name || msg.sentByName) && (
                          <div style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#f97316',
                            marginBottom: '4px',
                            letterSpacing: '0.01em',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <span>{msg.sent_by_name || msg.sentByName}</span>
                            {(() => {
                              const meta = typeof msg.message_metadata === 'string' 
                                ? (() => { try { return JSON.parse(msg.message_metadata); } catch(_) { return {}; } })() 
                                : (msg.message_metadata || {});
                              
                              if (meta.retry_info?.retried) {
                                const retryInfo = meta.retry_info;
                                const retryDate = retryInfo.retried_at ? new Date(retryInfo.retried_at).toLocaleString() : '';
                                const tooltipText = language === 'ar' 
                                  ? `أعيد الإرسال بواسطة ${retryInfo.retried_by} في ${retryDate}`
                                  : `Retried by ${retryInfo.retried_by} at ${retryDate}`;
                                
                                return (
                                  <span 
                                    title={tooltipText}
                                    style={{
                                      cursor: 'help',
                                      color: '#f59e0b',
                                      fontSize: '10px'
                                    }}
                                  >
                                    🔄
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}
                        {!isClient && msg.message_metadata && (() => {
                          const qmeta = typeof msg.message_metadata === 'string' ? (() => { try { return JSON.parse(msg.message_metadata); } catch (_) { return {}; } })() : (msg.message_metadata || {});
                          const rid = qmeta.reply_to_whatsapp_id;
                          if (!rid) return null;

                          // Look up in local messages first; fall back to lazy-fetched cache
                          const ridStr = String(rid);
                          let parent = messages.find((m) => String(m.whatsapp_message_id || '') === ridStr)
                            || quotedMsgCacheRef.current[ridStr];

                          // If not found yet, fire a background fetch (once per wamid per session)
                          if (!parent && !quotedMsgCacheRef.current[`__fetching_${ridStr}`]) {
                            quotedMsgCacheRef.current[`__fetching_${ridStr}`] = true;
                            getMessageByWamid(ridStr)
                              .then((res) => {
                                if (res?.data) {
                                  quotedMsgCacheRef.current[ridStr] = res.data;
                                  // Bump version counter to trigger re-render
                                  setQuotedMsgCacheVersion((v) => v + 1);
                                }
                              })
                              .catch(() => {
                                // Mark as not-found so we don't retry on every render
                                quotedMsgCacheRef.current[ridStr] = { _notFound: true };
                              });
                          }

                          // While fetching, show a subtle loading indicator
                          if (!parent) {
                            return (
                              <div style={{
                                marginBottom: '10px', padding: '8px 10px', borderRadius: '8px',
                                borderLeft: language === 'ar' ? 'none' : '3px solid #d1d5db',
                                borderRight: language === 'ar' ? '3px solid #d1d5db' : 'none',
                                background: 'rgba(255,255,255,0.5)',
                              }}>
                                <div style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>
                                  {parent?._notFound
                                    ? (language === 'ar' ? '↩ رسالة محذوفة' : '↩ Deleted message')
                                    : (language === 'ar' ? '↩ جاري التحميل…' : '↩ Loading…')}
                                </div>
                              </div>
                            );
                          }
                          if (parent._notFound) return null;

                          let prev = '';
                          if (parent.message_type === 'image') prev = language === 'ar' ? '[صورة]' : '[Image]';
                          else if (parent.message_type === 'video') prev = language === 'ar' ? '[فيديو]' : '[Video]';
                          else if (parent.message_type === 'audio') prev = language === 'ar' ? '[صوت]' : '[Audio]';
                          else if (parent.message_type === 'location') prev = language === 'ar' ? '[موقع]' : '[Location]';
                          else if (parent.message_type === 'contact') prev = language === 'ar' ? '[جهة اتصال]' : '[Contact]';
                          else if (parent.message_type === 'document') prev = language === 'ar' ? '[مستند]' : '[Document]';
                          else if (parent.message_type === 'interactive') prev = language === 'ar' ? '[تفاعلي]' : '[Interactive]';
                          else prev = truncateReplyPreview(parent.message);

                          const qdir = getTextDirection(prev);
                          const parentIsAgent = String(parent.sender || '') === 'agent';
                          const quoteTitle = parentIsAgent
                            ? (language === 'ar' ? '↩ رد على زميل' : '↩ Reply to teammate')
                            : (language === 'ar' ? '↩ رد على العميل' : '↩ Reply to client');
                          return (
                            <div style={{
                              marginBottom: '10px',
                              padding: '8px 10px',
                              borderRadius: '8px',
                              borderLeft: language === 'ar' ? 'none' : '3px solid #22c55e',
                              borderRight: language === 'ar' ? '3px solid #22c55e' : 'none',
                              background: 'rgba(255,255,255,0.7)',
                              direction: qdir,
                              textAlign: qdir === 'rtl' ? 'right' : 'left'
                            }}>
                              <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', marginBottom: '4px' }}>
                                {quoteTitle}
                              </div>
                              <div style={{ fontSize: '12px', color: '#334155', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{prev}</div>
                            </div>
                          );
                        })()}
                        {/* Image Message */}
                        {msg.message_type === 'image' && msg.message_metadata && (() => {
                          const meta = typeof msg.message_metadata === 'string' ? (() => { try { return JSON.parse(msg.message_metadata); } catch(_) { return {}; } })() : (msg.message_metadata || {});
                          // Prefer a stored imageUrl; otherwise proxy via our server using Meta's media ID
                          const resolvedConvId = msg.conversation_id || selectedConversation?.id || '';
                          // Always prefer direct CDN URLs (Bunny CDN) - best performance
                          const primaryUrl = meta.mediaUrl || meta.imageUrl;
                          const src = primaryUrl && !String(primaryUrl).includes('[Image]')
                            ? primaryUrl  // Direct CDN URL - no proxy needed
                            : meta.imageId
                              ? `/api/whatsapp/inbox-media/${meta.imageId}?conversation_id=${resolvedConvId}`
                              : null;
                          
                          if (!src) return null;
                          return (
                          <div style={{ marginBottom: '8px' }}>
                              <div
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setImageModal({ src, alt: language === 'ar' ? 'صورة مشتركة' : 'Shared image' });
                                }}
                                style={{ cursor: 'pointer', display: 'inline-block' }}
                                title={language === 'ar' ? 'انقر لعرض الصورة' : 'Click to view image'}
                              >
                                <img
                                  src={src}
                                  alt={language === 'ar' ? 'صورة مشتركة' : 'Shared image'}
                                  style={{
                                    maxWidth: '320px',
                                    maxHeight: '240px',
                                    width: 'auto',
                                    height: 'auto',
                                    borderRadius: '8px',
                                    display: 'block',
                                    objectFit: 'cover',
                                    cursor: 'zoom-in',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                                  }}
                                  onError={(e) => {
                                    console.log('[Image Load Error]', {
                                      src: e.currentTarget.src,
                                      messageId: msg.id,
                                      error: 'Failed to load image'
                                    });
                                    e.currentTarget.style.display = 'none';
                                    const fallback = e.currentTarget.parentElement?.querySelector('.img-fallback');
                                    if (fallback) fallback.style.display = 'block';
                                    const s = e.currentTarget.src || '';
                                    setInboxProxyMediaHints((prev) => ({ ...prev, [msg.id]: { pending: true, status: null } }));
                                    (async () => {
                                      const st = await probeInboxProxyMediaStatus(s);
                                      setInboxProxyMediaHints((prev) => ({ ...prev, [msg.id]: { pending: false, status: st } }));
                                    })();
                                  }}
                                  onLoad={(e) => {
                                    console.log('[Image Load Success]', {
                                      src: e.currentTarget.src,
                                      messageId: msg.id
                                    });
                                  }}
                                />
                                <a className="img-fallback" href={src} target="_blank" rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  style={{ display: 'none', fontSize: '12px', color: '#2563eb' }}>
                                  {language === 'ar' ? '📎 فتح الصورة' : '📎 Open image'}
                                </a>
                                {inboxProxyMediaHints[msg.id] && (
                                  <div
                                    style={{
                                      marginTop: '8px',
                                      fontSize: '12px',
                                      lineHeight: 1.45,
                                      color: '#92400e',
                                      maxWidth: '320px',
                                      padding: '8px 10px',
                                      background: '#fffbeb',
                                      borderRadius: '8px',
                                      border: '1px solid #fde68a'
                                    }}
                                  >
                                    {inboxProxyMediaHints[msg.id].pending
                                      ? (language === 'ar' ? 'جارٍ التحقق من سبب الخطأ…' : 'Checking why this failed…')
                                      : inboxProxyMediaHintText(inboxProxyMediaHints[msg.id].status, language)}
                                  </div>
                                )}
                          </div>
                            </div>
                          );
                        })()}
                        
                        {/* Document — rich card; PDF can preview in-app (no new tab required) */}
                        {msg.message_type === 'document' && (() => {
                          const meta = typeof msg.message_metadata === 'string' ? (() => { try { return JSON.parse(msg.message_metadata); } catch(_) { return {}; } })() : (msg.message_metadata || {});
                          const docUrl = meta.documentUrl || meta.url
                            || (meta.documentId ? `/api/whatsapp/inbox-media/${meta.documentId}?conversation_id=${encodeURIComponent(msg.conversation_id || selectedConversation?.id || '')}` : '')
                            || '';
                          const filename = meta.filename || (language === 'ar' ? 'مستند' : 'Document');
                          const mime = meta.mimeType || '';
                          const pres = getDocumentPresentation(mime, filename);
                          const FileIcon = pres.isPdf ? FaFilePdf : FaFile;
                          return (
                          <div style={{
                              marginBottom: '8px',
                              padding: '12px',
                              background: '#f8fafc',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              borderLeft: `4px solid ${pres.accent}`
                            }}>
                              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                <div style={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: '10px',
                                  background: `${pres.accent}14`,
                            display: 'flex',
                            alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}>
                                  <FileIcon style={{ fontSize: '22px', color: pres.accent }} />
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', lineHeight: 1.35, wordBreak: 'break-word' }}>
                                    {filename}
                              </div>
                                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                                    {pres.label}{mime ? ` · ${mime.split('/').pop()}` : ''}
                            </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                                    {docUrl && pres.isPdf && (
                                      <button
                                        type="button"
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDocumentPreviewModal({ url: docUrl, filename });
                                        }}
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                          padding: '7px 12px',
                                          fontSize: '12px',
                                          fontWeight: 700,
                                          border: 'none',
                                          borderRadius: '8px',
                                          cursor: 'pointer',
                                          background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                          color: '#fff',
                                          boxShadow: '0 2px 8px rgba(34, 197, 94, 0.25)'
                                        }}
                                      >
                                        {language === 'ar' ? 'معاينة' : 'Preview'}
                                      </button>
                                    )}
                                    {docUrl && (
                                      <a
                                        href={docUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        download={filename}
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                          padding: '7px 12px',
                                          fontSize: '12px',
                                          fontWeight: 600,
                                          borderRadius: '8px',
                                          border: '1px solid #cbd5e1',
                                          background: '#fff',
                                          color: '#334155',
                                          textDecoration: 'none'
                                        }}
                                      >
                                        <FaExternalLinkAlt style={{ fontSize: '11px', opacity: 0.85 }} />
                                        {language === 'ar' ? 'تبويب جديد' : 'New tab'}
                                      </a>
                                    )}
                                    {!docUrl && (
                                      <span style={{ fontSize: '11px', color: '#b45309' }}>
                                        {language === 'ar' ? 'تعذر تحميل الملف.' : 'File unavailable.'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              </div>
                          );
                        })()}

                        {/* Location Message — WhatsApp often sends only lat/lng (no name); always offer a maps link */}
                        {msg.message_type === 'location' && (() => {
                          let meta = {};
                          try {
                            meta = typeof msg.message_metadata === 'string'
                              ? JSON.parse(msg.message_metadata)
                              : (msg.message_metadata || {});
                          } catch (_) {
                            meta = {};
                          }
                          let lat = meta.latitude != null ? Number(meta.latitude) : NaN;
                          let lng = meta.longitude != null ? Number(meta.longitude) : NaN;
                          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                            const m = String(msg.message || '').match(/\[Location:\s*([-\d.]+)\s*,\s*([-\d.]+)\s*\]/i);
                            if (m) {
                              lat = Number(m[1]);
                              lng = Number(m[2]);
                            }
                          }
                          const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
                          const mapsUrl = hasCoords
                            ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`
                            : '';
                          const title = (meta.name && String(meta.name).trim()) || (language === 'ar' ? 'موقع' : 'Location');
                          const address = meta.address && String(meta.address).trim();
                          return (
                          <div style={{
                              padding: '10px 12px',
                            backgroundColor: 'rgba(0,0,0,0.05)',
                              borderRadius: '8px',
                              marginBottom: '8px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <FaMapMarkerAlt style={{ fontSize: '20px', color: '#ef4444', marginBottom: '6px' }} />
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{title}</div>
                              {address ? (
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', lineHeight: 1.4 }}>{address}</div>
                              ) : null}
                              {hasCoords ? (
                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px', fontFamily: 'ui-monospace, monospace' }}>
                                  {lat.toFixed(6)}, {lng.toFixed(6)}
                            </div>
                              ) : null}
                              {mapsUrl ? (
                                <a
                                  href={mapsUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    marginTop: '10px',
                                    padding: '8px 14px',
                                    background: '#25D366',
                                    color: '#fff',
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    textDecoration: 'none',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                                  }}
                                >
                                  {language === 'ar' ? 'فتح في خرائط جوجل' : 'Open in Google Maps'}
                                </a>
                              ) : (
                                <div style={{ fontSize: '11px', color: '#b45309', marginTop: '8px' }}>
                                  {language === 'ar' ? 'لا تتوفر إحداثيات لفتح الخريطة.' : 'Coordinates missing — map link unavailable.'}
                                  </div>
                                )}
                          </div>
                          );
                        })()}

                        {/* Reaction Message — client reacted to one of our messages */}
                        {msg.message_type === 'reaction' && (() => {
                          const meta = typeof msg.message_metadata === 'string' ? (() => { try { return JSON.parse(msg.message_metadata); } catch(_) { return {}; } })() : (msg.message_metadata || {});
                          const emoji = meta.emoji || msg.message || '👍';
                          const targetWamid = meta.reaction_target_wamid || meta.reactionTargetWamid;
                          const knownWamids = new Set(messages.map((m) => m.whatsapp_message_id).filter(Boolean));
                          const isOrphan = targetWamid && !knownWamids.has(targetWamid);
                          return (
                            <div style={{ marginBottom: '4px' }}>
                              <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 10px',
                                background: 'rgba(0,0,0,0.04)',
                                border: '1px solid rgba(0,0,0,0.08)',
                                borderRadius: '999px',
                                fontSize: '13px',
                                color: '#374151',
                              }}>
                                <span style={{ fontSize: '18px', lineHeight: 1 }}>{emoji}</span>
                                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                  {language === 'ar' ? 'تفاعل على رسالة' : 'Reacted to a message'}
                                </span>
                              </div>
                              {isOrphan && (
                                <div style={{ fontSize: '10px', color: '#b0b8c8', marginTop: '3px', paddingLeft: '2px' }}>
                                  {language === 'ar'
                                    ? 'الرسالة الأصلية من نظام سابق'
                                    : 'Original message is from a previous system'}
                          </div>
                        )}
                            </div>
                          );
                        })()}

                        {/* Contact Message */}
                        {msg.message_type === 'contact' && (
                          <div style={{
                            padding: '8px',
                            backgroundColor: 'rgba(0,0,0,0.05)',
                            borderRadius: '4px',
                            marginBottom: '8px'
                          }}>
                            <FaAddressCard style={{ fontSize: '20px', color: '#25D366', marginBottom: '4px' }} />
                            <div style={{ fontSize: '12px', fontWeight: '600' }}>
                              {typeof msg.message_metadata === 'string'
                                ? JSON.parse(msg.message_metadata).name || (language === 'ar' ? 'جهة اتصال' : 'Contact')
                                : msg.message_metadata?.name || (language === 'ar' ? 'جهة اتصال' : 'Contact')}
                            </div>
                            {typeof msg.message_metadata === 'string'
                              ? JSON.parse(msg.message_metadata).phone && (
                                  <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                                    {JSON.parse(msg.message_metadata).phone}
                                  </div>
                                )
                              : msg.message_metadata?.phone && (
                                  <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                                    {msg.message_metadata.phone}
                                  </div>
                                )}
                          </div>
                        )}

                        {/* Audio Message */}
                        {msg.message_type === 'audio' && (() => {
                          const meta = typeof msg.message_metadata === 'string' ? (() => { try { return JSON.parse(msg.message_metadata); } catch(_) { return {}; } })() : (msg.message_metadata || {});
                          const audioUrl = meta.audioUrl || meta.url
                            || (meta.audioId ? `/api/whatsapp/inbox-media/${meta.audioId}?conversation_id=${msg.conversation_id || selectedConversation?.id || ''}` : '')
                            || '';
                          return <AudioMessagePlayer audioUrl={audioUrl} language={language} messageId={msg.id} />;
                        })()}

                        {/* Video Message — inbound from customer or outbound agent upload */}
                        {msg.message_type === 'video' && (() => {
                          const meta = typeof msg.message_metadata === 'string' ? (() => { try { return JSON.parse(msg.message_metadata); } catch(_) { return {}; } })() : (msg.message_metadata || {});
                          const convId = encodeURIComponent(msg.conversation_id || selectedConversation?.id || '');
                          // Prefer stored CDN/signed URL; fall back to live Meta proxy using the raw videoId
                          const src = meta.mediaUrl || meta.videoUrl
                            || (meta.videoId ? `/api/whatsapp/inbox-media/${meta.videoId}?conversation_id=${convId}` : null);
                          if (!src) return (
                            <div style={{ fontSize: '12px', color: '#9ca3af', padding: '6px 0' }}>
                              {language === 'ar' ? '[فيديو — لا يمكن تحميله]' : '[Video — could not load]'}
                            </div>
                          );
                          return (
                            <div style={{ marginBottom: '8px' }}>
                              <video
                                src={src}
                                controls
                                style={{ maxWidth: '100%', maxHeight: '320px', borderRadius: '8px', display: 'block', background: '#000' }}
                                preload="metadata"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const fb = e.currentTarget.parentElement?.querySelector('.video-fallback');
                                  if (fb) fb.style.display = 'inline-flex';
                                  const s = e.currentTarget.src || '';
                                  const hintKey = `video:${msg.id}`;
                                  setInboxProxyMediaHints((prev) => ({ ...prev, [hintKey]: { pending: true, status: null } }));
                                  (async () => {
                                    const st = await probeInboxProxyMediaStatus(s);
                                    setInboxProxyMediaHints((prev) => ({ ...prev, [hintKey]: { pending: false, status: st } }));
                                  })();
                                }}
                              />
                                <a
                                className="video-fallback"
                                href={src}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  display: 'none',
                                  alignItems: 'center',
                                  gap: '6px',
                                  marginTop: '6px',
                                  padding: '7px 12px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  borderRadius: '8px',
                                  border: '1px solid #cbd5e1',
                                  background: '#fff',
                                  color: '#334155',
                                  textDecoration: 'none'
                                }}
                              >
                                <FaExternalLinkAlt style={{ fontSize: '11px' }} />
                                {language === 'ar' ? 'فتح الفيديو' : 'Open video'}
                              </a>
                              {inboxProxyMediaHints[`video:${msg.id}`] && (
                                <div
                                  style={{
                                    marginTop: '8px',
                                    fontSize: '12px',
                                    lineHeight: 1.45,
                                    color: '#92400e',
                                    maxWidth: '320px',
                                    padding: '8px 10px',
                                    background: '#fffbeb',
                                    borderRadius: '8px',
                                    border: '1px solid #fde68a'
                                  }}
                                >
                                  {inboxProxyMediaHints[`video:${msg.id}`].pending
                                    ? (language === 'ar' ? 'جارٍ التحقق من سبب الخطأ…' : 'Checking why this failed…')
                                    : inboxProxyMediaHintText(inboxProxyMediaHints[`video:${msg.id}`].status, language)}
                                </div>
                              )}
                              {meta.caption && (
                                <div style={{ fontSize: '13px', color: '#374151', marginTop: '6px', lineHeight: 1.4 }}>
                                  {meta.caption}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Interactive: outbound (body + reply buttons) or inbound (button/list selection) — matches WhatsApp layout */}
                        {msg.message_type === 'interactive' && (() => {
                          const meta = typeof msg.message_metadata === 'string'
                            ? (() => { try { return JSON.parse(msg.message_metadata); } catch (_) { return {}; } })()
                            : (msg.message_metadata || {});
                          const bodyText = meta.body?.text || msg.message || '';
                          const dir = getTextDirection(bodyText);
                          const actionButtons = meta.action?.buttons;
                          const actionSections = meta.action?.sections;
                          const listMenuBtn = meta.action?.button;
                          const isOutboundButtons = meta.type === 'button' && Array.isArray(actionButtons) && actionButtons.length > 0;
                          const isOutboundList = meta.type === 'list' && Array.isArray(actionSections) && actionSections.some((s) => Array.isArray(s?.rows) && s.rows.length > 0);
                          const isInboundReply = Boolean(meta.buttonTitle || meta.listTitle || meta.buttonId || meta.listId);

                          if (isOutboundList) {
                            return (
                              <div style={{ marginBottom: '4px' }}>
                                {bodyText ? (
                                  <div style={{
                                    fontSize: '14px',
                                    color: '#111827',
                                    lineHeight: 1.55,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    direction: dir,
                                    textAlign: dir === 'rtl' ? 'right' : 'left',
                                    marginBottom: '10px'
                                  }}>
                                    {renderFormattedText(bodyText)}
                                  </div>
                                ) : null}
                                <div style={{
                                  padding: '10px 14px',
                                  background: '#fff',
                                  border: '1px solid rgba(0,0,0,0.08)',
                                  borderRadius: '8px',
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  color: '#b45309',
                                  textAlign: 'center',
                                  boxShadow: '0 1px 0 rgba(0,0,0,0.04)',
                                  marginBottom: '8px'
                                }}>
                                  ▾ {listMenuBtn || (language === 'ar' ? 'القائمة' : 'List')}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {actionSections.map((sec, sidx) => (
                                    <div key={`ls-${sidx}`}>
                                      {sec.title ? (
                                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{sec.title}</div>
                                      ) : null}
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        {(sec.rows || []).map((row, ridx) => {
                                          const t = row.title || '';
                                          if (!t) return null;
                                          return (
                                            <div
                                              key={`lr-${sidx}-${ridx}`}
                                              style={{
                                                padding: '8px 12px',
                                                background: '#f8fafc',
                                                border: '1px solid rgba(0,0,0,0.06)',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                color: '#334155'
                                              }}
                                            >
                                              {t}
                                              {row.description ? (
                                                <div style={{ fontSize: '11px', fontWeight: 400, color: '#64748b', marginTop: '4px' }}>{row.description}</div>
                                              ) : null}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          }

                          if (isOutboundButtons) {
                            return (
                              <div style={{ marginBottom: '4px' }}>
                                {bodyText ? (
                                  <div style={{
                                    fontSize: '14px',
                                    color: '#111827',
                                    lineHeight: 1.55,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    direction: dir,
                                    textAlign: dir === 'rtl' ? 'right' : 'left',
                                    marginBottom: '10px'
                                  }}>
                                    {renderFormattedText(bodyText)}
                                  </div>
                                ) : null}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                  {actionButtons.map((btn, idx) => {
                                    const title = btn?.reply?.title || btn?.title || '';
                                    if (!title) return null;
                                    return (
                                      <div
                                        key={`ib-${idx}`}
                                        style={{
                                          padding: '10px 14px',
                                          background: '#fff',
                                          border: '1px solid rgba(0,0,0,0.08)',
                                          borderRadius: '8px',
                                          fontSize: '13px',
                                          fontWeight: 600,
                                          color: '#009de2',
                                          textAlign: 'center',
                                          boxShadow: '0 1px 0 rgba(0,0,0,0.04)'
                                        }}
                                      >
                                        {title}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          }

                          if (isInboundReply) {
                            const picked = meta.buttonTitle || meta.listTitle || msg.message;
                            const sub = meta.listDescription;
                            const threadPreview = meta.reply_thread_preview ? String(meta.reply_thread_preview).trim() : '';
                            const threadFromBiz = meta.reply_thread_from === 'business' || meta.reply_thread_from === 'agent';
                            const hasThread = Boolean(threadPreview);
                            const threadDir = getTextDirection(threadPreview);
                            return (
                              <div style={{ marginBottom: '4px' }}>
                                {hasThread && (
                                  <div style={{
                                    marginBottom: '10px',
                                    padding: '8px 10px',
                                    borderRadius: '8px',
                                    borderLeft: language === 'ar' ? 'none' : '3px solid #94a3b8',
                                    borderRight: language === 'ar' ? '3px solid #94a3b8' : 'none',
                                    background: '#f1f5f9',
                                    direction: threadDir,
                                    textAlign: threadDir === 'rtl' ? 'right' : 'left'
                                  }}>
                                    <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                      {language === 'ar'
                                        ? (threadFromBiz ? '↩ ردّ على رسالتك التفاعلية' : '↩ مرتبط برسالة سابقة')
                                        : (threadFromBiz ? '↩ Reply to your interactive message' : '↩ Linked to earlier message')}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#334155', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                      {threadPreview}
                                    </div>
                          </div>
                        )}
                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                  {language === 'ar' ? 'رد تفاعلي' : 'Interactive reply'}
                                </div>
                                <div style={{
                                  fontSize: '14px',
                                  color: '#111827',
                                  fontWeight: 600,
                                  direction: getTextDirection(String(picked)),
                                  textAlign: getTextDirection(String(picked)) === 'rtl' ? 'right' : 'left',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word'
                                }}>
                                  {picked}
                                </div>
                                {sub ? (
                                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', whiteSpace: 'pre-wrap' }}>{sub}</div>
                                ) : null}
                              </div>
                            );
                          }

                          return (
                            <div style={{ fontSize: '14px', color: '#111827', whiteSpace: 'pre-wrap', wordBreak: 'break-word', direction: dir, textAlign: dir === 'rtl' ? 'right' : 'left', fontFamily: whatsappFontFamily, unicodeBidi: 'isolate' }}>
                              {renderFormattedText(bodyText)}
                            </div>
                          );
                        })()}

                        {/* Template Message with Image Header */}
                        {msg.message_type === 'template' && msg.message_metadata && (() => {
                          const meta = typeof msg.message_metadata === 'string' ? (() => { try { return JSON.parse(msg.message_metadata); } catch(_) { return {}; } })() : (msg.message_metadata || {});
                          let headerFormat = meta.headerFormat;
                          let headerImageUrl = meta.headerImageUrl;
                          let headerText = meta.headerText;
                          let footerText = meta.footerText;
                          let buttons = meta.buttons;
                          
                          // For old messages without header info, try to fetch from cache or API
                          const templateName = meta.templateName || meta.name || msg.template_name;
                          if (templateName && (!headerFormat && !headerImageUrl && !headerText && !footerText && !buttons)) {
                            // Check cache first
                            if (templateHeaderCache[templateName]) {
                              const cached = templateHeaderCache[templateName];
                              headerFormat = cached.headerFormat;
                              headerImageUrl = cached.headerImageUrl;
                              headerText = cached.headerText;
                              footerText = cached.footerText;
                              buttons = cached.buttons;
                            } else {
                              // Fetch header info asynchronously
                              getTemplateHeader(templateName).then(res => {
                                if (res.data) {
                                  setTemplateHeaderCache(prev => ({
                                    ...prev,
                                    [templateName]: res.data
                                  }));
                                }
                              }).catch(err => {
                                console.warn(`Failed to fetch header for template ${templateName}:`, err);
                              });
                            }
                          }
                          
                          // Handle image/video/document header
                          if (headerImageUrl && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat)) {
                            const resolvedConvId = msg.conversation_id || selectedConversation?.id || '';
                            // Use CDN proxy for Meta URLs to avoid CORS
                            let src = headerImageUrl;
                            try {
                              const url = new URL(headerImageUrl);
                              if (/\.(whatsapp\.net|fbcdn\.net)$/i.test(url.host)) {
                                src = `/api/whatsapp/cdn-proxy?url=${encodeURIComponent(headerImageUrl)}`;
                              }
                            } catch (_) {}
                            
                            return (
                              <div style={{ marginBottom: '12px' }}>
                                {headerFormat === 'IMAGE' && (
                                  <div
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setImageModal({ src, alt: language === 'ar' ? 'صورة القالب' : 'Template image' });
                                    }}
                                    style={{ cursor: 'pointer', display: 'block', width: '100%', marginBottom: '10px' }}
                                    title={language === 'ar' ? 'انقر لعرض الصورة' : 'Click to view image'}
                                  >
                                    <img
                                      src={src}
                                      alt={language === 'ar' ? 'صورة القالب' : 'Template image'}
                                      style={{ 
                                        width: '100%', 
                                        height: '140px', 
                                        borderRadius: '8px', 
                                        display: 'block', 
                                        objectFit: 'cover'
                                      }}
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        const fallback = e.currentTarget.parentElement?.querySelector('.img-fallback');
                                        if (fallback) fallback.style.display = 'block';
                                      }}
                                    />
                                    <a className="img-fallback" href={src} target="_blank" rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      style={{ display: 'none', fontSize: '12px', color: '#2563eb' }}>
                                      {language === 'ar' ? '📎 فتح الصورة' : '📎 Open image'}
                                    </a>
                          </div>
                        )}
                                {headerFormat === 'VIDEO' && (
                                  <video
                                    src={src}
                                    controls
                                    onMouseDown={(e) => e.stopPropagation()}
                                    style={{ maxWidth: '100%', borderRadius: '8px', display: 'block' }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      const fallback = e.currentTarget.parentElement?.querySelector('.video-fallback');
                                      if (fallback) fallback.style.display = 'block';
                                    }}
                                  />
                                )}
                                {headerFormat === 'DOCUMENT' && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: '8px' }}>
                                    <FaFile style={{ fontSize: '20px', color: '#6b7280', flexShrink: 0 }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: '12px', fontWeight: '600' }}>{language === 'ar' ? 'مستند القالب' : 'Template document'}</div>
                                      <a
                                        href={src}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        style={{ fontSize: '10px', color: '#2563eb' }}
                                      >
                                        {language === 'ar' ? 'انقر لفتح' : 'Click to open'}
                                      </a>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          // Handle text header
                          if (headerText && headerFormat === 'TEXT') {
                            return (
                              <div style={{
                                width: '100%',
                                padding: '10px 12px',
                                color: '#0f172a',
                                fontWeight: 700,
                                fontSize: '13px',
                                textAlign: 'left',
                                marginBottom: '8px',
                                whiteSpace: 'pre-wrap'
                              }}>
                                {headerText}
                              </div>
                            );
                          }
                          
                          return null;
                        })()}

                        {/* Message Text — captions / real text only (no duplicate [Location:…] / [Document:…] placeholders) */}
                        {shouldShowSecondaryMessageText(msg) && (() => {
                          // Detect text direction for proper BiDi handling
                          const messageText = msg.message || '';
                          const textDirection = getTextDirection(messageText);
                          
                          return (
                          <div style={{ 
                            fontSize: '14px', 
                            color: '#111827', 
                            marginBottom: '4px', 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-word', 
                            position: 'relative', 
                            zIndex: 1, 
                            fontFamily: whatsappFontFamily,
                            direction: textDirection,
                            textAlign: textDirection === 'rtl' ? 'right' : 'left',
                            unicodeBidi: 'isolate'
                          }}>
                            {(() => {
                              const parts = splitTextWithUrls(msg.message);
                              return parts.map((part, i) => {
                                if (/^https?:\/\/[^\s]+$/i.test(part) || /^www\.[^\s]+$/i.test(part)) {
                                  const href = hrefFromUrlToken(part);
                                  return (
                                    <a
                                      key={i}
                                      href={href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: '#2563eb', textDecoration: 'underline', position: 'relative', zIndex: 2 }}
                                      onClick={(e) => e.stopPropagation()}
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      {part}
                                    </a>
                                  );
                                }
                                return <span key={i}>{renderFormattedText(part)}</span>;
                              });
                            })()}
                          </div>
                          );
                        })()}

                        {/* Template Footer and Buttons - rendered AFTER message text */}
                        {msg.message_type === 'template' && msg.message_metadata && (() => {
                          const meta = typeof msg.message_metadata === 'string' ? (() => { try { return JSON.parse(msg.message_metadata); } catch(_) { return {}; } })() : (msg.message_metadata || {});
                          let footerText = meta.footerText;
                          let buttons = meta.buttons;
                          
                          // For old messages, check cache
                          const templateName = meta.templateName || meta.name || msg.template_name;
                          
                          // Debug: Always log template metadata to see what we have
                          console.log('[Template Debug]', {
                            templateName,
                            hasMeta: !!meta,
                            metaKeys: Object.keys(meta),
                            buttons: buttons,
                            buttonsType: typeof buttons,
                            footerText: footerText,
                            hasCache: !!templateHeaderCache[templateName],
                            cacheData: templateHeaderCache[templateName]
                          });
                          
                          if (templateName && templateHeaderCache[templateName]) {
                            const cached = templateHeaderCache[templateName];
                            console.log('[Template Debug] Using cached data:', cached);
                            if (!footerText) footerText = cached.footerText;
                            if (!buttons) buttons = cached.buttons;
                          }
                          
                          // Debug: log buttons to console
                          if (buttons) {
                            console.log('[Template Buttons Debug]', {
                              templateName,
                              buttons,
                              type: buttons?.type,
                              buttonsArray: buttons?.buttons,
                              isArray: Array.isArray(buttons?.buttons),
                              length: buttons?.buttons?.length
                            });
                          } else {
                            console.log('[Template Buttons Debug] No buttons found for template:', templateName);
                          }
                          
                          return (
                            <>
                              {/* Footer - comes before buttons (WhatsApp standard) */}
                              {footerText && (() => {
                                // Detect text direction for RTL support
                                const textDir = getTextDirection(footerText);
                                return (
                                  <div style={{
                                    marginTop: '8px',
                                    fontSize: '11px',
                                    color: '#6b7280',
                                    textAlign: textDir === 'rtl' ? 'right' : 'left',
                                    direction: textDir,
                                    whiteSpace: 'pre-wrap'
                                  }}>
                                    {footerText}
                                  </div>
                                );
                              })()}
                              
                              {/* Buttons - CTA comes last (WhatsApp standard & UX best practice) */}
                              {buttons && buttons.type === 'url' && Array.isArray(buttons.buttons) && buttons.buttons.length > 0 && (
                                <div style={{ marginTop: '8px', display: 'grid', gap: '6px' }}>
                                  {buttons.buttons.map((btn, idx) => (
                                    <a
                                      key={`btn-${idx}`}
                                      href={btn.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      style={{
                                        padding: '10px 14px',
                                        background: '#25D366',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        color: '#fff',
                                        fontWeight: '500',
                                        textAlign: 'center',
                                        textDecoration: 'none',
                                        display: 'block',
                                        fontFamily: whatsappFontFamily
                                      }}
                                    >
                                      {btn.text}
                                    </a>
                                  ))}
                                </div>
                              )}
                              {buttons && buttons.type === 'phone' && Array.isArray(buttons.buttons) && buttons.buttons.length > 0 && (
                                <div style={{ marginTop: '8px', display: 'grid', gap: '6px' }}>
                                  {buttons.buttons.map((btn, idx) => (
                                    <a
                                      key={`btn-${idx}`}
                                      href={`tel:${btn.phoneNumber}`}
                                      onClick={(e) => e.stopPropagation()}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      style={{
                                        padding: '10px 14px',
                                        background: '#25D366',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        color: '#fff',
                                        fontWeight: '500',
                                        textAlign: 'center',
                                        textDecoration: 'none',
                                        display: 'block',
                                        fontFamily: whatsappFontFamily
                                      }}
                                    >
                                      {btn.text}
                                    </a>
                                  ))}
                                </div>
                              )}
                              {buttons && buttons.type === 'quick_reply' && Array.isArray(buttons.buttons) && buttons.buttons.length > 0 && (
                                <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  {buttons.buttons.map((text, idx) => (
                                    <span
                                      key={`qr-${idx}`}
                                      style={{
                                        padding: '8px 14px',
                                        background: '#e9ecef',
                                        borderRadius: '20px',
                                        fontSize: '13px',
                                        color: '#1c1e21',
                                        fontWeight: '500',
                                        fontFamily: whatsappFontFamily
                                      }}
                                    >
                                      {text}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </>
                          );
                        })()}
                        
                        {/* Footer: timestamp + tick / failed + retry */}
                        <div style={{ fontSize: '11px', color: '#6b7280', textAlign: isClient ? 'left' : 'right', display: 'flex', alignItems: 'center', gap: '6px', justifyContent: isClient ? 'flex-start' : 'flex-end', flexWrap: 'wrap', marginTop: '4px' }}>
                          {formatTime(msg.timestamp)}
                          {!isClient && msg.status && (
                            <>
                              {msg.status !== 'failed' ? (
                            <span style={{ 
                              fontSize: '10px',
                              opacity: 0.9,
                              color: msg.status === 'read' ? '#2563eb' : '#6b7280',
                              fontWeight: msg.status === 'read' ? '700' : '500'
                            }}>
                              {msg.status === 'sent' && '✓'}
                              {msg.status === 'delivered' && '✓✓'}
                              {msg.status === 'read' && '✓✓'}
                                </span>
                              ) : (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                  <span style={{ fontSize: '10px', color: '#9f1239', fontWeight: 500 }}>
                                    {language === 'ar' ? 'فشل الإرسال' : 'Failed to send'}
                                  </span>
                                  <button
                                    type="button"
                                    disabled={retryingFailedMessageId === msg.id}
                                    onClick={() => handleRetryFailedMessage(msg)}
                                    style={{
                                      fontSize: '10px',
                                      fontWeight: 600,
                                      color: '#1d4ed8',
                                      background: 'none',
                                      border: 'none',
                                      padding: 0,
                                      cursor: retryingFailedMessageId === msg.id ? 'wait' : 'pointer',
                                      textDecoration: 'underline',
                                      textUnderlineOffset: '2px',
                                      fontFamily: 'inherit',
                                      opacity: retryingFailedMessageId === msg.id ? 0.6 : 1
                                    }}
                                  >
                                    {retryingFailedMessageId === msg.id
                                      ? (language === 'ar' ? 'جاري المحاولة…' : 'Retrying…')
                                      : (language === 'ar' ? 'إعادة المحاولة' : 'Retry')}
                                  </button>
                            </span>
                              )}
                            </>
                          )}
                        </div>
                        {rxEmoji && (
                          <span
                            className={`wa-msg-reaction-chip ${isClient ? 'wa-msg-reaction-chip--incoming' : 'wa-msg-reaction-chip--outgoing'}`}
                            title={isClient
                              ? (language === 'ar' ? 'تفاعلك' : 'Your reaction')
                              : (language === 'ar' ? 'تفاعل العميل' : 'Client reaction')}
                            aria-label={isClient
                              ? (language === 'ar' ? 'تفاعلك' : 'Your reaction')
                              : (language === 'ar' ? 'تفاعل العميل' : 'Client reaction')}
                            style={{
                              position: 'absolute',
                              zIndex: 4,
                              bottom: -10,
                              ...(language === 'ar'
                                ? (isClient ? { left: 6, right: 'auto' } : { right: 6, left: 'auto' })
                                : (isClient ? { right: 6, left: 'auto' } : { left: 6, right: 'auto' })),
                            }}
                          >
                            {rxEmoji}
                            </span>
                          )}
                      </div>
                      </div>
                        );
                      })()}
                    </div>
                  </React.Fragment>
                );
                })}
                


                {/* agent_replying in-thread notice — flashes for 5s after another agent sends */}
                {selectedConversation && replyingAgents[String(selectedConversation.id)] && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 10px',
                    background: '#fefce8',
                    border: '1px solid #fde68a',
                    borderRadius: '10px',
                    fontSize: '13px',
                    color: '#92400e',
                    fontWeight: 500,
                    marginBottom: '4px',
                  }}>
                    <span>✓</span>
                    <span>
                      {replyingAgents[String(selectedConversation.id)].agentName} {language === 'ar' ? 'أرسل ردًّا للتو' : 'just sent a reply'}
                    </span>
                  </div>
                )}

                {/* In-chat typing indicator — auto-scroll makes it always visible */}
                {selectedConversation && typingAgents[String(selectedConversation.id)]?.length > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 4px',
                    marginTop: '4px',
                  }}>
                    <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
                      {[0, 1, 2].map(i => (
                        <div key={i} style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: '#6b7280',
                          animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                        }} />
                      ))}
                    </div>
                    <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>
                      {typingAgents[String(selectedConversation.id)].length === 1
                        ? `${typingAgents[String(selectedConversation.id)][0].userName} ${language === 'ar' ? 'يكتب...' : 'is typing...'}`
                        : `${typingAgents[String(selectedConversation.id)].length} ${language === 'ar' ? 'يكتبون...' : 'agents typing...'}`
                      }
                    </span>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {showWorkspacePanel && (
                <div className="wa-workspace-panel">
                  <div style={{ fontWeight: '700', fontSize: '13px', marginBottom: '10px', color: '#374151' }}>
                    {language === 'ar' ? 'لوحة المحادثة' : 'Conversation Workspace'}
                  </div>
                  
                  {/* WhatsApp Connection Status Indicator */}
                  {(() => {
                    // Check if we have recent token expiration in console or connection issues
                    const hasConnectionIssues = false; // This would be set by error state management
                    const hasTokenExpiration = false; // This would be set when we detect token errors
                    
                    if (hasTokenExpiration) {
                      return (
                <div style={{
                          marginBottom: '10px', 
                          padding: '6px 8px', 
                          background: '#fef2f2', 
                          border: '1px solid #fca5a5', 
                          borderRadius: '6px', 
                          fontSize: '11px', 
                          color: '#991b1b', 
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          🚨 {language === 'ar' ? 'انتهت صلاحية رمز الواتساب' : 'WhatsApp token expired'}
                  </div>
                      );
                    }
                    
                    if (hasConnectionIssues) {
                      return (
                        <div style={{ 
                          marginBottom: '10px', 
                          padding: '6px 8px', 
                          background: '#fef3c7', 
                          border: '1px solid #fbbf24', 
                          borderRadius: '6px', 
                          fontSize: '11px', 
                          color: '#92400e', 
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          ⚠️ {language === 'ar' ? 'مشكلة في الاتصال' : 'Connection issues'}
                        </div>
                      );
                    }
                    
                    // Normal status - show when everything is working
                    return (
                      <div style={{ 
                        marginBottom: '10px', 
                        padding: '6px 8px', 
                        background: '#dcfce7', 
                        border: '1px solid #86efac', 
                        borderRadius: '6px', 
                        fontSize: '11px', 
                        color: '#166534', 
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        ✅ {language === 'ar' ? 'واتساب متصل' : 'WhatsApp connected'}
                      </div>
                    );
                  })()}
                  {(() => {
                    const win = get24hWindowStatus(selectedConversation);
                    return win.open ? (
                      <div style={{ marginBottom: '10px', padding: '8px 12px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: '8px', fontSize: '12px', color: '#166534', fontWeight: '600' }}>
                        {language === 'ar' ? 'نافذة الرد مفتوحة' : 'Reply window open'}{' '}
                        {win.hoursLeft != null && (
                          <span>
                            {win.hoursLeft > 0 ? `${win.hoursLeft}h ` : ''}{win.minutesLeft}m {language === 'ar' ? 'متبقية' : 'left'}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div style={{ marginBottom: '10px', fontSize: '12px', color: '#991b1b' }}>
                        {language === 'ar' ? 'استخدم قالب معتمد فقط' : 'Use approved template only'}
                      </div>
                    );
                  })()}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', marginBottom: '10px' }}>
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px', backgroundColor: '#f8fafc' }}>
                      <div style={{ fontSize: '10px', color: '#6b7280' }}>{language === 'ar' ? 'الأولوية' : 'Priority'}</div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>{Math.round(Number(selectedConversation.priority_score || 0))}</div>
                    </div>
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px', backgroundColor: '#f8fafc' }}>
                      <div style={{ fontSize: '10px', color: '#6b7280' }}>{language === 'ar' ? 'غير مقروء' : 'Unread'}</div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>{Number(selectedConversation.unread_count || 0)}</div>
                    </div>
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px', backgroundColor: '#f8fafc' }}>
                      <div style={{ fontSize: '10px', color: '#6b7280' }}>{language === 'ar' ? 'الانتظار' : 'Waiting'}</div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>{formatWaitingFor(selectedConversation.waiting_for)}</div>
                    </div>
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px', backgroundColor: '#f8fafc' }}>
                      <div style={{ fontSize: '10px', color: '#6b7280' }}>{language === 'ar' ? 'جاهزية العميل' : 'Readiness'}</div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>
                        {String(selectedConversation.conversation_type || 'potential_client') !== 'potential_client'
                          ? <span style={{ color: '#6b7280', fontStyle: 'italic', fontSize: '12px' }}>—</span>
                          : formatLeadTemperature(selectedConversation.client_readiness || selectedConversation.lead_temperature)
                        }
                    </div>
                  </div>
                  </div>

                  {/* AI Insights Card */}
                  {(() => {
                    console.log('🧠 AI Insights Debug:');
                    console.log('   Selected conversation:', selectedConversation?.phone);
                    console.log('   Has ai_profile:', !!selectedConversation?.ai_profile);
                    console.log('   Raw ai_profile:', selectedConversation?.ai_profile);
                    console.log('   Workspace panel visible:', showWorkspacePanel);
                    
                    let aiProfile = {};
                    if (selectedConversation?.ai_profile) {
                      // ai_profile comes as an object from the API, not a JSON string
                      if (typeof selectedConversation.ai_profile === 'string') {
                        try {
                          aiProfile = JSON.parse(selectedConversation.ai_profile);
                        } catch (e) {
                          console.warn('❌ Failed to parse ai_profile JSON:', e);
                          aiProfile = {};
                        }
                      } else {
                        aiProfile = selectedConversation.ai_profile;
                      }
                    }
                    console.log('   Parsed ai_profile:', aiProfile);
                    
                    // Extract meaningful insights (non-duplicate info)
                    const insights = [];
                    if (aiProfile.intent && aiProfile.intent !== 'unknown') {
                      const intentLabels = {
                        'buy': language === 'ar' ? 'شراء' : 'Buy',
                        'rent': language === 'ar' ? 'إيجار' : 'Rent', 
                        'inquiry': language === 'ar' ? 'استفسار' : 'Inquiry'
                      };
                      insights.push({
                        key: 'intent',
                        label: language === 'ar' ? 'النية' : 'Intent',
                        value: intentLabels[aiProfile.intent] || aiProfile.intent,
                        icon: '🎯'
                      });
                    }
                    
                    if (aiProfile.budget && aiProfile.budget !== 'unknown' && Number(aiProfile.budget) >= 500) {
                      insights.push({
                        key: 'budget',
                        label: language === 'ar' ? 'الميزانية' : 'Budget',
                        value: typeof aiProfile.budget === 'number' 
                          ? `${aiProfile.budget.toLocaleString()} ${language === 'ar' ? 'ريال' : 'SAR'}`
                          : aiProfile.budget,
                        icon: '💰'
                      });
                    }
                    
                    if (aiProfile.area && aiProfile.area !== 'unknown') {
                      insights.push({
                        key: 'area',
                        label: language === 'ar' ? 'المنطقة' : 'Area',
                        value: aiProfile.area,
                        icon: '📍'
                      });
                    }
                    
                    if (aiProfile.property_type && aiProfile.property_type !== 'unknown') {
                      const propertyLabels = {
                        'villa': language === 'ar' ? 'فيلا' : 'Villa',
                        'apartment': language === 'ar' ? 'شقة' : 'Apartment',
                        'office': language === 'ar' ? 'مكتب' : 'Office',
                        'commercial': language === 'ar' ? 'تجاري' : 'Commercial',
                        'land': language === 'ar' ? 'أرض' : 'Land'
                      };
                      insights.push({
                        key: 'property_type',
                        label: language === 'ar' ? 'نوع العقار' : 'Property',
                        value: propertyLabels[aiProfile.property_type] || aiProfile.property_type,
                        icon: '🏠'
                      });
                    }
                    
                    console.log('   Extracted insights:', insights);
                    console.log('   Should show card:', insights.length > 0);
                    
                    if (insights.length === 0) {
                      console.log('❌ No insights - card hidden');
                      return null; // Don't show the card if no insights available
                    }
                    
                    console.log('✅ Showing AI Insights card with', insights.length, 'insights');
                    
                    // Create compact summary for collapsed state
                    const summaryParts = insights.slice(0, 3).map(insight => insight.value);
                    const summary = summaryParts.join(' • ');
                    
                    return (
                      <div style={{ 
                        marginBottom: '10px',
                        border: '1px solid #f3e8ff', 
                        borderRadius: '8px', 
                        background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                        overflow: 'hidden',
                        boxShadow: '0 1px 3px rgba(139, 92, 246, 0.1)'
                      }}>
                        {/* Header */}
                        <div 
                          onClick={() => setAiInsightsExpanded(!aiInsightsExpanded)}
                          style={{ 
                            padding: '10px 12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: aiInsightsExpanded ? '#f3e8ff' : 'transparent',
                            borderBottom: aiInsightsExpanded ? '1px solid #e9d5ff' : 'none',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '16px' }}>🧠</span>
                            <div>
                              <div style={{ 
                                fontSize: '13px', 
                                fontWeight: '700', 
                                color: '#7c3aed',
                                marginBottom: aiInsightsExpanded ? '0' : '2px'
                              }}>
                                {language === 'ar' ? 'تحليل ذكي' : 'AI Insights'}
                              </div>
                              {!aiInsightsExpanded && (
                                <div style={{ 
                                  fontSize: '11px', 
                                  color: '#6b46c1',
                                  fontWeight: '500',
                                  maxWidth: '200px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {insights.length} {language === 'ar' ? 'عنصر محلل' : 'insights detected'}
                                </div>
                              )}
                            </div>
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#9ca3af',
                            transform: aiInsightsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.15s ease'
                          }}>
                            ▼
                          </div>
                        </div>
                        
                        {/* Content Grid */}
                        {aiInsightsExpanded && (
                          <div style={{ padding: '12px' }}>
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', 
                              gap: '8px'
                            }}>
                              {insights.map((insight, index) => (
                                <div key={insight.key} style={{
                                  border: '1px solid #e9d5ff',
                                  borderRadius: '6px',
                                  padding: '8px',
                                  backgroundColor: '#ffffff',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '2px'
                                }}>
                                  <div style={{ 
                                    fontSize: '10px', 
                                    color: '#8b5cf6',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}>
                                    <span>{insight.icon}</span>
                                    {insight.label}
                                  </div>
                                  <div style={{ 
                                    fontSize: '12px', 
                                    fontWeight: '700', 
                                    color: '#374151',
                                    lineHeight: '1.2'
                                  }}>
                                    {insight.value}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Conversation type selector + optional auto vendor hint */}
                  {(() => {
                    const convType = String(selectedConversation.conversation_type || 'potential_client');
                    const vendorSuggested = selectedConversation.vendor_suggestion === true || selectedConversation.vendor_suggestion === 1;
                    const typeOptions = [
                      { value: 'potential_client', labelAr: 'عميل محتمل', labelEn: 'Potential Client', icon: '👤' },
                      { value: 'vendor', labelAr: 'مزود خدمة', labelEn: 'Vendor', icon: '🏢' },
                      { value: 'other', labelAr: 'أخرى', labelEn: 'Other', icon: '❓' },
                    ];
                    return (
                      <div style={{ marginBottom: '10px' }}>
                        {vendorSuggested && convType === 'potential_client' && (
                          <div style={{ marginBottom: '8px', padding: '8px 10px', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '8px', fontSize: '12px', color: '#92400e' }}>
                            <div style={{ fontWeight: 700, marginBottom: '4px' }}>
                              {language === 'ar' ? '⚠️ يبدو أن هذه محادثة مع مزود خدمة' : '⚠️ This looks like a vendor conversation'}
                            </div>
                            <div style={{ color: '#78350f', marginBottom: '6px', lineHeight: 1.4 }}>
                              {language === 'ar'
                                ? 'هل تريد تصنيفها بشكل صحيح حتى لا تظهر ضمن العملاء المحتملين؟'
                                : 'Would you like to classify it so it doesn\'t appear in your lead queue?'}
                            </div>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                onClick={() => handleConversationTypeChange('vendor')}
                                style={{ padding: '4px 10px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 700 }}
                              >
                                {language === 'ar' ? 'نعم، مزود خدمة' : 'Yes, it\'s a vendor'}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleConversationTypeChange('potential_client')}
                                style={{ padding: '4px 10px', background: 'none', border: '1px solid #d97706', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#92400e' }}
                              >
                                {language === 'ar' ? 'لا، احتفظ به كعميل' : 'No, keep as lead'}
                              </button>
                            </div>
                          </div>
                        )}
                        <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', fontWeight: 600 }}>
                          {language === 'ar' ? 'نوع المحادثة' : 'Conversation Type'}
                        </div>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {typeOptions.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => handleConversationTypeChange(opt.value)}
                              style={{
                                padding: '3px 8px', fontSize: '11px', cursor: 'pointer', borderRadius: '12px', border: '1px solid',
                                fontWeight: convType === opt.value ? 700 : 400,
                                background: convType === opt.value ? (opt.value === 'potential_client' ? '#dbeafe' : opt.value === 'vendor' ? '#fef3c7' : '#f3f4f6') : '#fff',
                                borderColor: convType === opt.value ? (opt.value === 'potential_client' ? '#93c5fd' : opt.value === 'vendor' ? '#fbbf24' : '#d1d5db') : '#e5e7eb',
                                color: convType === opt.value ? (opt.value === 'potential_client' ? '#1d4ed8' : opt.value === 'vendor' ? '#92400e' : '#374151') : '#6b7280',
                              }}
                            >
                              {opt.icon} {language === 'ar' ? opt.labelAr : opt.labelEn}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* "No follow-up needed" one-tap dismiss — owner/manager or wa_inbox_triage */}
                  {canInboxTriage &&
                    !selectedConversation.followup_resolved_at &&
                    String(selectedConversation.conversation_type || 'potential_client') === 'potential_client' &&
                    (Number(selectedConversation.unread_count || 0) > 0 || selectedConversation.waiting_for === 'agent') && (
                    <button
                      onClick={async () => {
                        try {
                          const { data } = await resolveConversationFollowup(selectedConversation.id);
                          const now = data?.followup_resolved_at || new Date().toISOString();
                          const patch = {
                            followup_resolved_at: now,
                            followup_resolved_by: data?.followup_resolved_by ?? null,
                            followup_resolved_by_name: data?.followup_resolved_by_name ?? null,
                            waiting_for: 'none',
                            unread_count: 0
                          };
                          setConversations((prev) => prev.map((c) =>
                            c.id === selectedConversation.id ? { ...c, ...patch } : c
                          ));
                          setSelectedConversation((prev) => prev ? { ...prev, ...patch } : prev);
                        } catch (e) {
                          console.warn('Failed to resolve follow-up:', e?.message || e);
                        }
                      }}
                      style={{
                        width: '100%', padding: '7px 12px', marginBottom: '8px',
                        background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px',
                        cursor: 'pointer', fontSize: '12px', fontWeight: 700, color: '#15803d',
                        textAlign: 'center',
                      }}
                    >
                      ✓ {language === 'ar' ? 'لا تحتاج متابعة' : 'No follow-up needed'}
                    </button>
                  )}
                  <div style={{ fontSize: '12px', color: '#2563eb', marginBottom: '8px', fontWeight: 700 }}>
                    {language === 'ar' ? 'ثقة التصنيف' : 'Classification confidence'}: {getConfidenceScore(selectedConversation)}%
                  </div>
                  <div style={{ margin: '10px 0', borderTop: '1px solid #f3f4f6' }} />
                  {selectedConversation.attributed_campaign_id && (
                    <div style={{ marginBottom: '8px', padding: '6px 8px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', fontSize: '11px', color: '#0369a1' }}>
                      📣 {language === 'ar'
                        ? `مصدر المحادثة: ردّ على حملة${selectedConversation.attributed_campaign_name ? ` "${selectedConversation.attributed_campaign_name}"` : ''}`
                        : `Source: reply to campaign${selectedConversation.attributed_campaign_name ? ` "${selectedConversation.attributed_campaign_name}"` : ''}`}
                    </div>
                  )}
                  <div style={{ fontSize: '12px', color: '#1e40af', marginBottom: '8px', fontWeight: 600 }}>
                    {language === 'ar' ? 'آخر رد من' : 'Last responded by'}: {lastResponderNameForStats(selectedConversation)}
                  </div>
                  {selectedConversation.followup_resolved_at && (
                    <div style={{ fontSize: '12px', color: '#15803d', marginBottom: '8px', fontWeight: 500, lineHeight: 1.4 }}>
                      {language === 'ar' ? 'أُغلقت المتابعة' : 'Follow-up cleared'}
                      {(selectedConversation.followup_resolved_by_name || selectedConversation.followup_resolved_by != null)
                        ? (language === 'ar'
                          ? ` بواسطة ${selectedConversation.followup_resolved_by_name || `المستخدم #${selectedConversation.followup_resolved_by}`}`
                          : ` by ${selectedConversation.followup_resolved_by_name || `User #${selectedConversation.followup_resolved_by}`}`)
                        : (language === 'ar' ? ' (قبل تسجيل المسؤول)' : ' (before resolver was tracked)')}
                      {formatDate(selectedConversation.followup_resolved_at)
                        ? ` — ${formatDate(selectedConversation.followup_resolved_at)}`
                        : ''}
                    </div>
                  )}
                  <div style={{ fontSize: '12px', color: '#4b5563', marginBottom: '6px' }}>
                    {language === 'ar' ? 'رسائل العميل' : 'Client messages'}: {messages.filter((m) => m.sender === 'client' && !isReactionSidecarRecord(m)).length}
                  </div>
                  <div style={{ fontSize: '12px', color: '#4b5563', marginBottom: '6px' }}>
                    {language === 'ar' ? 'رسائل الموظف' : 'Employee messages'}: {messages.filter((m) => m.sender === 'agent' && !isReactionSidecarRecord(m)).length}
                  </div>
                  <div style={{ fontSize: '12px', color: '#4b5563', marginBottom: '6px' }}>
                    {language === 'ar' ? 'آخر رسالة' : 'Last message'}: {formatTime(selectedConversation.last_message_at || selectedConversation.last_activity_at)}
                  </div>
                  {Array.isArray(selectedConversation.tags) && selectedConversation.tags.length > 0 && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#374151' }}>
                      {selectedConversation.tags.map((tg) => localizeConversationTag(tg)).join(' ')}
                    </div>
                  )}
                  {clientProfile && selectedConversation.linked_client_id && (
                    <div style={{ marginTop: '12px', borderTop: '1px solid #f3f4f6', paddingTop: '10px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                        {language === 'ar' ? 'ملف العميل' : 'Client Profile'}
                      </div>
                      {(() => {
                        const profileNameLine = String(
                          clientProfile.client?.name || displayLinkedClientName(selectedConversation.linked_client_name) || ''
                        ).trim() || '—';
                        const contactOnly = formatClientProfileContactLine(clientProfile.client, profileNameLine);
                        return (
                          <>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                        {profileNameLine}
                      </div>
                      {contactOnly && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                          {contactOnly}
                        </div>
                      )}
                          </>
                        );
                      })()}
                      {(clientProfile.client?.property_preference || clientProfile.client?.budget) && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                          {clientProfile.client?.property_preference && (
                            <span>{language === 'ar' ? 'تفضيل' : 'Pref'}: {clientProfile.client.property_preference}</span>
                          )}
                          {clientProfile.client?.property_preference && clientProfile.client?.budget && ' · '}
                          {clientProfile.client?.budget && (
                            <span>{language === 'ar' ? 'ميزانية' : 'Budget'}: {clientProfile.client.budget}</span>
                          )}
                        </div>
                      )}
                      <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                        {language === 'ar' ? 'صفقات' : 'Deals'}: {Array.isArray(clientProfile.deals) ? clientProfile.deals.length : 0}
                        {Array.isArray(clientProfile.deals) && clientProfile.deals.length > 0 && (
                          <span style={{ color: '#9ca3af', marginLeft: '4px' }}>
                            ({clientProfile.deals.filter(d => (d.status || '').toLowerCase() === 'active').length} {language === 'ar' ? 'نشطة' : 'active'})
                          </span>
                        )}
                      </div>
                      {clientProfile.client?.notes && (
                        <div style={{ marginTop: '6px', fontSize: '10px', color: '#6b7280', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {String(clientProfile.client.notes).slice(0, 60)}{String(clientProfile.client.notes).length > 60 ? '…' : ''}
                        </div>
                      )}
                      {Array.isArray(clientProfile.deals) && clientProfile.deals.filter(d => (d.status || '').toLowerCase() === 'active').length > 0 && (
                        <div style={{ marginTop: '6px', fontSize: '10px', color: '#9ca3af' }}>
                          {language === 'ar' ? 'عناوين الصفقات' : 'Deal properties'}: {clientProfile.deals.filter(d => (d.status || '').toLowerCase() === 'active').slice(0, 2).map(d => d.property_address || d.property_type || '—').join(', ')}
                        </div>
                      )}
                      {Array.isArray(clientProfile.activities) && clientProfile.activities.length > 0 && (
                        <div style={{ marginTop: '6px', fontSize: '10px', color: '#9ca3af' }}>
                          {language === 'ar' ? 'آخر نشاط' : 'Last activity'}: {clientProfile.activities[0]?.activity_type || clientProfile.activities[0]?.title || '—'}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ marginTop: '12px', borderTop: '1px solid #f3f4f6', paddingTop: '10px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                      {language === 'ar' ? 'ملاحظات' : 'Notes'}
                    </div>
                    <div style={{ maxHeight: '120px', overflowY: 'auto', marginBottom: '8px' }}>
                      {conversationNotes.length === 0 ? (
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{language === 'ar' ? 'لا توجد ملاحظات' : 'No notes'}</div>
                      ) : (
                        conversationNotes.map((n) => (
                          <div key={n.id} style={{ fontSize: '11px', padding: '6px 8px', background: '#f9fafb', borderRadius: '6px', marginBottom: '6px' }}>
                            <div style={{ color: '#6b7280', marginBottom: '2px' }}>{n.author_name || '—'}</div>
                            <div style={{ color: '#111827' }}>{n.note}</div>
                          </div>
                        ))
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input
                        value={newNoteText}
                        onChange={(e) => setNewNoteText(e.target.value)}
                        placeholder={language === 'ar' ? 'إضافة ملاحظة...' : 'Add note...'}
                        style={{ flex: 1, padding: '6px 8px', fontSize: '11px', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                      />
                      <button
                        onClick={async () => {
                          if (!newNoteText.trim()) return;
                          try {
                            await createConversationNote(selectedConversation.id, newNoteText.trim());
                            const res = await getConversationNotes(selectedConversation.id);
                            setConversationNotes(res.data?.notes || []);
                            setNewNoteText('');
                          } catch (e) { console.warn(e); }
                        }}
                        style={{ padding: '6px 10px', fontSize: '11px', fontWeight: 600, background: '#111827', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                      >
                        {language === 'ar' ? 'إضافة' : 'Add'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div style={{
              padding: '16px',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#ffffff',
              position: 'relative'
            }}>
              {!get24hWindowStatus(selectedConversation).open && (
                <div ref={metaTemplatePanelRef} style={{
                  padding: '20px',
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
                  border: '1px solid #bbf7d0',
                  borderRadius: '12px',
                  marginBottom: '12px'
                }}>
                  <div style={{ fontSize: '14px', color: '#166534', fontWeight: '700', marginBottom: '6px' }}>
                    {language === 'ar' ? '📋 الرد عبر قالب معتمد' : '📋 Reply with approved template'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#15803d', marginBottom: '10px', lineHeight: 1.5 }}>
                    {language === 'ar'
                      ? 'اختر قالباً معتمداً من القائمة أدناه ثم اضغط إرسال. خارج نافذة 24 ساعة من آخر رسالة واردة من العميل، هذا هو المسموح لميتا.'
                      : 'Pick an approved template below and click Send. After 24 hours from the client’s last inbound message, Meta only allows reaching them this way.'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#166534', marginBottom: '14px', lineHeight: 1.45, opacity: 0.95 }}>
                    {language === 'ar'
                      ? 'إرسال القالب لا يفعّل مربع الرسائل العادية فوراً: يُفعّل بعد أن يرسل العميل أي رسالة في المحادثة (مثل ردّه على القالب).'
                      : 'Sending a template does not turn on normal replies immediately — that happens after the customer sends any message here (e.g. a reply to your template).'}
                  </div>
                  {isManagerOrOwner && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
                      {onNavigateToTemplates && (
                        <button
                          type="button"
                          onClick={onNavigateToTemplates}
                          style={{ fontSize: '11px', padding: '4px 10px', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: '6px', color: '#ea580c', cursor: 'pointer', fontWeight: '600' }}
                        >
                          {language === 'ar' ? '📋 إدارة القوالب' : '📋 Manage templates'}
                        </button>
                      )}
                      <a
                        href="https://business.facebook.com/wa/manage/message-templates/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '11px', color: '#15803d', textDecoration: 'underline' }}
                      >
                        {language === 'ar' ? '📌 إدارة القوالب في ميتا' : '📌 Manage templates in Meta'}
                      </a>
                      <button
                        type="button"
                        onClick={() => loadMetaTemplates()}
                        style={{ fontSize: '11px', padding: '4px 10px', background: '#ecfdf5', border: '1px solid #86efac', borderRadius: '6px', color: '#166534', cursor: 'pointer', fontWeight: '600' }}
                      >
                        {language === 'ar' ? '🔄 تحديث القائمة' : '🔄 Refresh list'}
                      </button>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <select
                      value={metaTemplateName}
                      onChange={(e) => { setMetaTemplateName(e.target.value); if (e.target.value !== '__custom__') setCustomMetaTemplateName(''); }}
                      style={{
                        flex: 1,
                        minWidth: '180px',
                        padding: '12px 14px',
                        fontSize: '14px',
                        border: '1px solid #86efac',
                        borderRadius: '8px',
                        outline: 'none',
                        backgroundColor: '#fff',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">{language === 'ar' ? 'اختر قالباً...' : 'Select template...'}</option>
                      {metaTemplatesLoadFailed
                        ? <option value="" disabled>{language === 'ar' ? '⚠️ تعذر تحميل القوالب — تحقق من إعدادات التكامل' : '⚠️ Could not load templates — check integration settings'}</option>
                        : metaTemplates.map((t) => (
                        <option key={t.name} value={t.name}>
                          {language === 'ar' ? (t.label_ar || t.name) : (t.label_en || t.name)}
                        </option>
                          ))
                      }
                      <option value="__custom__">{language === 'ar' ? 'أخرى (أدخل الاسم يدوياً)' : 'Other (enter name)'}</option>
                    </select>
                    {metaTemplateName === '__custom__' && (
                      <input
                        type="text"
                        value={customMetaTemplateName}
                        onChange={(e) => setCustomMetaTemplateName(e.target.value)}
                        placeholder={language === 'ar' ? 'اسم القالب (مثال: follow_up)' : 'Template name (e.g. follow_up)'}
                        style={{
                          flex: 1,
                          minWidth: '140px',
                          padding: '12px 14px',
                          fontSize: '14px',
                          border: '1px solid #86efac',
                          borderRadius: '8px',
                          outline: 'none',
                          backgroundColor: '#fff'
                        }}
                      />
                    )}
                    <button
                      onClick={handleSendMetaTemplate}
                      disabled={sendingMessage || !((metaTemplateName === '__custom__' ? customMetaTemplateName : metaTemplateName)?.trim())}
                      style={{
                        padding: '12px 20px',
                        fontSize: '14px',
                        fontWeight: '600',
                        background: '#25D366',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: sendingMessage || !((metaTemplateName === '__custom__' ? customMetaTemplateName : metaTemplateName)?.trim()) ? 'not-allowed' : 'pointer',
                        opacity: sendingMessage || !((metaTemplateName === '__custom__' ? customMetaTemplateName : metaTemplateName)?.trim()) ? 0.7 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {sendingMessage ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...') : (language === 'ar' ? 'إرسال القالب' : 'Send Template')}
                      <FaPaperPlane style={{ fontSize: '12px' }} />
                    </button>
                  </div>
                </div>
              )}
              {suggestedReplies.length > 0 && get24hWindowStatus(selectedConversation).open && (
                <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>
                    {language === 'ar' ? 'ردود مقترحة:' : 'Suggested replies:'}
                  </span>
                  {suggestedReplies.map((s) => (
                    <button
                      key={s.label + s.content}
                      onClick={() => { setNewMessage(s.content); setShowTemplates(false); setShowSnippets(false); }}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        background: '#f9fafb',
                        color: '#374151',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => { e.target.style.background = '#eff6ff'; e.target.style.borderColor = '#93c5fd'; }}
                      onMouseLeave={(e) => { e.target.style.background = '#f9fafb'; e.target.style.borderColor = '#d1d5db'; }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
              {/* Message Type Selector - only when 24h window open */}
              {get24hWindowStatus(selectedConversation).open && messageType !== 'text' && (
                <div style={{
                  padding: '8px 12px',
                  marginBottom: '8px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px'
                }}>
                  <span style={{ color: '#6b7280' }}>
                    {messageType === 'image' && (language === 'ar' ? '📷 صورة' : '📷 Image')}
                    {messageType === 'document' && (language === 'ar' ? '📄 مستند' : '📄 Document')}
                    {messageType === 'video' && (language === 'ar' ? '🎥 فيديو' : '🎥 Video')}
                    {messageType === 'audio' && (language === 'ar' ? '🎤 تسجيل صوتي' : '🎤 Voice Note')}
                    {messageType === 'location' && (language === 'ar' ? '📍 موقع' : '📍 Location')}
                    {messageType === 'contact' && (language === 'ar' ? '👤 جهة اتصال' : '👤 Contact')}
                    {messageType === 'interactive' && (language === 'ar' ? '🔘 رسالة تفاعلية' : '🔘 Interactive')}
                  </span>
                  <button
                    onClick={() => {
                      setMessageType('text');
                      setImageFile(null);
                      setDocumentFile(null);
                      setVideoFile(null);
                      setAudioFile(null);
                      voiceRecordingForUploadRef.current = false;
                      setLocationData(null);
                      setContactData(null);
                      setSelectedInteractivePreset(null);
                      // Stop recording if active
                      if (isRecording) {
                        handleStopRecording();
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#6b7280',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    {language === 'ar' ? 'إلغاء' : 'Cancel'}
                  </button>
                </div>
              )}

              {/* Action Suggestion Card */}
              {actionSuggestions.length > 0 && actionSuggestions.map((sug) => {
                const auto = typeof sug.auto_applied === 'string' ? (() => { try { return JSON.parse(sug.auto_applied); } catch { return {}; } })() : (sug.auto_applied || {});
                const hasTags = Array.isArray(auto.tags) && auto.tags.length > 0;
                const hasPriority = Boolean(auto.priority);
                const hasActivity = Boolean(sug.suggest_activity_label);
                return (
                  <div key={sug.id} style={{ padding: '12px 14px', marginBottom: '8px', background: 'linear-gradient(135deg, #faf5ff 0%, #f0fdf4 100%)', border: '1.5px solid #c4b5fd', borderRadius: '12px', boxShadow: '0 1px 4px rgba(124,58,237,0.08)' }}>
                    {(hasTags || hasPriority) && (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px', alignItems: 'center' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, color: '#6d28d9', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {language === 'ar' ? '✅ تم تلقائياً:' : '✅ Auto-applied:'}
                        </span>
                        {hasTags && auto.tags.map((t, i) => (
                          <span key={i} style={{ padding: '2px 8px', background: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: '20px', fontSize: '11px', color: '#4c1d95', fontWeight: 600 }}>{localizeConversationTag(t)}</span>
                        ))}
                        {hasPriority && (
                          <span style={{ padding: '2px 8px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '20px', fontSize: '11px', color: '#92400e', fontWeight: 600 }}>
                            {language === 'ar' ? `أولوية: ${auto.priority === 'high' ? 'عالية' : auto.priority === 'medium' ? 'متوسطة' : 'منخفضة'}` : `Priority: ${auto.priority}`}
                          </span>
                        )}
                      </div>
                    )}
                    {hasActivity && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                          <span style={{ fontSize: '18px' }}>🗓</span>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>
                              {sug.button_title
                                ? (language === 'ar' ? `العميل اختار: "${sug.button_title}"` : `Client chose: "${sug.button_title}"`)
                                : (language === 'ar' ? 'رد تفاعلي جديد' : 'New interactive reply')}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6d28d9', marginTop: '2px' }}>
                              {language === 'ar' ? `جدولة: ${sug.suggest_activity_label}` : `Schedule: ${sug.suggest_activity_label}`}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                          <button onClick={() => { setSchedulingModal(sug); setSchedulingLabel(sug.suggest_activity_label || ''); setSchedulingDate(''); setSchedulingTime(''); setSchedulingNotes(''); }} style={{ padding: '7px 14px', background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 700, boxShadow: '0 2px 6px rgba(124,58,237,0.3)' }}>
                            {language === 'ar' ? '📅 جدولة' : '📅 Schedule'}
                          </button>
                          <button onClick={async () => { try { await dismissActionSuggestion(sug.id); setActionSuggestions((prev) => prev.filter((s) => s.id !== sug.id)); } catch {} }} style={{ padding: '7px 10px', background: 'none', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', color: '#6b7280' }}>
                            {language === 'ar' ? 'تجاهل' : 'Dismiss'}
                          </button>
                        </div>
                      </div>
                    )}
                    {!hasActivity && (hasTags || hasPriority) && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button onClick={async () => { try { await dismissActionSuggestion(sug.id); setActionSuggestions((prev) => prev.filter((s) => s.id !== sug.id)); } catch {} }} style={{ padding: '4px 10px', background: 'none', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#6b7280' }}>
                          {language === 'ar' ? 'تم ✓' : 'Done ✓'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Interactive Preset Picker - only when 24h window open */}
              {get24hWindowStatus(selectedConversation).open && messageType === 'interactive' && (
                <div style={{ padding: '12px', marginBottom: '8px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#15803d', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {language === 'ar' ? 'اختر رسالة تفاعلية' : 'Select interactive message'}
                  </div>
                  {interactivePresets.length === 0 ? (
                    <div style={{ fontSize: '12px', color: '#6b7280', padding: '8px', textAlign: 'center' }}>
                      {language === 'ar' ? 'لا توجد قوالب بعد.' : 'No presets yet.'}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {interactivePresets.map(p => {
                        const pt = p.preset_type || 'button';
                        const listParsed = pt === 'list' ? parseStoredButtons(p.buttons, 'list') : null;
                        const btns = pt === 'button'
                          ? (Array.isArray(p.buttons) ? p.buttons : (typeof p.buttons === 'string' ? (() => { try { return JSON.parse(p.buttons); } catch { return []; } })() : []))
                          : [];
                        const listRows = pt === 'list' && listParsed
                          ? (listParsed.sections || []).flatMap((s) => (s.rows || []).filter((r) => (r.title || '').trim()))
                          : [];
                        const isSelected = selectedInteractivePreset?.id === p.id;
                        const isAr = getTextDirection(p.body || '') === 'rtl';
                        return (
                          <div key={p.id} onClick={() => setSelectedInteractivePreset(isSelected ? null : p)}
                            style={{ padding: '10px 12px', border: `2px solid ${isSelected ? '#22c55e' : '#e2e8f0'}`, borderRadius: '10px', cursor: 'pointer', background: isSelected ? '#dcfce7' : '#fff', transition: 'all 0.15s' }}>
                            <div style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {p.label}
                                {pt === 'list' && (
                                  <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '999px', background: '#fef3c7', color: '#92400e' }}>{language === 'ar' ? 'قائمة' : 'List'}</span>
                                )}
                              </span>
                              {isSelected && <span style={{ fontSize: '15px', color: '#16a34a' }}>✓</span>}
                            </div>
                            <div style={{ padding: '8px 12px', background: 'var(--wa-agent-bubble-fill, #f5f6f8)', border: '1px solid var(--wa-agent-bubble-border, rgba(37, 211, 102, 0.12))', borderRadius: '8px 8px 2px 8px', fontSize: '13px', color: '#111', lineHeight: 1.5, direction: isAr ? 'rtl' : 'ltr', textAlign: isAr ? 'right' : 'left', marginBottom: '8px', boxShadow: 'var(--wa-agent-bubble-shadow, 0 0 0 1px rgba(37, 211, 102, 0.08), 0 2px 8px rgba(37, 211, 102, 0.07))' }}>{p.body}</div>
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                              {pt === 'button' ? btns.filter(b => b.title).map((b, i) => (
                                <span key={i} style={{ padding: '3px 10px', border: '1.5px solid #22c55e', borderRadius: '20px', fontSize: '11px', color: '#15803d', fontWeight: 600, background: '#f0fdf4' }}>{b.title}</span>
                              )) : (
                                <>
                                  {listParsed?.listButton ? (
                                    <span style={{ padding: '3px 10px', border: '1.5px solid #d97706', borderRadius: '20px', fontSize: '11px', color: '#b45309', fontWeight: 600, background: '#fffbeb' }}>▾ {listParsed.listButton}</span>
                                  ) : null}
                                  {listRows.map((r, i) => (
                                    <span key={i} style={{ padding: '3px 10px', border: '1.5px solid #94a3b8', borderRadius: '6px', fontSize: '11px', color: '#475569', fontWeight: 500 }}>{r.title}</span>
                                  ))}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Location Picker - only when 24h window open */}
              {get24hWindowStatus(selectedConversation).open && showLocationPicker && (
                <div style={{
                  padding: '12px',
                  marginBottom: '8px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  position: 'relative'
                }}>
                  <button
                    onClick={() => {
                      setShowLocationPicker(false);
                      setLocationSearchQuery('');
                      setLocationSearchResults([]);
                    }}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'none',
                      border: 'none',
                      color: '#6b7280',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title={language === 'ar' ? 'إغلاق' : 'Close'}
                  >
                    <FaTimes />
                  </button>
                  <button
                    onClick={handleLocationSelect}
                    style={{
                      width: '100%',
                      padding: '8px',
                      backgroundColor: '#25D366',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <FaMapMarkerAlt /> {language === 'ar' ? 'استخدام موقعي الحالي' : 'Use My Current Location'}
                  </button>
                  <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                      placeholder={language === 'ar' ? 'عنوان، حي، مدينة، أو رمز قصير (مثل RRRA6565)' : 'Address, district, city, or short code (e.g. RRRA6565)'}
                      value={locationSearchQuery}
                      onChange={(e) => handleLocationSearchChange(e.target.value)}
                    style={{
                      width: '100%',
                        padding: '8px 12px',
                      border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      autoFocus
                    />
                    {isSearchingLocation && (
                      <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <FaSpinner className="spin" style={{ fontSize: '14px', color: '#6b7280' }} />
                      </div>
                    )}
                    {locationSearchResults.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000
                      }}>
                        {locationSearchResults.map((result, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleSelectLocation(result)}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              textAlign: 'left',
                              border: 'none',
                              backgroundColor: 'transparent',
                              cursor: 'pointer',
                              fontSize: '13px',
                              color: '#111827',
                              borderBottom: idx < locationSearchResults.length - 1 ? '1px solid #f3f4f6' : 'none',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '8px'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <FaMapMarkerAlt style={{ fontSize: '14px', color: '#25D366', marginTop: '2px', flexShrink: 0 }} />
                            <span style={{ flex: 1, lineHeight: '1.4' }}>{result.display_name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {locationSearchQuery.length >= 2 && !isSearchingLocation && locationSearchResults.length === 0 && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px',
                        fontSize: '13px',
                        color: '#6b7280',
                        textAlign: 'center',
                        backgroundColor: '#f9fafb',
                        borderRadius: '4px'
                      }}>
                        {language === 'ar' ? 'لا توجد نتائج' : 'No results found'}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Form - only when 24h window open */}
              {get24hWindowStatus(selectedConversation).open && showContactForm && (
                <div style={{
                  padding: '12px',
                  marginBottom: '8px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  position: 'relative',
                  direction: language === 'ar' ? 'rtl' : 'ltr'
                }}>
                  <button
                    onClick={() => {
                      setShowContactForm(false);
                      setContactData(null);
                      setContactSearchQuery('');
                      setContactSearchResults([]);
                    }}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      [language === 'ar' ? 'left' : 'right']: '8px',
                      background: 'none',
                      border: 'none',
                      color: '#6b7280',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10
                    }}
                    title={language === 'ar' ? 'إغلاق' : 'Close'}
                  >
                    <FaTimes />
                  </button>
                  
                  {/* Contact Search */}
                  <div style={{ position: 'relative', marginBottom: '12px' }}>
                    <input
                      type="text"
                      placeholder={language === 'ar' ? 'ابحث عن جهة اتصال...' : 'Search for a contact...'}
                      value={contactSearchQuery}
                      onChange={(e) => handleContactSearchChange(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        paddingRight: language === 'ar' && isSearchingContact ? '40px' : '12px',
                        paddingLeft: language !== 'ar' && isSearchingContact ? '40px' : '12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        fontSize: '14px',
                        direction: language === 'ar' ? 'rtl' : 'ltr',
                        textAlign: language === 'ar' ? 'right' : 'left'
                      }}
                      autoFocus
                    />
                    {isSearchingContact && (
                      <div style={{
                        position: 'absolute',
                        [language === 'ar' ? 'left' : 'right']: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <FaSpinner className="spin" style={{ fontSize: '14px', color: '#6b7280' }} />
                      </div>
                    )}
                    {contactSearchResults.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        direction: language === 'ar' ? 'rtl' : 'ltr'
                      }}>
                        {contactSearchResults.map((client, idx) => (
                          <button
                            key={client.id || idx}
                            onClick={() => handleSelectContact(client)}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              textAlign: language === 'ar' ? 'right' : 'left',
                              border: 'none',
                              backgroundColor: 'transparent',
                              cursor: 'pointer',
                              fontSize: '13px',
                              color: '#111827',
                              borderBottom: idx < contactSearchResults.length - 1 ? '1px solid #f3f4f6' : 'none',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                              direction: language === 'ar' ? 'rtl' : 'ltr'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <div style={{ fontWeight: '600' }}>{client.name || (language === 'ar' ? 'بدون اسم' : 'No name')}</div>
                            {client.phone && <div style={{ fontSize: '12px', color: '#6b7280' }}>{client.phone}</div>}
                            {client.email && <div style={{ fontSize: '12px', color: '#6b7280' }}>{client.email}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                    {contactSearchQuery.length >= 2 && !isSearchingContact && contactSearchResults.length === 0 && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px',
                        fontSize: '13px',
                        color: '#6b7280',
                        textAlign: 'center',
                        backgroundColor: '#f9fafb',
                        borderRadius: '4px'
                      }}>
                        {language === 'ar' ? 'لا توجد نتائج - يمكنك إضافة جهة اتصال جديدة أدناه' : 'No results - You can add a new contact below'}
                      </div>
                    )}
                  </div>

                  <div style={{ 
                    fontSize: '12px', 
                    color: '#6b7280', 
                    marginBottom: '8px', 
                    fontWeight: '600',
                    textAlign: language === 'ar' ? 'right' : 'left'
                  }}>
                    {language === 'ar' ? 'أو أضف جهة اتصال جديدة:' : 'Or add a new contact:'}
                  </div>

                  <input
                    type="text"
                    placeholder={language === 'ar' ? 'الاسم' : 'Name'}
                    value={contactData?.name || ''}
                    onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
                    style={{
                      width: '100%',
                      marginBottom: '8px',
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                      fontSize: '14px',
                      direction: language === 'ar' ? 'rtl' : 'ltr',
                      textAlign: language === 'ar' ? 'right' : 'left'
                    }}
                  />
                  <div style={{ display: 'flex', gap: 6, direction: 'ltr', marginBottom: '8px' }}>
                    <select
                      value={contactPhoneCode}
                      onChange={e => setContactPhoneCode(e.target.value)}
                      style={{
                        padding: '8px 6px', fontSize: 13, fontWeight: 600,
                        border: '1px solid #e5e7eb', borderRadius: '4px',
                        background: '#fff', cursor: 'pointer', flexShrink: 0, minWidth: 82
                      }}
                    >
                      {COUNTRY_CODES.map(c => (
                        <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                      ))}
                    </select>
                  <input
                    type="tel"
                      placeholder={language === 'ar' ? 'مثال: 501234567' : 'e.g. 501234567'}
                      value={contactPhoneLocal}
                      onChange={e => setContactPhoneLocal(e.target.value.replace(/\D/g, ''))}
                    style={{
                        flex: 1,
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                      fontSize: '14px',
                        direction: 'ltr',
                        textAlign: 'left'
                    }}
                  />
                  </div>
                  <input
                    type="email"
                    placeholder={language === 'ar' ? 'البريد الإلكتروني (اختياري)' : 'Email (optional)'}
                    value={contactData?.email || ''}
                    onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                    style={{
                      width: '100%',
                      marginBottom: '8px',
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                      fontSize: '14px',
                      direction: language === 'ar' ? 'rtl' : 'ltr',
                      textAlign: language === 'ar' ? 'right' : 'left'
                    }}
                  />
                  <button
                    onClick={handleContactSubmit}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#25D366',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <FaAddressCard /> {language === 'ar' ? 'إضافة جهة الاتصال' : 'Add Contact'}
                  </button>
                </div>
              )}

              {/* File Preview - only when 24h window open */}
              {get24hWindowStatus(selectedConversation).open && (imageFile || (snippetMediaType === 'image' && snippetMediaUrl)) && (
                <div style={{
                  padding: '8px',
                  marginBottom: '8px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <img
                    src={imageFile ? URL.createObjectURL(imageFile) : snippetMediaUrl}
                    alt="Preview"
                    style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                  />
                  <span style={{ flex: 1, fontSize: '12px', color: '#6b7280' }}>{imageFile ? imageFile.name : (language === 'ar' ? 'صورة من الرد الجاهز' : 'Image from quick reply')}</span>
                  <button
                    onClick={() => {
                      setImageFile(null);
                      setSnippetMediaUrl(null);
                      setSnippetMediaType(null);
                      setMessageType('text');
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}

              {get24hWindowStatus(selectedConversation).open && documentFile && (
                <div style={{
                  padding: '8px',
                  marginBottom: '8px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <FaFile style={{ fontSize: '20px', color: '#6b7280' }} />
                  <span style={{ flex: 1, fontSize: '12px', color: '#6b7280' }}>{documentFile.name}</span>
                  <button
                    onClick={() => {
                      setDocumentFile(null);
                      setMessageType('text');
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}

              {get24hWindowStatus(selectedConversation).open && (videoFile || (snippetMediaType === 'video' && snippetMediaUrl)) && (
                <div style={{
                  padding: '8px',
                  marginBottom: '8px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <video
                    src={videoFile ? URL.createObjectURL(videoFile) : snippetMediaUrl}
                    style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                    muted
                  />
                  <span style={{ flex: 1, fontSize: '12px', color: '#6b7280' }}>{videoFile ? videoFile.name : (language === 'ar' ? 'فيديو من الرد الجاهز' : 'Video from quick reply')}</span>
                  <button
                    onClick={() => {
                      setVideoFile(null);
                      setSnippetMediaUrl(null);
                      setSnippetMediaType(null);
                      setMessageType('text');
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* Voice Recording Status */}
              {get24hWindowStatus(selectedConversation).open && (isRecording || isProcessingVoice) && (
                <div style={{
                  padding: '12px',
                  marginBottom: '8px',
                  backgroundColor: '#fef2f2',
                  borderRadius: '6px',
                  border: '1px solid #fecaca',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: isRecording ? '#ef4444' : '#6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: isRecording ? 'wa-pulse-subtle 1.5s infinite' : 'none'
                  }}>
                    {isRecording ? <FaMicrophone style={{ color: '#fff', fontSize: '18px' }} /> : <FaSpinner className="spin" style={{ color: '#fff', fontSize: '18px' }} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                      {isRecording ? (language === 'ar' ? 'جاري التسجيل...' : 'Recording...') : (language === 'ar' ? 'جاري المعالجة...' : 'Processing...')}
                    </div>
                    {isRecording && (
                      <button
                        onClick={handleStopRecording}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          backgroundColor: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {language === 'ar' ? 'إيقاف' : 'Stop'}
                      </button>
                    )}
                  </div>
                  {recordingError && (
                    <div style={{ fontSize: '12px', color: '#ef4444' }}>{recordingError}</div>
                  )}
                </div>
              )}

              {get24hWindowStatus(selectedConversation).open && (audioFile || (snippetMediaType === 'audio' && snippetMediaUrl)) && !isRecording && !isProcessingVoice && (
                <div style={{
                  padding: '12px',
                  marginBottom: '8px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  {/* Audio Player */}
                  {(audioUrl || (snippetMediaType === 'audio' && snippetMediaUrl)) && (
                    <>
                      <audio
                        ref={audioPlayerRef}
                        src={audioUrl || snippetMediaUrl}
                        preload="metadata"
                        style={{ display: 'none' }}
                      />
                      <button
                        onClick={() => {
                          const audio = audioPlayerRef.current;
                          if (audio) {
                            if (isPlayingAudio) {
                              audio.pause();
                            } else {
                              audio.play();
                            }
                          }
                        }}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#25D366',
                          color: '#fff',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#20BA5A'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#25D366'}
                        title={isPlayingAudio ? (language === 'ar' ? 'إيقاف' : 'Pause') : (language === 'ar' ? 'تشغيل' : 'Play')}
                      >
                        {isPlayingAudio ? <FaPause style={{ fontSize: '14px' }} /> : <FaPlay style={{ fontSize: '14px', marginLeft: '2px' }} />}
                      </button>
                    </>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '2px' }}>
                      {language === 'ar' ? 'تسجيل صوتي' : 'Voice Note'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                      {(audioFile instanceof File && audioFile.name) || (snippetMediaUrl ? (language === 'ar' ? 'صوت من الرد الجاهز' : 'Audio from quick reply') : (language === 'ar' ? 'ملف صوتي' : 'Audio file'))}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (audioPlayerRef.current) {
                        audioPlayerRef.current.pause();
                        audioPlayerRef.current.currentTime = 0;
                      }
                      setIsPlayingAudio(false);
                      setAudioFile(null);
                      voiceRecordingForUploadRef.current = false;
                      setSnippetMediaUrl(null);
                      setSnippetMediaType(null);
                      setMessageType('text');
                      if (audioUrl) {
                        URL.revokeObjectURL(audioUrl);
                        setAudioUrl(null);
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px',
                      flexShrink: 0
                    }}
                    title={language === 'ar' ? 'إزالة' : 'Remove'}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}

              {/* Quick Reply Templates - only when 24h window open */}
              {get24hWindowStatus(selectedConversation).open && showTemplates && templates.length > 0 && (
                <div style={{
                  position: 'absolute', bottom: '100%',
                  left: '16px', right: '16px',
                  marginBottom: '8px', backgroundColor: '#fff',
                  border: '1px solid #e2e8f0', borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  maxHeight: '360px', overflowY: 'auto', zIndex: 1000,
                  direction: language === 'ar' ? 'rtl' : 'ltr'
                }}>
                  <div style={{ padding: '8px 12px 6px', fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f3f4f6', textAlign: 'start' }}>
                    {language === 'ar' ? `ردود سريعة (${templates.length})` : `Quick Replies (${templates.length})`}
                  </div>
                  {templates.map((template) => {
                    const templateText = language === 'ar' ? template.message.ar : template.message.en;
                    const isAr = getTextDirection(templateText || '') === 'rtl';
                    return (
                    <button
                        type="button"
                      key={template.id}
                        className="wa-quick-template-row"
                      onClick={() => handleTemplateClick(template)}
                        style={{ width: '100%', padding: '10px 14px', border: 'none', borderBottom: '1px solid #f9fafb', background: '#fff', cursor: 'pointer', transition: 'background 0.15s', display: 'block', boxSizing: 'border-box' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                      >
                        <span className="wa-quick-template-label">
                        {language === 'ar' ? template.name.ar : template.name.en}
                        </span>
                        <div style={{ padding: '9px 13px', background: 'var(--wa-agent-bubble-fill, #f5f6f8)', border: '1px solid var(--wa-agent-bubble-border, rgba(37, 211, 102, 0.12))', borderRadius: '10px 10px 3px 10px', fontSize: '13px', color: '#111', lineHeight: 1.55, direction: isAr ? 'rtl' : 'ltr', textAlign: isAr ? 'right' : 'left', wordBreak: 'break-word', maxHeight: '80px', overflow: 'hidden', WebkitLineClamp: 4, display: '-webkit-box', WebkitBoxOrient: 'vertical', boxShadow: 'var(--wa-agent-bubble-shadow, 0 0 0 1px rgba(37, 211, 102, 0.08), 0 2px 8px rgba(37, 211, 102, 0.07))', fontFamily: whatsappFontFamily }}>
                          {templateText}
                      </div>
                    </button>
                    );
                  })}
                </div>
              )}

              {/* Snippets / Quick Replies — enhanced panel with search + category filter */}
              {showSnippets && snippets.length > 0 && (() => {
                // Derive unique categories from snippets
                const allCats = [...new Set(snippets.map((s) => (s.category || '').trim().toLowerCase()).filter(Boolean))];

                // Active filter term: slash query takes priority over manual search
                const activeTerm = slashQuery !== null ? slashQuery : snippetSearch;

                // Filter by category first, then by search/slash term
                const filteredSnips = snippets.filter((s) => {
                  const catMatch = snippetCategoryFilter === 'all' || (s.category || '').toLowerCase() === snippetCategoryFilter;
                  if (!catMatch) return false;
                  if (!activeTerm) return true;
                  const term = activeTerm.toLowerCase();
                  return (
                    (s.label || '').toLowerCase().includes(term) ||
                    (s.category || '').toLowerCase().includes(term) ||
                    (s.content || '').toLowerCase().includes(term)
                  );
                });

                return (
                <div style={{
                    position: 'absolute', bottom: '100%',
                    left: '16px', right: '16px', marginBottom: '8px',
                    backgroundColor: '#fff', border: '1px solid #e2e8f0',
                    borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                    zIndex: 1000, display: 'flex', flexDirection: 'column',
                    direction: language === 'ar' ? 'rtl' : 'ltr',
                    maxHeight: '380px', overflow: 'hidden'
                  }}>
                    {/* Header */}
                    <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: allCats.length > 0 ? '8px' : 0 }}>
                        {/* Slash indicator or title */}
                        {slashQuery !== null ? (
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#6366f1', flex: 1 }}>
                            /{slashQuery || (language === 'ar' ? 'ابحث...' : 'type to filter...')}
                          </span>
                        ) : (
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {language === 'ar' ? 'ردود جاهزة' : 'Quick Replies'}
                          </span>
                        )}
                        {/* Search input — only shown when NOT using slash trigger */}
                        {slashQuery === null && (
                          <input
                            autoFocus
                            value={snippetSearch}
                            onChange={(e) => setSnippetSearch(e.target.value)}
                            placeholder={language === 'ar' ? 'بحث...' : 'Search...'}
                      style={{
                              flex: 1, height: '28px', border: '1px solid #e2e8f0', borderRadius: '8px',
                              padding: '0 10px', fontSize: '12px', outline: 'none', background: '#f9fafb',
                              direction: language === 'ar' ? 'rtl' : 'ltr'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <button onClick={() => { setShowSnippets(false); setSlashQuery(null); setSnippetSearch(''); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '14px', lineHeight: 1, padding: '2px 4px' }}>✕</button>
                      </div>
                      {/* Category filter pills */}
                      {allCats.length > 0 && (
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {['all', ...allCats].map((cat) => (
                            <button key={cat} onClick={() => setSnippetCategoryFilter(cat)}
                              style={{
                                padding: '2px 10px', borderRadius: '9999px', border: '1px solid',
                                fontSize: '11px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                                borderColor: snippetCategoryFilter === cat ? '#6366f1' : '#e2e8f0',
                                background: snippetCategoryFilter === cat ? '#eef2ff' : '#f9fafb',
                                color: snippetCategoryFilter === cat ? '#4f46e5' : '#6b7280'
                              }}>
                              {cat === 'all' ? (language === 'ar' ? 'الكل' : 'All') : formatSnippetCategoryLabel(cat, language)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Snippet list */}
                    <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
                      {filteredSnips.length === 0 ? (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                          {language === 'ar' ? 'لا توجد نتائج' : 'No results'}
                        </div>
                      ) : filteredSnips.map((snippet) => {
                        const dir = getTextDirection(snippet.content || '');
                        const hasMedia = snippet.message_type && snippet.message_type !== 'text';
                        const mediaIcon = snippet.message_type === 'image' ? '🖼' : snippet.message_type === 'video' ? '🎥' : snippet.message_type === 'audio' ? '🎤' : snippet.message_type === 'document' ? '📄' : null;
                        return (
                          <button key={snippet.id} onClick={() => handleSnippetClick(snippet)}
                            style={{ width: '100%', padding: '10px 14px', border: 'none', borderBottom: '1px solid #f9fafb', background: '#fff', cursor: 'pointer', transition: 'background 0.15s', textAlign: language === 'ar' ? 'right' : 'left' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f5f3ff'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px', flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
                              <span style={{ fontSize: '12px', fontWeight: 700, color: '#374151' }}>{snippet.label}</span>
                              {snippet.category && <span style={{ fontSize: '10px', padding: '1px 6px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '9999px', color: '#4f46e5', fontWeight: 600 }}>{formatSnippetCategoryLabel(snippet.category, language)}</span>}
                              {hasMedia && <span style={{ fontSize: '12px' }}>{mediaIcon}</span>}
                              {(snippet.usage_count || 0) > 0 && <span style={{ fontSize: '10px', color: '#9ca3af', marginInlineStart: 'auto' }}>×{snippet.usage_count}</span>}
                            </div>
                            {snippet.content ? (
                              <div style={{ padding: '8px 12px', background: 'var(--wa-agent-bubble-fill, #f5f6f8)', border: '1px solid var(--wa-agent-bubble-border, rgba(37, 211, 102, 0.12))', borderRadius: '10px 10px 3px 10px', fontSize: '13px', color: '#111', lineHeight: 1.55, direction: dir, textAlign: dir === 'rtl' ? 'right' : 'left', wordBreak: 'break-word', maxHeight: '72px', overflow: 'hidden', WebkitLineClamp: 3, display: '-webkit-box', WebkitBoxOrient: 'vertical', boxShadow: 'var(--wa-agent-bubble-shadow, 0 0 0 1px rgba(37, 211, 102, 0.08), 0 2px 8px rgba(37, 211, 102, 0.07))' }}>
                          {renderFormattedText(snippet.content)}
                      </div>
                            ) : hasMedia && (
                              <div style={{ padding: '8px 12px', background: 'var(--wa-agent-bubble-fill, #f5f6f8)', border: '1px solid var(--wa-agent-bubble-border, rgba(37, 211, 102, 0.12))', borderRadius: '10px 10px 3px 10px', fontSize: '13px', color: '#374151', boxShadow: 'var(--wa-agent-bubble-shadow, 0 0 0 1px rgba(37, 211, 102, 0.08), 0 2px 8px rgba(37, 211, 102, 0.07))' }}>
                                {mediaIcon} {language === 'ar' ? 'وسائط' : 'Media'}
                              </div>
                            )}
                    </button>
                    );
                  })}
                </div>

                    {/* Footer hint */}
                    <div style={{ padding: '6px 12px', borderTop: '1px solid #f3f4f6', fontSize: '11px', color: '#9ca3af', flexShrink: 0, textAlign: language === 'ar' ? 'right' : 'left' }}>
                      {slashQuery !== null
                        ? (language === 'ar' ? 'اضغط Esc للإغلاق' : 'Press Esc to close')
                        : (language === 'ar' ? 'اكتب / في الرسالة للبحث السريع' : 'Type / in message for quick search')
                      }
                    </div>
                  </div>
                );
              })()}

              {get24hWindowStatus(selectedConversation).open && (
              <div
                ref={composerRef}
                style={{ direction: language === 'ar' ? 'rtl' : 'ltr', position: 'relative' }}
              >
                {/* Drag-and-drop overlay — covers entire composer, sits above all buttons */}
                {composerDragOverlay && (
                  <div
                    style={{
                      position: 'absolute', inset: 0, zIndex: 2000,
                      borderRadius: '12px',
                      border: '2px dashed #6366f1',
                      background: 'rgba(99,102,241,0.08)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      pointerEvents: 'all',
                    }}
                    onDragOver={(e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'; }}
                  >
                    <div style={{
                      background: 'rgba(255,255,255,0.92)', borderRadius: '12px',
                      padding: '16px 28px', textAlign: 'center',
                      boxShadow: '0 4px 16px rgba(99,102,241,0.15)',
                    }}>
                      <div style={{ fontSize: '28px', marginBottom: '6px' }}>📎</div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#4f46e5' }}>
                        {language === 'ar' ? 'أسقط الملف هنا' : 'Drop file to attach'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        {language === 'ar' ? 'صورة • فيديو • مستند • صوت' : 'Image • Video • Document • Audio'}
                      </div>
                    </div>
                  </div>
                )}


                {/* === Tool pill row === */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap', direction: language === 'ar' ? 'rtl' : 'ltr' }}>

                  {/* Attach — media dropdown */}
                <div style={{ position: 'relative' }}>
                    <button onClick={() => setShowMediaOptions(!showMediaOptions)} style={toolPillStyle(showMediaOptions)}>
                      <FaPaperclip style={{ fontSize: '11px' }} />
                      {language === 'ar' ? 'إرفاق' : 'Attach'}
                      </button>
                  {showMediaOptions && (
                      <div style={{ position: 'absolute', bottom: '100%', [language === 'ar' ? 'right' : 'left']: 0, marginBottom: '8px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '6px', display: 'flex', flexDirection: 'column', gap: '2px', zIndex: 1001, minWidth: '160px' }}>
                        <button onClick={() => { fileInputRef.current?.click(); setShowMediaOptions(false); }} style={mediaItemStyle}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <FaImage style={{ color: '#6366f1', fontSize: '13px' }} /> {language === 'ar' ? 'صورة' : 'Image'}
                      </button>
                        <button onClick={() => { const i = document.createElement('input'); i.type='file'; i.accept='.pdf,.doc,.docx,.xls,.xlsx'; i.onchange=(e)=>handleFileSelect(e,'document'); i.click(); setShowMediaOptions(false); }} style={mediaItemStyle}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <FaFile style={{ color: '#f59e0b', fontSize: '13px' }} /> {language === 'ar' ? 'مستند' : 'Document'}
                      </button>
                        <button onClick={() => { const i = document.createElement('input'); i.type='file'; i.accept='video/*'; i.onchange=(e)=>handleFileSelect(e,'video'); i.click(); setShowMediaOptions(false); }} style={mediaItemStyle}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <FaVideo style={{ color: '#ef4444', fontSize: '13px' }} /> {language === 'ar' ? 'فيديو' : 'Video'}
                      </button>
                        <button onClick={() => { if (isRecording) handleStopRecording(); else handleStartRecording(); setShowMediaOptions(false); }} disabled={isProcessingVoice}
                          style={{ ...mediaItemStyle, background: isRecording ? '#fef2f2' : 'transparent', color: isRecording ? '#ef4444' : '#111827', opacity: isProcessingVoice ? 0.5 : 1, cursor: isProcessingVoice ? 'not-allowed' : 'pointer' }}
                          onMouseEnter={(e) => !isProcessingVoice && (e.currentTarget.style.background = isRecording ? '#fee2e2' : '#f9fafb')} onMouseLeave={(e) => e.currentTarget.style.background = isRecording ? '#fef2f2' : 'transparent'}>
                          {isRecording ? <FaStop style={{ color: '#ef4444', fontSize: '13px' }} /> : <FaMicrophone style={{ color: '#10b981', fontSize: '13px' }} />}
                          {language === 'ar' ? (isRecording ? 'إيقاف' : 'صوتي') : (isRecording ? 'Stop' : 'Voice')}
                      </button>
                        <button onClick={() => { setShowLocationPicker(true); setShowMediaOptions(false); }} style={mediaItemStyle}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <FaMapMarkerAlt style={{ color: '#f97316', fontSize: '13px' }} /> {language === 'ar' ? 'موقع' : 'Location'}
                      </button>
                        <button onClick={() => { setShowContactForm(true); setContactData({ name: '', phone: '', email: '' }); setShowMediaOptions(false); }} style={mediaItemStyle}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <FaAddressCard style={{ color: '#8b5cf6', fontSize: '13px' }} /> {language === 'ar' ? 'جهة اتصال' : 'Contact'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Hidden file input */}
                  <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleFileSelect(e, 'image')} />

                  {/* Quick Reply Templates */}
                  {templates.length > 0 && (
                    <button onClick={() => { setShowTemplates(!showTemplates); setShowSnippets(false); }} style={toolPillStyle(showTemplates)}>
                      <FaLightbulb style={{ fontSize: '11px' }} />
                      {language === 'ar' ? 'رد سريع' : 'Quick Reply'}
                </button>
                )}

                  {/* Snippets */}
                  <button onClick={() => { const next = !showSnippets; setShowSnippets(next); setShowTemplates(false); if (next) { setSnippetSearch(''); setSnippetCategoryFilter('all'); setSlashQuery(null); } }} style={toolPillStyle(showSnippets)}>
                    <FaComments style={{ fontSize: '11px' }} />
                    {language === 'ar' ? 'ردود جاهزة' : 'Snippets'}
                  </button>

                  {/* Interactive — own pill, separated from media */}
                <button
                    onClick={async () => {
                      const next = messageType !== 'interactive';
                      setMessageType(next ? 'interactive' : 'text');
                      if (next && !interactivePresetsLoaded) {
                        try {
                          const res = await getWhatsAppInteractivePresets();
                          setInteractivePresets(Array.isArray(res.data?.presets) ? res.data.presets : []);
                          setInteractivePresetsLoaded(true);
                        } catch { setInteractivePresets([]); }
                      }
                    }}
                    style={toolPillStyle(messageType === 'interactive')}
                  >
                    <span style={{ fontSize: '11px' }}>🔘</span>
                    {language === 'ar' ? 'تفاعلية' : 'Interactive'}
                </button>
                </div>

                {/* === Input row === */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', direction: language === 'ar' ? 'rtl' : 'ltr' }}>
                
                {/* Input Container */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0
                }}>
                  {/* Formatting Toolbar - moved here for better structure */}
                  {messageType === 'text' && (
                    <div style={{
                      display: 'flex',
                      gap: '4px',
                      padding: '6px 8px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: language === 'ar' ? '0 0 8px 8px' : '8px 8px 0 0',
                      borderBottom: 'none',
                      direction: language === 'ar' ? 'rtl' : 'ltr'
                    }}>
                      <button
                        type="button"
                        onClick={() => insertFormatting('bold')}
                        style={{
                          padding: '6px 10px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#374151',
                          fontSize: '14px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#e5e7eb';
                          e.currentTarget.style.color = '#111827';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#374151';
                        }}
                        title={language === 'ar' ? 'نص عريض (*text*)' : 'Bold (*text*)'}
                      >
                        <FaBold />
                      </button>
                      <button
                        type="button"
                        onClick={() => insertFormatting('italic')}
                        style={{
                          padding: '6px 10px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#374151',
                          fontSize: '14px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#e5e7eb';
                          e.currentTarget.style.color = '#111827';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#374151';
                        }}
                        title={language === 'ar' ? 'مائل (_text_)' : 'Italic (_text_)'}
                      >
                        <FaItalic />
                      </button>
                      <button
                        type="button"
                        onClick={() => insertFormatting('strikethrough')}
                        style={{
                          padding: '6px 10px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#374151',
                          fontSize: '14px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#e5e7eb';
                          e.currentTarget.style.color = '#111827';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#374151';
                        }}
                        title={language === 'ar' ? 'خط في الوسط (~text~)' : 'Strikethrough (~text~)'}
                      >
                        <FaStrikethrough />
                      </button>
                      <button
                        type="button"
                        onClick={() => insertFormatting('monospace')}
                        style={{
                          padding: '6px 10px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#374151',
                          fontSize: '14px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#e5e7eb';
                          e.currentTarget.style.color = '#111827';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#374151';
                        }}
                        title={language === 'ar' ? 'نص برمجي (`text`)' : 'Monospace (`text`)'}
                      >
                        <FaCode />
                      </button>
                    </div>
                  )}

                  {replyToMessage && selectedConversation && get24hWindowStatus(selectedConversation).open && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      padding: '8px 10px',
                      marginBottom: '6px',
                      background: '#ecfeff',
                      border: '1px solid #a5f3fc',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: '#0e7490', marginBottom: '4px' }}>
                          {replyToMessage.quotedSender === 'agent'
                            ? (language === 'ar' ? '↩ الرد على رسالة زميل' : '↩ Replying to teammate')
                            : (language === 'ar' ? '↩ الرد على رسالة العميل' : '↩ Replying to client')}
                        </div>
                        <div style={{ color: '#134e4a', lineHeight: 1.4, wordBreak: 'break-word' }}>
                          {replyToMessage.textPreview}
                        </div>
                      </div>
                <button
                        type="button"
                        onClick={() => setReplyToMessage(null)}
                        aria-label={language === 'ar' ? 'إلغاء الرد' : 'Cancel reply'}
                  style={{
                          flexShrink: 0,
                          width: '28px',
                          height: '28px',
                          border: 'none',
                          borderRadius: '6px',
                          background: '#fff',
                    cursor: 'pointer',
                          color: '#64748b',
                          fontSize: '16px',
                          lineHeight: 1,
                          padding: 0,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  )}
                  
                  <textarea
                    ref={messageInputRef}
                  value={newMessage}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewMessage(val);
                    
                    // Handle typing indicators
                    if (val.trim()) {
                      handleTypingStart();
                    }
                    
                    // Detect /word at the very start (or after whitespace) to trigger snippet picker
                    const slashMatch = val.match(/(?:^|\s)\/(\S*)$/);
                    if (slashMatch) {
                      setSlashQuery(slashMatch[1]);
                      setShowSnippets(true);
                      setShowTemplates(false);
                    } else if (slashQuery !== null) {
                      // User erased the slash trigger — close picker
                      setSlashQuery(null);
                      setShowSnippets(false);
                    }
                  }}
                  onBlur={() => {
                    // Don't stop typing on blur — tab switches fire onBlur and would clear
                    // the indicator before the other agent even sees it.
                    // The 4-second inactivity timeout handles cleanup instead.
                  }}
                  onFocus={() => {
                    if (newMessage.trim()) {
                      handleTypingStart();
                    }
                  }}
                    onKeyDown={handleKeyPress}
                  placeholder={
                    !get24hWindowStatus(selectedConversation).open
                      ? (language === 'ar' ? 'استخدم حقل القالب أعلاه للإرسال' : 'Use template field above to send')
                      : messageType === 'image' ? (language === 'ar' ? 'اكتب تعليق للصورة...' : 'Add caption...') :
                    messageType === 'document' ? (language === 'ar' ? 'اكتب تعليق للمستند...' : 'Add caption...') :
                      messageType === 'video' ? (language === 'ar' ? 'اكتب تعليق للفيديو...' : 'Add caption...') :
                      messageType === 'audio' ? (language === 'ar' ? 'اكتب تعليق للصوت...' : 'Add caption...') :
                    messageType === 'location' ? (language === 'ar' ? 'اسم الموقع (اختياري)' : 'Location name (optional)') :
                      (language === 'ar' ? 'اكتب رسالة... أو / للردود الجاهزة' : 'Type a message... or / for quick replies')
                  }
                  style={{
                      width: '100%',
                      minHeight: '42px',
                      maxHeight: '120px',
                      padding: `10px 14px`,
                    border: '1px solid #e5e7eb',
                      borderRadius: messageType === 'text' 
                        ? (language === 'ar' ? '8px 8px 0 0' : '0 0 8px 8px')
                        : ui.radiusMd,
                    fontSize: ui.fontMd,
                      fontFamily: 'inherit',
                      resize: 'none',
                      overflowY: 'auto',
                      lineHeight: '1.5',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                      backgroundColor: '#fff',
                      direction: language === 'ar' ? 'rtl' : 'ltr',
                      textAlign: language === 'ar' ? 'right' : 'left'
                  }}
                />
                </div>

                <div style={{ position: 'relative', flexShrink: 0, alignSelf: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowComposerEmojiPicker((p) => {
                        const next = !p;
                        if (next) {
                          setShowMediaOptions(false);
                          setShowTemplates(false);
                          setShowSnippets(false);
                        }
                        return next;
                      });
                    }}
                    title={language === 'ar' ? 'إيموجي' : 'Emoji'}
                    aria-label={language === 'ar' ? 'إدراج إيموجي' : 'Insert emoji'}
                    style={{
                      height: '42px',
                      width: '42px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `1px solid ${showComposerEmojiPicker ? '#5eead4' : '#e5e7eb'}`,
                      borderRadius: ui.radiusMd,
                      background: showComposerEmojiPicker ? '#ecfeff' : '#fff',
                      color: '#374151',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <FaSmile style={{ fontSize: '18px' }} />
                  </button>
                  {showComposerEmojiPicker && (
                    <div
                      className="wa-emoji-picker-wrap"
                      style={{
                        position: 'absolute',
                        bottom: '100%',
                        [language === 'ar' ? 'left' : 'right']: 0,
                        marginBottom: '8px',
                        zIndex: 1100,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                      }}
                    >
                      <EmojiPicker onEmojiClick={insertComposerEmoji} width={300} height={340} theme="light" />
                    </div>
                  )}
                </div>
                
                <button
                  onClick={handleSendMessage}
                  disabled={
                    !get24hWindowStatus(selectedConversation).open ||
                    (messageType === 'text' && !newMessage.trim()) ||
                    (messageType === 'image' && !imageFile && !snippetMediaUrl && !newMessage.trim()) ||
                    (messageType === 'document' && !documentFile && !newMessage.trim()) ||
                    (messageType === 'video' && !videoFile && !snippetMediaUrl && !newMessage.trim()) ||
                    (messageType === 'audio' && !audioFile && !snippetMediaUrl && !newMessage.trim()) ||
                    (messageType === 'location' && !locationData) ||
                    (messageType === 'contact' && !contactData) ||
                    (messageType === 'interactive' && !selectedInteractivePreset) ||
                    sendingMessage
                  }
                  style={{
                    height: '42px',
                    padding: '0 20px',
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: ui.radiusMd,
                    cursor: sendingMessage ? 'not-allowed' : 'pointer',
                    fontSize: ui.fontMd,
                    fontWeight: '600',
                    opacity: (
                      !get24hWindowStatus(selectedConversation).open ||
                      (messageType === 'text' && !newMessage.trim()) ||
                      (messageType === 'image' && !imageFile && !snippetMediaUrl && !newMessage.trim()) ||
                      (messageType === 'document' && !documentFile && !newMessage.trim()) ||
                      (messageType === 'video' && !videoFile && !snippetMediaUrl && !newMessage.trim()) ||
                      (messageType === 'audio' && !audioFile && !snippetMediaUrl && !newMessage.trim()) ||
                      (messageType === 'location' && !locationData) ||
                      (messageType === 'contact' && !contactData) ||
                      (messageType === 'interactive' && !selectedInteractivePreset) ||
                      sendingMessage
                    ) ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 6px 16px rgba(34, 197, 94, 0.28)',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {sendingMessage ? (
                    <FaSpinner className="spin" />
                  ) : (
                    <FaWhatsapp />
                  )}
                  {language === 'ar' ? 'إرسال' : 'Send'}
                </button>
              </div>
              </div>
              )}
            </div>
          </>
        ) : (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6b7280',
            fontSize: '16px'
          }}>
            {language === 'ar' ? 'اختر محادثة لعرض الرسائل' : 'Select a conversation to view messages'}
          </div>
        )}
      </div>
      )}

      {/* Scheduling Modal for action suggestions */}
      {schedulingModal && ReactDOM.createPortal(
        <div onClick={() => setSchedulingModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', padding: '24px', maxWidth: '440px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🗓 {language === 'ar' ? 'جدولة نشاط' : 'Schedule Activity'}
              </div>
              <button onClick={() => setSchedulingModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#94a3b8' }}>×</button>
            </div>
            <div style={{ display: 'grid', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '5px' }}>
                  {language === 'ar' ? 'عنوان النشاط *' : 'Activity title *'}
                </label>
                <input
                  className="wa-input"
                  value={schedulingLabel}
                  onChange={(e) => setSchedulingLabel(e.target.value.slice(0, 200))}
                  placeholder={language === 'ar' ? 'موعد معاينة العقار' : 'Property viewing appointment'}
                  style={{ width: '100%' }}
                  autoFocus
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '5px' }}>
                    {language === 'ar' ? 'التاريخ' : 'Date'}
                  </label>
                  <DualCalendarPicker
                    value={schedulingDate}
                    onChange={(v) => setSchedulingDate(v)}
                    language={language}
                    placeholder={language === 'ar' ? 'اختر تاريخاً' : 'Pick a date'}
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '5px' }}>
                    {language === 'ar' ? 'الوقت' : 'Time'}
                  </label>
                  <input type="time" className="wa-input" value={schedulingTime} onChange={(e) => setSchedulingTime(e.target.value)} style={{ width: '100%' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '5px' }}>
                  {language === 'ar' ? 'ملاحظات' : 'Notes'}
                </label>
                <textarea className="wa-input" value={schedulingNotes} onChange={(e) => setSchedulingNotes(e.target.value)} rows={3} style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }} placeholder={language === 'ar' ? 'تفاصيل إضافية…' : 'Additional details…'} />
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button onClick={() => setSchedulingModal(null)} style={{ padding: '9px 18px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  disabled={!schedulingLabel.trim() || schedulingSaving}
                  onClick={async () => {
                    if (!schedulingLabel.trim()) return;
                    try {
                      setSchedulingSaving(true);
                      const scheduledAt = schedulingDate ? `${schedulingDate}${schedulingTime ? 'T' + schedulingTime : ''}` : null;
                      await actOnActionSuggestion(schedulingModal.id, { label: schedulingLabel.trim(), notes: schedulingNotes.trim(), scheduled_at: scheduledAt });
                      setActionSuggestions((prev) => prev.filter((s) => s.id !== schedulingModal.id));
                      setSchedulingModal(null);
                      toast.success(language === 'ar' ? 'تمت الجدولة ✓' : 'Activity scheduled ✓');
                    } catch (err) {
                      toast.error(err?.response?.data?.error || (language === 'ar' ? 'فشل الحفظ' : 'Failed to save'));
                    } finally {
                      setSchedulingSaving(false);
                    }
                  }}
                  style={{ padding: '9px 20px', background: schedulingLabel.trim() ? 'linear-gradient(135deg, #7c3aed, #6d28d9)' : '#e2e8f0', color: schedulingLabel.trim() ? '#fff' : '#94a3b8', border: 'none', borderRadius: '8px', cursor: schedulingLabel.trim() ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: 700, boxShadow: schedulingLabel.trim() ? '0 2px 6px rgba(124,58,237,0.3)' : 'none' }}
                >
                  {schedulingSaving ? (language === 'ar' ? 'جاري الحفظ…' : 'Saving…') : (language === 'ar' ? '✓ حفظ وجدولة' : '✓ Save & Schedule')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Image Modal/Lightbox - Rendered via Portal */}
      {imageModal && ReactDOM.createPortal(
        <div
          onClick={() => setImageModal(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            cursor: 'pointer'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'default'
            }}
          >
            <img
              src={imageModal.src}
              alt={imageModal.alt}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                borderRadius: '8px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                objectFit: 'contain'
              }}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                setImageModal(null);
              }}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'rgba(255, 255, 255, 0.95)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '24px',
                lineHeight: '1',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#1f2937',
                fontWeight: 'bold',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 1)';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.95)';
                e.target.style.transform = 'scale(1)';
              }}
              title={language === 'ar' ? 'إغلاق (ESC)' : 'Close (ESC)'}
            >
              ×
            </button>
            <a
              href={imageModal.src}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                bottom: '10px',
                right: '10px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '14px',
                cursor: 'pointer',
                color: '#2563eb',
                textDecoration: 'none',
                fontWeight: '600',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.95)';
              }}
            >
              {language === 'ar' ? 'فتح في تبويب جديد' : 'Open in new tab'}
            </a>
          </div>
        </div>,
        document.body
      )}

      {/* PDF preview in-app — inbox-media is same-origin; no new tab required */}
      {documentPreviewModal && ReactDOM.createPortal(
        <div
          onClick={() => setDocumentPreviewModal(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.88)',
            zIndex: 99998,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              width: 'min(960px, 96vw)',
              height: 'min(85vh, 820px)',
              background: '#fff',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 24px 48px rgba(0,0,0,0.35)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div style={{
              padding: '10px 14px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              background: '#f8fafc',
              flexShrink: 0
            }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', direction: getTextDirection(documentPreviewModal.filename) }} title={documentPreviewModal.filename}>
                {documentPreviewModal.filename}
              </div>
              <button
                type="button"
                onClick={() => setDocumentPreviewModal(null)}
                style={{
                  border: 'none',
                  background: '#e2e8f0',
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 20,
                  lineHeight: 1,
                  flexShrink: 0
                }}
                title={language === 'ar' ? 'إغلاق' : 'Close'}
              >
                ×
              </button>
            </div>
            <iframe
              title={documentPreviewModal.filename}
              src={documentPreviewModal.url}
              style={{ flex: 1, border: 'none', width: '100%', minHeight: 0, background: '#525659' }}
            />
            <div style={{ padding: '6px 12px', borderTop: '1px solid #e2e8f0', fontSize: '11px', color: '#64748b', background: '#fff', flexShrink: 0 }}>
              {language === 'ar' ? 'معاينة PDF — استخدم «تبويب جديد» إن لم تُعرض.' : 'PDF preview — use New tab if the viewer fails.'}
            </div>
          </div>
        </div>,
        document.body
      )}
      {reassignModalOpen && ReactDOM.createPortal(
        <div
          role="presentation"
          onClick={() => setReassignModalOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.55)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="wa-reassign-title"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(420px, 100%)',
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              padding: '20px',
              direction: 'rtl'
            }}
          >
            <div id="wa-reassign-title" style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', marginBottom: '14px' }}>
              من يتولّى هذه المحادثة؟
            </div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
              تعيين إلى
            </label>
            {!assignableLoading && reassignModalCandidates.length === 0 ? (
              <div
                style={{
                  fontSize: '13px',
                  color: '#64748b',
                  marginBottom: '14px',
                  padding: '12px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}
              >
                لا يوجد موظف آخر في الفريق يمكن اختياره (المكلّف الحالي مستثنى).
              </div>
            ) : (
              <select
                value={reassignTarget}
                onChange={(e) => setReassignTarget(e.target.value)}
                disabled={assignableLoading || reassignModalCandidates.length === 0}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '14px',
                  background: '#fff'
                }}
              >
                {reassignModalCandidates.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {u.name}
                    {currentUserId && Number(u.id) === Number(currentUserId)
                      ? ' (أنت)'
                      : ''}
                    {` — ${roleLabelAr(u.role)}`}
                  </option>
                ))}
              </select>
            )}
            {assignableLoading && (
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>
                جاري تحميل الفريق…
              </div>
            )}
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
              لماذا؟ (مطلوب)
            </label>
            <textarea
              value={reassignReasonDraft}
              onChange={(e) => setReassignReasonDraft(e.target.value)}
              rows={3}
              placeholder="مثلاً: العميل يفضّل موظفاً محدداً، أو انتقال منطقة…"
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '13px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                marginBottom: '16px',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setReassignModalOpen(false)}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  background: '#f8fafc',
                  cursor: 'pointer'
                }}
              >
                إلغاء
              </button>
              <button
                type="button"
                onClick={confirmReassignFromModal}
                disabled={
                  assignableLoading ||
                  reassignModalCandidates.length === 0 ||
                  !reassignModalCandidates.some((u) => String(u.id) === String(reassignTarget))
                }
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '8px',
                  background: '#111827',
                  color: '#fff',
                  cursor:
                    assignableLoading || reassignModalCandidates.length === 0
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    assignableLoading || reassignModalCandidates.length === 0 ? 0.6 : 1
                }}
              >
                تأكيد
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default WhatsAppInbox;
